/**
 * annotations.js — highlight comments ("划线评论") on blog posts.
 *
 * Interaction (code-review / WeChat-reading style, no hover popups):
 *   - Select text in the article or tap a Mermaid label -> floating toolbar: 「点赞」 / 「评论」 /
 *     「复制」 / 「搜一搜」 / 「分享」; the author can also 「划重点」 for
 *     readers, who see the same underline as any reader reaction.
 *   - 「点赞」 is an anonymous per-passage counter (worker /reactions, D1;
 *     no login, one per browser in localStorage), and records the chapter
 *     (nearest h2/h3) it sits in.
 *   - Images and Mermaid diagrams get a caption and a corner button (js/figures.js)
 *     that selects the caption's title — that text is the passage, so pictures
 *     take 点赞 / 评论 like any sentence; the figure gets `.has-note` outline.
 *   - Every heading (h2–h6) gets a tiny anonymous 「点赞」 button,
 *     for that section as a whole (same table, quote = '§ ' + heading) — no selection
 *     needed, which is what phones can actually do.
 *   - 「已修正」: a note's Issue got closed, or the author 🎉'd it / replied
 *     已修正 — the underline turns green, the note gets a check badge.
 *   - 「分享」 opens the article's share popover (js/share.js, window.BlogShare)
 *     for the passage link; completed shares are counted per passage (`share`
 *     in /reactions) and on the article.
 *     A passage with 点赞 or an author pin but no comment is underlined too
 *     (its quote is stored server-side and re-anchored here).
 *   - 「评论」 opens a large editor panel *in the flow*, right below the
 *     paragraph, with 取消 / 提交评论 bottom-right. If the selection lies inside
 *     an already-underlined passage the note joins that passage instead.
 *   - Every passage ends with a small marker (💬 comments · 👍, non-zero ones;
 *     a pinned passage with no counts shows a bare comment icon);
 *     clicking it (or the underline) expands a thread panel below the paragraph:
 *     a 赞 / 分享 row, all notes on that passage, their replies, and
 *     a box to add yours.
 *
 * Storage: a note is a normal comment of the post's giscus / GitHub Discussions
 * thread —
 *
 *     > quoted passage
 *     >
 *     > <sub>[§ 原文位置](https://arganzheng.life/<slug>.html#annot-<hash>) · [⚑ Issue #N](…)</sub>
 *
 *     the note
 *
 * — so it reads naturally on GitHub. The optional `⚑ Issue` link is added when
 * the reader ticks 「同时提交 Issue」 (code-review style "this needs fixing"): the
 * relay files a GitHub Issue first, then the comment links to it and is shown
 * with a red flag badge. On load the thread is fetched through the
 * secret-free relay in tools/annotations-worker/, comments of that shape are
 * parsed into a W3C TextQuoteSelector { exact, prefix, suffix } and anchored
 * exactly first, then fuzzily (approx-string-match, the algorithm Hypothesis
 * uses) so highlights survive small edits.
 *
 * Links: `#annot-<hash>` (FNV-1a of the quote) locates a thread, `#hl=<text>`
 * (from 「分享」 of a passage nobody commented on) flashes a passage. Both are handled here on load and on
 * hashchange — no Text Fragment directive, so no sticky purple browser
 * highlight and no dependency on the browser's matcher (footnote markers
 * broke it).
 *
 * The comment section at the bottom of the post is rendered here too (no giscus
 * iframe any more): the same discussion, the same commentEl / renderEditor, so
 * plain comments and passage notes share reply / edit / delete / 「同时提交
 * Issue」. giscus is kept only as the OAuth broker: login redirects to
 * giscus.app/api/oauth/authorize, which comes back with ?giscus=<session>; we
 * store it under localStorage["giscus-session"], the relay exchanges it for a
 * GitHub token and the browser calls GitHub GraphQL directly. Any failure
 * degrades to copying the Markdown for posting on GitHub.
 *
 * The author's own top-level comments are private working notes (they feed
 * the AI revision loop): hidden from every reader, shown only while the
 * author is logged in (visibleComments). The relay's /stats leaves them out
 * of the list-page comment counts the same way.
 */

(function () {
  'use strict';

  var GISCUS_ORIGIN = 'https://giscus.app';
  var GITHUB_GRAPHQL = 'https://api.github.com/graphql';
  var GITHUB_MARKDOWN = 'https://api.github.com/markdown';
  var SESSION_KEY = 'giscus-session';
  var SHOW_ARCHIVED_KEY = 'annotations:showArchived';
  var CONTEXT_CHARS = 32;
  // Footnote bodies are indexed (a note can be about a footnote); only the
  // `[1]` superscripts and the `↩` back links are not. A KaTeX formula is
  // indexed through its TeX source (the MathML <annotation>, see isExcluded),
  // not through the rendered glyphs in .katex-html.
  var EXCLUDE_SELECTOR = '.comment, .pager, .related-posts, .reversefootnote, sup[id^="fnref"], a.footnote, ' +
    'script, style, title, desc, noscript, svg, .katex-html, .mermaid-error, .fig-tools, button, .heading-anchor, .callout-title, .heading-fold, .annotation-toolbar, .annotation-panel, .annotation-marker, .sec-react, .moment-head, .moment-foot, .moment-music, .moment-ref';
  var BLOCK_SELECTOR = 'p, li, pre, blockquote, h1, h2, h3, h4, h5, h6, dd, dt, figcaption, figure, .table-caption, .highlight, table';
  var GHOST = { login: 'ghost', url: 'https://github.com/ghost', avatarUrl: 'https://avatars.githubusercontent.com/u/10137?s=64&v=4' };

  var cfg = null;
  var container = null;
  var index = null;          // { text, nodes:[{node, charIdx:[]}] }
  var allComments = [];      // every top-level comment of the post's discussion (see parseComment)
  var comments = [];         // the ones this reader may see: allComments minus the author's own (see visibleComments)
  var annotations = [];      // the subset of `comments` with a selector, anchored in the article
  var reactions = {};        // hash -> { hash, quote, up, share, range, marks }: anonymous passage reactions (worker /reactions)
  var chapters = {};         // section title -> { hash, quote: '§ ' + text, up }: section-level reactions (same table; 随笔 ♡)
  var discussion = null;     // { id, url, totalCommentCount, likes: { up, mine } }
  var pageViews = null;      // number once GET/POST /views answered; stays null when the worker has no counter
  var loaded = false;        // loadDiscussion() has answered (either way)
  var loadError = null;
  var commentsHost = null;   // .annotation-comments in section.comment (the bottom comment section)
  var token = null;
  var viewer = null;
  var toolbar = null;
  var panel = null;          // the single in-flow panel (thread or editor)
  var panelState = null;     // { kind:'thread'|'editor'|'archived-section', ids, selector, offsets, join }
  var renderedOwner = null;
  var renderedArchived = null;
  var toast = null;
  var selectionTimer = null;

  // ------------------------------------------------------------------ init

  function init() {
    var section = document.querySelector('section.comment[data-annotations-api]');
    container = document.querySelector('.post-container');
    document.addEventListener('blog:favorites', function () {
      if (panelState && panelState.kind === 'thread') {
        var open = passageFor(panelState.ids);
        if (open) renderThread(open);
      }
      if (toolbar && toolbar.style.display !== 'none') updateToolbar();
    });
    var privateLogin = document.querySelector('.private-post-login');
    if (privateLogin) privateLogin.addEventListener('click', login);
    // List pages have no comment section but do have action bars (js/share.js)
    // that vote through our auth helpers: pick up a returning OAuth session and
    // the API base from any element that carries it, then stop.
    var carrier = section || document.querySelector('[data-annotations-api]');
    var api = (localStorage.getItem('annotationsApi') || (carrier && carrier.getAttribute('data-annotations-api')) || '').replace(/\/$/, '');
    takeSessionFromUrl();
    cfg = { api: api, path: location.pathname };
    if (!section || !container || !window.InlinePopover || !api) {
      if (getSession() && api) fetchViewer();
      else dispatchViewer();
      return;
    }
    if (!getSession()) dispatchViewer();

    cfg = {
      api: api,
      path: section.getAttribute('data-page-path') || location.pathname,
      siteUrl: (section.getAttribute('data-site-url') || location.origin).replace(/^http:/, 'https:').replace(/\/$/, ''),
      repoId: section.getAttribute('data-repo-id') || '',
      categoryId: section.getAttribute('data-category-id') || '',
      issues: section.getAttribute('data-issues') === '1',
      repo: section.getAttribute('data-repo') || '',
      author: section.getAttribute('data-author') || '',
      section: section
    };

    if (supportsScreenshotCapture()) {
      document.documentElement.classList.add('ap-screenshot-ready');
      document.addEventListener('keydown', function (e) {
        if (e.defaultPrevented || !e.altKey || e.metaKey || e.ctrlKey || e.shiftKey || e.code !== 'KeyS') return;
        var target = e.target && e.target.nodeType === 1 ? e.target : e.target && e.target.parentElement;
        if (target && (target.matches('INPUT, TEXTAREA, SELECT') || target.isContentEditable)) return;
        if (document.querySelector('.ap-crop-overlay') || document.documentElement.classList.contains('ap-capturing')) return;
        e.preventDefault();
        startScreenshotComment();
      });
      document.addEventListener('blog:screenshot-comment', function (e) {
        startScreenshotComment(e.detail && e.detail.target, e.detail && e.detail.block);
      });
    }

    bindSelection();
    initCommentSection();
    loadViews();
    window.addEventListener('hashchange', focusFromHash);
    whenRichContentSettled(function () {
      renderChapterBars();
      Promise.all([loadDiscussion(false), loadReactions()]).then(function () { if (!restoreDraft()) focusFromHash(); });
    });
  }

  // giscus' OAuth flow redirects back to `redirect_uri?giscus=<session>`; its
  // client.js used to store that — now we do (same key, same JSON encoding).
  function takeSessionFromUrl() {
    var url = new URL(location.href);
    var session = url.searchParams.get('giscus');
    if (!session) return;
    try { localStorage.setItem(SESSION_KEY, JSON.stringify(session)); } catch (e) { /* ignore */ }
    url.searchParams.delete('giscus');
    history.replaceState(history.state, '', url.toString());
  }

  // Mermaid / KaTeX rewrite the DOM asynchronously; anchor after they finish
  // (they fire richcontent:rendered), or straight away when there is nothing
  // to render. Re-anchor on every later event as well.
  function whenRichContentSettled(fn) {
    var needsWait = container.querySelector('.mermaid, pre code.language-mermaid') || hasRawMath(container);
    var done = false;
    function run() { if (!done) { done = true; fn(); } }
    document.addEventListener('richcontent:rendered', function () {
      if (done) setTimeout(reanchorAll, 0); else setTimeout(run, 0);
    });
    if (!needsWait) run(); else setTimeout(run, 6000);
  }

  function hasRawMath(root) {
    var walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
    while (walker.nextNode()) {
      var t = walker.currentNode.nodeValue;
      if ((t.indexOf('\\(') !== -1 || t.indexOf('\\[') !== -1) && !walker.currentNode.parentNode.closest('pre, code')) return true;
    }
    return false;
  }

  // ------------------------------------------------------------ text index

  function isExcluded(node) {
    var el = node.nodeType === 1 ? node : node.parentNode;
    if (!el || !el.closest) return false;
    // Inside a formula only the <annotation encoding="application/x-tex"> text
    // counts: the passage reads `\frac{a}{b}`, the way the author wrote it.
    if (el.closest('.katex-mathml')) return !el.closest('annotation');
    var diagram = el.closest('.mermaid');
    if (diagram) {
      if (diagram.classList.contains('mermaid-error') || el.closest('style, title, desc, .fig-tools, button')) return true;
      return !el.closest('svg');
    }
    return !!el.closest(EXCLUDE_SELECTOR);
  }
  function diagramLabelHost(node) {
    var el = node.nodeType === 1 ? node : node.parentNode;
    var host = el && el.closest && el.closest('foreignObject, text');
    return host && host.closest('.mermaid') ? host : null;
  }
  function mermaidOf(node) {
    var el = node.nodeType === 1 ? node : node.parentNode;
    return el && el.closest && el.closest('.mermaid');
  }
  // The element a reader sees for a mark: the whole formula when the mark sits
  // in its hidden TeX source, else the mark itself.
  function markHost(mark) { return mark.closest('.katex') || mark; }
  function katexOf(node) {
    var el = node.nodeType === 1 ? node : node.parentNode;
    var k = el && el.closest && el.closest('.katex');
    return k && container.contains(k) ? k : null;
  }

  function hasLineBreakBetween(a, b) {
    var range = document.createRange();
    try {
      range.setStart(a, a.nodeValue.length);
      range.setEnd(b, 0);
      return !!range.cloneContents().querySelector('br');
    } catch (e) {
      return false;
    }
  }

  function buildIndex() {
    var text = '';
    var nodes = [];
    var lastWasSpace = true;
    var lastHost = null, lastNode = null, hasPreviousNode = false;
    var walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, {
      acceptNode: function (n) { return isExcluded(n) ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT; }
    });
    while (walker.nextNode()) {
      var node = walker.currentNode;
      var host = diagramLabelHost(node);
      if (hasPreviousNode && host !== lastHost && text && !lastWasSpace) {
        text += ' ';
        lastWasSpace = true;
      } else if (host && host === lastHost && lastNode && text && !lastWasSpace && hasLineBreakBetween(lastNode, node)) {
        text += ' ';
        lastWasSpace = true;
      }
      lastHost = host;
      hasPreviousNode = true;
      lastNode = node;
      var value = node.nodeValue;
      var charIdx = new Array(value.length);
      for (var i = 0; i < value.length; i++) {
        var ch = value.charAt(i);
        if (/\s/.test(ch)) {
          if (lastWasSpace) { charIdx[i] = -1; continue; }
          ch = ' ';
          lastWasSpace = true;
        } else {
          lastWasSpace = false;
        }
        charIdx[i] = text.length;
        text += ch;
      }
      nodes.push({ node: node, charIdx: charIdx });
    }
    index = { text: text, nodes: nodes };
    return index;
  }

  // Normalised index range of a DOM Range (or null if it is outside the article).
  function rangeToOffsets(range) {
    if (!index) buildIndex();
    var start = -1, end = -1;
    for (var n = 0; n < index.nodes.length; n++) {
      var entry = index.nodes[n];
      if (!range.intersectsNode(entry.node)) continue;
      var node = entry.node, len = node.nodeValue.length;
      if (start === -1) {
        for (var k = 0; k <= len; k++) {
          if (range.comparePoint(node, k) >= 0) { start = firstIndexAtOrAfter(entry, k); break; }
        }
      }
      for (var m = len; m >= 0; m--) {
        if (range.comparePoint(node, m) <= 0) {
          var e = lastIndexBefore(entry, m);
          if (e !== -1) end = e + 1;
          break;
        }
      }
    }
    if (start === -1 || end === -1 || end <= start) return null;
    while (start < end && index.text.charAt(start) === ' ') start++;
    while (end > start && index.text.charAt(end - 1) === ' ') end--;
    return end > start ? { start: start, end: end } : null;
  }

  // Chromium can report a selection whose boundary is the heading element
  // itself (rather than its text node) after the heading's feedback buttons are
  // appended. In that case comparePoint() may not intersect any indexed text
  // node even though the user selected visible heading text. Fall back to the
  // normalized selected text so heading comments follow the same path as body
  // comments instead of opening an editor that cannot be submitted.
  function selectionOffsets(range) {
    // Rebuild immediately before converting a user selection. Heading controls,
    // Mermaid, and comment highlights can all change the text-node tree after
    // the last article-wide index was created.
    buildIndex();
    var offsets = rangeToOffsets(range);
    if (offsets) return offsets;
    var exact = (range.toString() || '').replace(/\s+/g, ' ').trim();
    if (!exact) return null;
    if (!index) buildIndex();
    var start = index.text.indexOf(exact);
    return start === -1 ? null : { start: start, end: start + exact.length };
  }

  function firstIndexAtOrAfter(entry, k) {
    for (var i = k; i < entry.charIdx.length; i++) if (entry.charIdx[i] !== -1) return entry.charIdx[i];
    var pos = index.nodes.indexOf(entry);
    for (var n = pos + 1; n < index.nodes.length; n++) {
      var c = index.nodes[n].charIdx;
      for (var j = 0; j < c.length; j++) if (c[j] !== -1) return c[j];
    }
    return index.text.length;
  }

  function lastIndexBefore(entry, m) {
    for (var i = m - 1; i >= 0; i--) if (entry.charIdx[i] !== -1) return entry.charIdx[i];
    var pos = index.nodes.indexOf(entry);
    for (var n = pos - 1; n >= 0; n--) {
      var c = index.nodes[n].charIdx;
      for (var j = c.length - 1; j >= 0; j--) if (c[j] !== -1) return c[j];
    }
    return -1;
  }

  function selectorFromOffsets(o) {
    var t = index.text;
    return {
      exact: t.slice(o.start, o.end),
      prefix: t.slice(Math.max(0, o.start - CONTEXT_CHARS), o.start),
      suffix: t.slice(o.end, o.end + CONTEXT_CHARS),
      section: sectionForOffsets(o)
    };
  }

  // Nearest h2/h3 above the passage — tells the author (and the AI revising the
  // post) *which chapter* a reaction or comment is about. Stored with the
  // reaction (worker `section`) and in the comment header (`位于「…」`).
  var SECTION_MAX = 120;
  function sectionForOffsets(o) {
    var segs = segmentsFor(o.start, o.end);
    if (!segs.length) return '';
    var node = segs[0].node, moment = node.parentElement && node.parentElement.closest('.moment[data-title]');
    if (moment) return moment.getAttribute('data-title');   // a 随笔 entry: its date is the section
    var owner = node.parentElement && node.parentElement.closest('h2, h3'), heads = container.querySelectorAll('h2, h3'), best = owner || null;
    for (var i = 0; i < heads.length; i++) {
      if (heads[i] === owner) break;
      if (heads[i].compareDocumentPosition(node) & Node.DOCUMENT_POSITION_FOLLOWING) best = heads[i]; else break;
    }
    return best ? headingText(best).slice(0, SECTION_MAX) : '';
  }
  // A heading's own words — without the anchor link, reaction buttons and comment markers.
  function headingText(h) {
    var c = h.cloneNode(true), junk = c.querySelectorAll('.sec-react, .heading-anchor, .heading-fold, .annotation-marker');
    for (var i = 0; i < junk.length; i++) junk[i].parentNode.removeChild(junk[i]);
    var katexHtml = c.querySelectorAll('.katex-html');
    for (var k = 0; k < katexHtml.length; k++) katexHtml[k].parentNode.removeChild(katexHtml[k]);
    return c.textContent.replace(/\s+/g, ' ').trim();
  }
  function sectionForExact(exact) {
    var r = anchor({ exact: exact });
    return r ? sectionForOffsets(r) : '';
  }

  // ---------------------------------------------------------------- anchor

  function contextScore(text, start, end, sel) {
    var score = 0;
    var p = sel.prefix || '', s = sel.suffix || '';
    for (var i = 1; i <= p.length && start - i >= 0; i++) {
      if (text.charAt(start - i) === p.charAt(p.length - i)) score++; else break;
    }
    for (var j = 0; j < s.length && end + j < text.length; j++) {
      if (text.charAt(end + j) === s.charAt(j)) score++; else break;
    }
    return score;
  }

  function anchor(sel) {
    if (!index) buildIndex();
    var text = index.text;
    var exact = (sel.exact || '').replace(/\s+/g, ' ').trim();
    if (!exact) return null;

    var hits = [], from = 0, at;
    while ((at = text.indexOf(exact, from)) !== -1) { hits.push(at); from = at + 1; }
    if (hits.length) {
      var best = hits[0], bestScore = -1;
      for (var i = 0; i < hits.length; i++) {
        var hitEnd = hits[i] + exact.length;
        var sc = contextScore(text, hits[i], hitEnd, sel);
        var hitSegs = segmentsFor(hits[i], hitEnd);
        var hitHeading = hitSegs.length && hitSegs[0].node.parentElement && hitSegs[0].node.parentElement.closest('h2, h3, h4, h5, h6');
        // Older links only carried the hash, so short repeated quotes lost
        // their prefix/suffix context. Prefer the heading occurrence for such
        // legacy comments; new links persist the context in :~:text below.
        if (!sel.prefix && !sel.suffix && hitHeading) sc += 100;
        if (sel.section && hitHeading && headingText(hitHeading) === sel.section) sc += 100;
        if (sc > bestScore) { bestScore = sc; best = hits[i]; }
      }
      return { start: best, end: best + exact.length, fuzzy: false };
    }

    if (exact.length < 4 || typeof window.approxStringMatch !== 'function') return null;
    var maxErrors = Math.min(40, Math.max(2, Math.round(exact.length * 0.2)));
    var matches = window.approxStringMatch(text, exact, maxErrors);
    if (!matches.length) return null;
    var chosen = null, chosenScore = -Infinity;
    for (var m = 0; m < matches.length; m++) {
      var mt = matches[m];
      var lengthPenalty = Math.abs((mt.end - mt.start) - exact.length);
      var score = -mt.errors * 3 - lengthPenalty + contextScore(text, mt.start, mt.end, sel);
      if (score > chosenScore) { chosenScore = score; chosen = mt; }
    }
    return chosen ? { start: chosen.start, end: chosen.end, fuzzy: true } : null;
  }

  // ------------------------------------------------------------- highlight

  // Character offsets of [start, end) inside each text node of the current index.
  function segmentsFor(start, end) {
    var segs = [];
    for (var n = 0; n < index.nodes.length; n++) {
      var entry = index.nodes[n], c = entry.charIdx, from = -1, to = -1;
      for (var i = 0; i < c.length; i++) {
        if (c[i] !== -1 && c[i] >= start && c[i] < end) { if (from === -1) from = i; to = i + 1; }
      }
      if (from !== -1) segs.push({ node: entry.node, from: from, to: to });
    }
    return segs;
  }

  // Cut text nodes at every boundary and wrap each piece in ONE highlight
  // carrying every id that covers it (overlaps never nest).
  function wrapPieces(items, className) {
    var perNode = [], byNode = new Map(), marks = [];
    items.forEach(function (it) {
      segmentsFor(it.start, it.end).forEach(function (sg) {
        var bucket = byNode.get(sg.node);
        if (!bucket) { bucket = { node: sg.node, segs: [] }; byNode.set(sg.node, bucket); perNode.push(bucket); }
        bucket.segs.push({ from: sg.from, to: sg.to, id: it.id });
      });
    });
    perNode.forEach(function (bucket) {
      var node = bucket.node, bounds = [];
      bucket.segs.forEach(function (sg) { if (bounds.indexOf(sg.from) === -1) bounds.push(sg.from); if (bounds.indexOf(sg.to) === -1) bounds.push(sg.to); });
      bounds.sort(function (x, y) { return x - y; });
      for (var b = bounds.length - 2; b >= 0; b--) {
        var from = bounds[b], to = bounds[b + 1];
        var ids = bucket.segs.filter(function (sg) { return sg.from <= from && sg.to >= to; }).map(function (sg) { return sg.id; });
        if (!ids.length) continue;
        var piece = node;
        if (to < piece.nodeValue.length) piece.splitText(to);
        if (from > 0) piece = piece.splitText(from);
        if (!piece.nodeValue.trim()) continue;
        var svgText = node.parentNode.namespaceURI === 'http://www.w3.org/2000/svg' && node.parentNode.closest('text');
        var mark = svgText ? document.createElementNS('http://www.w3.org/2000/svg', 'tspan') : document.createElement('mark');
        mark.setAttribute('class', className + (ids.length > 1 ? ' is-multi' : ''));
        mark.setAttribute('data-annotation-ids', ids.join(' '));
        piece.parentNode.insertBefore(mark, piece);
        mark.appendChild(piece);
        marks.push(mark);
      }
    });
    marks.sort(function (x, y) { return x.compareDocumentPosition(y) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1; });
    return marks;
  }

  function unwrap(selector) {
    var marks = container.querySelectorAll(selector);
    for (var i = 0; i < marks.length; i++) {
      var m = marks[i], parent = m.parentNode;
      while (m.firstChild) parent.insertBefore(m.firstChild, m);
      parent.removeChild(m);
    }
    container.normalize();
  }

  function applyHighlights() {
    container.querySelectorAll('td[class*="ts-note"], th[class*="ts-note"]').forEach(function (cell) {
      cell.classList.remove('ts-note', 'ts-note-t', 'ts-note-b', 'ts-note-l', 'ts-note-r');
    });
    var markers = container.querySelectorAll('.annotation-marker');
    for (var i = 0; i < markers.length; i++) markers[i].parentNode.removeChild(markers[i]);
    var sectionMarkers = container.querySelectorAll('.annotation-archived-section');
    for (var sm = 0; sm < sectionMarkers.length; sm++) sectionMarkers[sm].parentNode.removeChild(sectionMarkers[sm]);
    unwrap('mark.annotation-hl, tspan.annotation-hl');
    Array.prototype.forEach.call(container.querySelectorAll('.post-figcaption.has-note, .code-header.has-note'), function (c) { c.classList.remove('has-note', 'has-issue', 'is-resolved', 'is-archived'); });
    Array.prototype.forEach.call(container.querySelectorAll('.katex.has-note'), function (k) { k.className = 'katex'; });
    buildIndex();

    var orphans = [], items = [], anchoredHash = {};
    annotations.forEach(function (a) {
      a.range = anchor(a.selector);
      a.marks = [];
      if (a.range) { items.push({ start: a.range.start, end: a.range.end, id: a.id }); anchoredHash[annotHash(a.selector.exact)] = true; }
      else orphans.push(a);
    });
    // Reaction-only and author-pinned passages use their stored quote too.
    Object.keys(reactions).forEach(function (h) {
      var r = reactions[h];
      r.range = null; r.marks = [];
      if (!(r.up > 0 || r.pinned > 0) || anchoredHash[h]) return;
      r.range = anchor({ exact: r.quote });
      if (r.range) items.push({ start: r.range.start, end: r.range.end, id: 'r:' + h });
    });
    wrapPieces(items, 'annotation-hl').forEach(function (mark) {
      bindMark(mark);
      var issue = false, fixable = false, resolved = true, archived = true;
      mark.getAttribute('data-annotation-ids').split(' ').forEach(function (id) {
        var an = findAnnotation(id), r = null;
        if (an) { an.marks.push(mark); r = reactions[annotHash(an.selector.exact)]; fixable = true; if (!an.resolved) resolved = false; if (!an.archived) archived = false; if (an.issue && !an.resolved) issue = true; }
        else if (id.indexOf('r:') === 0 && reactions[id.slice(2)]) { r = reactions[id.slice(2)]; r.marks.push(mark); }
      });
      if (issue) mark.classList.add('has-issue'); // an open GitHub Issue hangs on this passage
      if (fixable && resolved) mark.classList.add('is-resolved'); // the author fixed every note here
      if (fixable && archived) mark.classList.add('is-archived'); // every visible note was folded on GitHub
      // a note on a figure's caption / a code block's header title (js/figures.js) outlines the whole figure / block
      var cap = mark.closest('.post-figcaption, .code-header'); if (cap) { cap.classList.add('has-note'); if (mark.classList.contains('has-issue')) cap.classList.add('has-issue'); if (mark.classList.contains('is-resolved')) cap.classList.add('is-resolved'); if (mark.classList.contains('is-archived')) cap.classList.add('is-archived'); }
      // a note on a formula: the mark is in the hidden TeX source, so the visible .katex carries the classes
      var host = markHost(mark);
      if (host !== mark) { host.classList.add('has-note'); ['has-issue', 'is-resolved', 'is-archived', 'is-multi'].forEach(function (c) { if (mark.classList.contains(c)) host.classList.add(c); }); }
      var diagram = mark.closest('.mermaid');
      var caption = diagram && diagram.nextElementSibling;
      if (caption && caption.classList.contains('post-figcaption')) {
        caption.classList.add('has-note');
        ['has-issue', 'is-resolved', 'is-archived'].forEach(function (c) { if (mark.classList.contains(c)) caption.classList.add(c); });
      }
    });
    annotations.forEach(function (a) {
      if (!a.selector.cells || !a.marks.length || !window.BlogTableSelect ||
          !window.BlogTableSelect.cellsFrom) return;
      var cell = a.marks[0].closest('td, th');
      if (!cell) return;
      window.BlogTableSelect.cellsFrom(cell, a.selector.cells).forEach(function (item) {
        item.cell.classList.add('ts-note');
        if (item.top) item.cell.classList.add('ts-note-t');
        if (item.bottom) item.cell.classList.add('ts-note-b');
        if (item.left) item.cell.classList.add('ts-note-l');
        if (item.right) item.cell.classList.add('ts-note-r');
      });
    });
    insertMarkers();
    buildIndex();
    renderOrphans(orphans);
    if (panelState && panelState.kind === 'thread') refreshThreadPanel();
    if (panelState && panelState.kind === 'archived-section') refreshArchivedSectionPanel();
  }

  // Every underlined passage: `{ key, ids, list, hash, exact, reaction, marks }` —
  // `list` = the annotations sharing that exact range (may be empty for a
  // reaction-only passage, whose ids are ['r:<hash>']).
  function passages() {
    var groups = {}, out = [];
    annotations.forEach(function (a) {
      if (!a.range || !a.marks.length) return;
      var key = a.range.start + '-' + a.range.end;
      if (!groups[key]) { groups[key] = { key: key, list: [], marks: a.marks }; out.push(groups[key]); }
      groups[key].list.push(a);
    });
    out.forEach(function (g) {
      g.exact = g.list[0].selector.exact; g.hash = annotHash(g.exact);
      g.ids = g.list.map(function (a) { return a.id; }); g.reaction = reactions[g.hash] || null;
    });
    Object.keys(reactions).forEach(function (h) {
      var r = reactions[h];
      if (!r.marks.length || !(r.up > 0 || r.pinned > 0)) return;
      out.push({ key: r.range.start + '-' + r.range.end, ids: ['r:' + h], list: [], hash: h, exact: r.quote, reaction: r, marks: r.marks });
    });
    return out;
  }
  // The passage behind a set of ids (annotation ids or 'r:<hash>'); null when it
  // is not on the page. A reaction-only passage that just got its first comment
  // is found again through the hash.
  function passageFor(ids) {
    var hash = null, a = null;
    for (var i = 0; i < ids.length && !hash; i++) {
      if (ids[i].indexOf('r:') === 0) hash = ids[i].slice(2);
      else if ((a = findAnnotation(ids[i]))) hash = annotHash(a.selector.exact);
    }
    if (!hash) return null;
    var all = passages();
    for (var j = 0; j < all.length; j++) if (all[j].hash === hash) return all[j];
    return null;
  }
  function commentCount(p) { return p.list.reduce(function (n, a) { return n + (a.deleted ? 0 : 1) + (a.replies || []).length; }, 0); }
  // A passage whose every live note the author has marked fixed.
  function passageResolved(p) {
    var live = p.list.filter(function (a) { return !a.deleted; });
    return live.length > 0 && live.every(function (a) { return a.resolved; });
  }
  function passageArchived(p) {
    var live = p.list.filter(function (a) { return !a.deleted; });
    return live.length > 0 && live.every(function (a) { return a.archived; });
  }

  // Notes here that were also filed as a GitHub Issue and are still open.
  function passageIssues(p) { return p.list.filter(function (a) { return !a.deleted && a.issue && !a.resolved; }).length; }
  // The viewer's own (live, top-level) note on this passage, if any.
  function myCommentIn(p) {
    if (!viewer) return null;
    for (var i = 0; i < p.list.length; i++) if (!p.list[i].deleted && p.list[i].author && p.list[i].author.login === viewer.login) return p.list[i];
    return null;
  }
  function markerClass(p) {
    return 'annotation-marker' + (passageIssues(p) ? ' has-issue' : '') + (passageResolved(p) ? ' is-resolved' : '') + (passageArchived(p) ? ' is-archived' : '');
  }

  // One marker per passage: ✓ fixed · ⚑ issue · 💬 comments · 👍 up.
  function markerHtml(p) {
    var r = p.reaction, n = commentCount(p);
    return (passageResolved(p) ? '<i class="fa fa-check-circle"></i>' : '') +
      (passageIssues(p) ? '<i class="fa fa-flag"></i>' : '') +
      (n ? '<i class="fa fa-comment"></i><span class="annotation-marker-count">' + n + '</span>' : '') +
      (r && r.up ? '<i class="fa fa-thumbs-up"></i><span class="annotation-marker-count">' + r.up + '</span>' : '');
  }
  function markerTitle(p) {
    var r = p.reaction, parts = [], n = commentCount(p);
    if (passageArchived(p)) return '已解决（已折叠）· ' + n + ' 条评论，点击查看';
    if (passageResolved(p)) parts.push('作者已修正');
    if (passageIssues(p)) parts.push(passageIssues(p) + ' 个待处理的 Issue');
    if (n) parts.push(n + ' 条评论');
    if (r && r.up) parts.push(r.up + ' 人赞');
    if (r && r.share) parts.push(r.share + ' 次分享');
    return parts.join(' · ') + '，点击查看';
  }
  function markIsClickable(mark) {
    return !!mark.closest('.mermaid') || !mark.closest('a[href]:not(.inline-tip)');
  }
  function markerNeeded(p) {
    return !!markerHtml(p) || (p.marks.length > 0 && !p.marks.some(markIsClickable));
  }
  function insertMarkers() {
    passages().forEach(function (p) {
      if (!markerNeeded(p)) return;
      var last = p.marks[p.marks.length - 1];
      if (last.closest('.mermaid')) return;
      var marker = document.createElement('span');
      marker.className = markerClass(p);
      marker.setAttribute('data-annotation-ids', p.ids.join(' '));
      marker.setAttribute('data-hash', p.hash);
      marker.setAttribute('title', markerTitle(p));
      marker.innerHTML = markerHtml(p) || '<i class="fa fa-comment"></i>';
      marker.addEventListener('click', function (e) {
        e.preventDefault(); e.stopPropagation();
        toggleThread(p.ids, last);
      });
      markHost(last).insertAdjacentElement('afterend', marker);
    });
  }

  function reanchorAll() {
    if (annotations.length || Object.keys(reactions).length) applyHighlights(); else buildIndex();
  }

  function bindMark(mark) {
    if (!markIsClickable(mark)) return; // links keep navigating; use the marker
    var host = markHost(mark);
    if (host !== mark && host._hlClick) host.removeEventListener('click', host._hlClick); // re-anchored: one handler per formula
    var onClick = function (e) {
      e.preventDefault();
      e.stopPropagation();
      toggleThread(idsFor(mark), mark);
    };
    if (host !== mark) host._hlClick = onClick;
    host.addEventListener('click', onClick);
  }

  function idsFor(mark) {
    return (mark.getAttribute('data-annotation-ids') || '').split(' ').filter(Boolean);
  }

  function findAnnotation(id) {
    for (var i = 0; i < annotations.length; i++) if (annotations[i].id === id) return annotations[i];
    return null;
  }

  function flashMarks(marks) {
    marks.forEach(function (m) {
      var host = markHost(m);
      host.classList.add('is-new');
      setTimeout(function () { host.classList.remove('is-new'); }, 2500);
    });
  }

  // Temporary highlight for `#hl=` links: wrap, flash, unwrap.
  function flashRange(range) {
    var marks = wrapPieces([{ start: range.start, end: range.end, id: 'flash' }], 'annotation-flash');
    if (!marks.length) return;
    scrollIntoViewInstant(marks[0]);
    setTimeout(function () { unwrap('mark.annotation-flash, tspan.annotation-flash'); buildIndex(); }, 2600);
  }

  function scrollIntoViewInstant(el) {
    if (window.SectionFold) window.SectionFold.reveal(el);
    window.scrollTo({ top: Math.max(0, el.getBoundingClientRect().top + window.pageYOffset - 160), behavior: 'instant' });
  }

  // #annot-<hash> -> open that thread; #hl=<text> -> flash that passage.
  function focusFromHash() {
    var h = location.hash || '';
    var m = /^#annot-([0-9a-f]{8})/.exec(h);
    if (m) {
      var p = passageFor(['r:' + m[1]]);
      if (p) {
        scrollIntoViewInstant(p.marks[0]);
        flashMarks(p.marks);
        openThread(p.ids, p.marks[p.marks.length - 1]);
        return;
      }
      showToast('这条评论对应的原文找不到了（可能已被修改）');
      return;
    }
    var hl = /^#hl=(.*)$/.exec(h);
    if (hl) {
      var exact;
      try { exact = decodeURIComponent(hl[1]); } catch (e) { exact = hl[1]; }
      var range = anchor({ exact: exact.replace(/\+/g, ' ') });
      if (range) flashRange(range); else showToast('链接指向的文字在本页找不到了');
    }
  }

  // ids of every annotation sharing `a`'s exact passage
  function groupIdsFor(a) {
    return annotations.filter(function (b) {
      return b.range && a.range && b.range.start === a.range.start && b.range.end === a.range.end;
    }).map(function (b) { return b.id; });
  }

  // ---------------------------------------------------------------- panel

  // Block element after which an in-flow panel for `node` is inserted.
  function blockFor(node) {
    var el = node.nodeType === 1 ? node : node.parentNode;
    var diagram = el.closest('.mermaid');
    if (diagram) {
      var caption = diagram.nextElementSibling;
      return caption && caption.classList.contains('post-figcaption') ? caption : diagram;
    }
    var cell = el.closest('td, th');
    var block = cell ? cell.closest('table') : el.closest(BLOCK_SELECTOR);
    // a note on a table cell / header row goes under the table's caption, like a figure's
    if (block && block.tagName === 'TABLE') {
      var wrap = block.parentNode.classList.contains('table-responsive') ? block.parentNode : block;
      var cap = wrap.nextElementSibling;
      if (cap && cap.classList.contains('table-caption')) block = cap;
    }
    // a note on a code block's header title or on a line of code goes under the whole block
    var code = el.closest('.code-block'); if (code) block = code;
    return (block && container.contains(block) && block !== container) ? block : el;
  }

  function ensurePanel() {
    if (panel) return panel;
    panel = document.createElement('div');
    panel.className = 'annotation-panel';
    panel.addEventListener('click', function (e) { e.stopPropagation(); });
    panel.addEventListener('mousedown', function (e) { e.stopPropagation(); });
    return panel;
  }

  function mountPanel(afterNode) {
    ensurePanel();
    var block = blockFor(afterNode);
    block.parentNode.insertBefore(panel, block.nextSibling);
    hideToolbar();
  }

  function closePanel() {
    if (panel && panel.parentNode) panel.parentNode.removeChild(panel);
    panelState = null;
  }

  function toggleThread(ids, anchorMark) {
    if (panelState && panelState.kind === 'thread' && panelState.ids.join() === ids.join()) { closePanel(); return; }
    openThread(ids, anchorMark);
  }

  function openThread(ids, anchorMark) {
    var p = passageFor(ids);
    if (!p) return;
    panelState = { kind: 'thread', ids: p.ids };
    ensurePanel();
    panel.className = 'annotation-panel is-thread';
    renderThread(p);
    mountPanel(anchorMark || p.marks[p.marks.length - 1]);
    if (window.getSelection) window.getSelection().removeAllRanges();
  }

  function refreshThreadPanel() {
    var p = passageFor(panelState.ids);
    if (!p || !p.marks.length) { closePanel(); return; }
    panelState.ids = p.ids;
    renderThread(p);
    if (!panel.parentNode) mountPanel(p.marks[p.marks.length - 1]);
  }

  // Reaction row of the thread panel (also re-rendered alone after a click).
  function reactBarHtml(p) {
    var r = p.reaction || { up: 0, share: 0 }, up = myReaction(p.hash, 'up');
    var saved = window.BlogFavorites && window.BlogFavorites.hasPassage(cfg.path, p.exact);
    return '<button type="button" class="ap-react-btn ap-react-up' + (up ? ' is-on' : '') + '" title="' + (up ? '取消点赞' : '点赞这段话（不用登录）') + '"><i class="fa ' + (up ? 'fa-thumbs-up' : 'fa-regular fa-thumbs-up') + '"></i> 点赞' + (r.up ? ' <b>' + r.up + '</b>' : '') + '</button>' +
      '<button type="button" class="ap-react-btn ap-react-share" title="分享这段话（微博 / X / 微信 / 复制链接）" aria-haspopup="true" aria-expanded="false"><i class="fa fa-share-alt"></i> 分享' + (r.share ? ' <b>' + r.share + '</b>' : '') + '</button>' +
      '<button type="button" class="ap-react-btn ap-react-fav' + (saved ? ' is-on' : '') + '" title="' + (saved ? '取消收藏这段摘录' : '收藏这段摘录') + '"><i class="fa ' + (saved ? 'fa-bookmark' : 'fa-regular fa-bookmark') + '"></i> ' + (saved ? '已收藏' : '收藏') + '</button>' +
      (isOwner() && r.pinned > 0 ? '<button type="button" class="ap-react-btn ap-react-pin">取消划重点</button>' : '');
  }
  function bindReactBar(host, p) {
    host.querySelector('.ap-react-up').addEventListener('click', function () { react(p.exact, 'up'); });
    host.querySelector('.ap-react-share').addEventListener('click', function (e) { e.stopPropagation(); sharePassage(e.currentTarget, p.exact, (p.list || []).length > 0); });
    var favorite = host.querySelector('.ap-react-fav');
    if (favorite) favorite.addEventListener('click', function () { togglePassageFavorite(p); });
    var pin = host.querySelector('.ap-react-pin');
    if (pin) pin.addEventListener('click', function () { setPinned(p.exact, false); });
  }

  function togglePassageFavorite(p) {
    if (!window.BlogFavorites || !p || document.documentElement.classList.contains('private-post')) return;
    var r = p.reaction, list = p.list || [], section = (list[0] && list[0].selector && list[0].selector.section) || (r && r.section) || sectionForExact(p.exact);
    var saved = window.BlogFavorites.togglePassage({
      path: cfg.path,
      title: postTitle(),
      exact: p.exact,
      section: section
    });
    showToast(saved ? '已收藏这段摘录' : '已取消收藏');
  }

  function renderThread(p) {
    var list = p.list, primary = list[0] || null;
    var selector = primary ? primary.selector : { exact: p.exact };
    var total = commentCount(p);
    panel.innerHTML =
      '<div class="ap-head">' +
        '<i class="fa fa-quote-left"></i><span class="ap-quote" title="' + escapeAttr(p.exact) + '">' + escapeHtml(p.exact) + '</span>' +
        '<span class="ap-count">' + (total ? total + ' 条评论' : '还没有评论') + '</span>' +
        '<button type="button" class="ap-close" title="收起">×</button>' +
      '</div>' +
      '<div class="ap-react"></div>' +
      '<div class="ap-thread"></div>' +
      '<div class="ap-editor"></div>';
    panel.querySelector('.ap-close').addEventListener('click', closePanel);
    var reactHost = panel.querySelector('.ap-react');
    reactHost.innerHTML = reactBarHtml(p);
    bindReactBar(reactHost, p);
    var thread = panel.querySelector('.ap-thread');
    var editorHost = panel.querySelector('.ap-editor');
    var replyTo = null; // { annotation, mention } or null = comment on the passage

    function setReplyTarget(a, mentionLogin) {
      replyTo = a ? { annotation: a, mention: mentionLogin } : null;
      var chip = editorHost.querySelector('.ap-reply-chip');
      chip.style.display = replyTo ? 'flex' : 'none';
      if (replyTo) chip.querySelector('span').textContent = '回复 @' + mentionLogin;
      var ta = editorHost.querySelector('.ap-text');
      ta.placeholder = replyTo ? '回复 @' + mentionLogin + '…' : '对这段文字发表评论…';
      editorHost.querySelector('.ap-submit-label').textContent = replyTo ? '回复' : '发表评论';
      var issueOpt = editorHost.querySelector('.ap-issue'); // replies are never issues
      if (issueOpt) { issueOpt.style.display = replyTo ? 'none' : ''; if (replyTo) issueOpt.querySelector('input').checked = false; }
      if (replyTo && mentionLogin !== a.author.login && !ta.value) ta.value = '@' + mentionLogin + ' ';
      ta.focus();
      ta.dispatchEvent(new Event('input'));
    }

    list.forEach(function (a) {
      thread.appendChild(commentEl(a, a.noteHTML, false, function () { setReplyTarget(a, a.author.login); }));
      (a.replies || []).forEach(function (r) {
        thread.appendChild(commentEl(r, r.bodyHTML, true, function () { setReplyTarget(a, r.author.login); }, a));
      });
    });
    renderEditor(editorHost, {
      placeholder: '对这段文字发表评论…',
      submitLabel: '发表评论',
      compact: true,
      replyChip: true,
      issueOption: true,
      onCancel: closePanel,
      onSubmit: function (text, extra) {
        return replyTo ? postReply(replyTo.annotation, text).then(closePanel) : postAnnotation(selector, text, extra.issue);
      },
      fallbackText: function (text) { return replyTo ? text : buildCommentBody(selector, text); }
    });
    editorHost.querySelector('.ap-reply-chip button').addEventListener('click', function () {
      var ta = editorHost.querySelector('.ap-text');
      if (replyTo && ta.value.trim() === '@' + replyTo.mention) ta.value = '';
      setReplyTarget(null);
    });
  }

  // `parent` is the top-level annotation a reply belongs to (undefined for top-level).
  function commentEl(c, html, isReply, onReply, parent) {
    var el = document.createElement('div');
    var mine = !c.deleted && viewer && c.author && viewer.login === c.author.login;
    if (c.deleted) onReply = null;
    var canReply = !!onReply && !(c.archived || (parent && parent.archived));
    el.className = 'ap-comment' + (isReply ? ' is-reply' : '') + (c.issue ? ' has-issue' : '') + (c.deleted ? ' is-deleted' : '') + (c.resolved ? ' is-resolved' : '') + (c.archived ? ' is-archived' : '');
    el.setAttribute('data-comment-id', c.id);
    el.innerHTML =
      '<a class="ap-avatar" href="' + escapeAttr(c.author.url) + '" target="_blank" rel="noopener noreferrer" aria-label="' + escapeAttr(c.author.login) + '"><img src="' + escapeAttr(c.author.avatarUrl) + '" alt=""></a>' +
      '<div class="ap-comment-main">' +
        '<div class="ap-comment-meta"><a href="' + escapeAttr(c.author.url) + '" target="_blank" rel="noopener noreferrer">' + escapeHtml(c.author.login) + '</a>' +
          (c.owner && !c.deleted ? '<span class="ap-owner" title="博客作者">作者</span>' : '') +
          '<time datetime="' + escapeAttr(c.createdAt) + '" title="' + escapeAttr(new Date(c.createdAt).toLocaleString()) + '">' + relativeTime(c.createdAt) + '</time>' +
          (c.lastEditedAt ? '<span class="ap-edited" title="' + escapeAttr(new Date(c.lastEditedAt).toLocaleString()) + '">已编辑</span>' : '') +
          (c.issue ? '<a class="ap-issue-badge" href="' + escapeAttr(c.issue.url) + '" target="_blank" rel="noopener noreferrer" title="' + (c.resolved ? '对应的 GitHub Issue 已关闭' : '已同时提交为 GitHub Issue') + '"><i class="fa fa-flag"></i> Issue' + (c.issue.number ? ' #' + c.issue.number : '') + '</a>' : '') +
          (c.archived ? '<span class="ap-resolved-badge is-archived" title="GitHub 上的评论已折叠"><i class="fa fa-check-circle"></i> 已解决</span>' : c.resolved ? '<span class="ap-resolved-badge" title="' + (c.issue ? 'Issue 已关闭：作者已处理' : '作者已修正原文') + '"><i class="fa fa-check-circle"></i> 已修正</span>' : '') +
          '<span class="ap-meta-actions">' +
            (c.deleted ? '' : voteHtml(c)) +
            (canReply ? '<button type="button" class="ap-reply-btn"><i class="fa fa-reply"></i> 回复</button>' : '') +
            (mine ? '<button type="button" class="ap-edit-btn" title="编辑"><i class="fa fa-pencil"></i> 编辑</button>' +
                    '<button type="button" class="ap-delete-btn" title="删除"><i class="fa fa-regular fa-trash-can"></i> 删除</button>' : '') +
            '<a class="ap-github" href="' + escapeAttr(c.url) + '" target="_blank" rel="noopener noreferrer" title="在 GitHub 上查看 / 编辑"><i class="fa fa-brands fa-github"></i></a>' +
          '</span>' +
        '</div>' +
        '<div class="ap-comment-body"></div>' +
      '</div>';
    var body = el.querySelector('.ap-comment-body');
    if (c.deleted) body.innerHTML = '<em class="ap-muted">此评论已删除</em>';
    else {
      body.appendChild(sanitizeHtml(html));
      InlinePopover.renderMathIfPresent(body);
      // same hash twice fires no hashchange — re-run the focus by hand
      body.addEventListener('click', function (e) {
        var a = e.target.closest && e.target.closest('a[href^="#"]');
        if (a && location.hash === a.getAttribute('href')) { e.preventDefault(); focusFromHash(); }
      });
    }
    if (canReply) el.querySelector('.ap-reply-btn').addEventListener('click', onReply);
    if (!c.deleted) bindVote(el.querySelector('.ap-vote'), c);
    if (mine) {
      el.querySelector('.ap-edit-btn').addEventListener('click', function () { startEdit(el, c, isReply, parent); });
      el.querySelector('.ap-delete-btn').addEventListener('click', function () { deleteComment(el, c, isReply, parent); });
    }
    return el;
  }

  // ------------------------------------------------- edit / delete (own comments)

  var NODE_BODY = 'query($id: ID!) { node(id: $id) { ... on DiscussionComment { body } } }';
  var UPDATE_COMMENT = 'mutation($id: ID!, $body: String!) { updateDiscussionComment(input: {commentId: $id, body: $body}) { comment { id bodyHTML } } }';
  var DELETE_COMMENT = 'mutation($id: ID!) { deleteDiscussionComment(input: {id: $id}) { comment { id } } }';

  // A top-level note's raw body starts with the quote header; only the note is editable.
  function stripQuoteHeader(md) {
    var lines = md.replace(/\r/g, '').split('\n'), i = 0;
    if (!/^>/.test(lines[0])) return md;
    while (i < lines.length && /^>/.test(lines[i])) i++;
    while (i < lines.length && !lines[i].trim()) i++;
    return lines.slice(i).join('\n');
  }

  function startEdit(el, c, isReply, parent) {
    if (el.querySelector('.ap-inline-editor')) return;
    var bodyEl = el.querySelector('.ap-comment-body');
    var inPanel = !!el.closest('.annotation-panel');
    var host = document.createElement('div');
    host.className = 'ap-editor ap-inline-editor';
    host.innerHTML = '<span class="ap-muted">正在读取原文…</span>';
    bodyEl.style.display = 'none';
    bodyEl.parentNode.insertBefore(host, bodyEl.nextSibling);
    function cancel() { host.parentNode.removeChild(host); bodyEl.style.display = ''; }
    graphql(NODE_BODY, { id: c.id }).then(function (data) {
      var raw = (data.node && data.node.body) || '';
      var ta = renderEditor(host, {
        placeholder: '编辑评论…',
        submitLabel: '保存',
        compact: true,
        inline: true,
        initialText: c.selector ? stripQuoteHeader(raw) : raw,
        onCancel: cancel,
        onSubmit: function (text) {
          var body = c.selector ? buildCommentBody(c.selector, text, c.issue) : text;
          return graphql(UPDATE_COMMENT, { id: c.id, body: body }).then(function (res) {
            c.bodyHTML = res.updateDiscussionComment.comment.bodyHTML;
            c.lastEditedAt = new Date().toISOString();
            if (!isReply) { var keep = c.selector; c.selector = null; c.noteHTML = null; c.issue = null; parseBodyHeader(c); if (!c.selector && keep) { c.selector = keep; c.noteHTML = c.bodyHTML; } }
            if (inPanel) closePanel(); // same as posting: the panel goes, the flash confirms
            syncViews();
            if (inPanel && c.marks && c.marks.length) flashMarks(c.marks);
            flashComment(c.id);
            showToast('已保存');
          });
        }
      });
      ta.focus();
    }).catch(function (err) { host.innerHTML = '<span class="ap-status is-error">' + escapeHtml(err.message) + '</span>'; setTimeout(cancel, 2500); });
  }

  function deleteComment(el, c, isReply, parent) {
    var replies = c.replies || [];
    var n = !isReply && replies.length;
    // GitHub soft-deletes a comment that has replies: the replies stay and the
    // comment shows as 「此评论已删除」 (same as on GitHub). A highlighted passage
    // loses its anchor with the quote header, so it disappears from the article.
    var msg = n
      ? (c.selector ? '删除这条评论？它的 ' + n + ' 条回复会保留（显示为「此评论已删除」），但这段划线会从文中消失。' : '删除这条评论？它的 ' + n + ' 条回复会保留，原位显示为「此评论已删除」。')
      : '删除这条评论？';
    if (!window.confirm(msg)) return;
    el.classList.add('is-deleting');
    graphql(DELETE_COMMENT, { id: c.id }).then(function () {
      if (isReply) {
        parent.replies = parent.replies.filter(function (r) { return r.id !== c.id; });
        parent.replyCount = parent.replies.length;
      } else if (replies.length) {
        c.deleted = true; c.bodyHTML = ''; c.selector = null; c.noteHTML = null; c.issue = null;
        if (panelState) panelState.ids = panelState.ids.filter(function (id) { return id !== c.id; });
      } else {
        allComments = allComments.filter(function (a) { return a.id !== c.id; });
        if (panelState) panelState.ids = panelState.ids.filter(function (id) { return id !== c.id; });
      }
      syncViews(); // re-renders (or closes) the thread panel and the comment section
      showToast('已删除');
    }).catch(function (err) { el.classList.remove('is-deleting'); showToast('删除失败：' + err.message); });
  }

  // ------------------------------------------------- bottom comment section

  // The classic comment list under the post: the same discussion, the same
  // commentEl / renderEditor as the in-article panel, so every comment (with or
  // without a passage) gets the same reply / edit / delete / issue controls.
  function initCommentSection() {
    commentsHost = cfg.section.querySelector('.annotation-comments');
    if (!commentsHost) return;
    commentsHost.innerHTML =
      '<div class="ac-head"><span class="ac-count">正在加载评论…</span></div>' +
      '<div class="ac-hot"></div>' +
      '<div class="ac-list"></div>' +
      '<div class="ap-editor ac-editor"></div>';
    renderEditor(commentsHost.querySelector('.ac-editor'), {
      placeholder: '写下你的评论…（想针对某句话说？选中正文里的文字，点「评论」）',
      submitLabel: '发表评论',
      issueOption: true,
      clearOnSubmit: true,
      initialText: readCommentDraft(),
      onChange: saveCommentDraft,
      beforeLogin: saveCommentDraft,
      onSubmit: function (text, extra) { return postComment(text, extra.issue).then(function () { saveCommentDraft(''); }); }
    });
  }

  function renderCommentSection() {
    if (!commentsHost) return;
    renderLikeBar();
    renderHotPassages();
    var list = commentsHost.querySelector('.ac-list');
    list.innerHTML = '';
    if (!comments.length) {
      if (!loadError) list.innerHTML = '<p class="ac-empty">还没有评论。可以在下方留言，也可以选中正文任意文字，对那句话发表评论。</p>';
      return;
    }
    comments.forEach(function (c) {
      var group = document.createElement('div');
      group.className = 'ac-group' + (c.archived ? ' is-archived' : '');
      group.appendChild(commentEl(c, c.bodyHTML, false, function () { openInlineReply(c, c.author.login, group); }, null));
      c.replies.forEach(function (r) {
        group.appendChild(commentEl(r, r.bodyHTML, true, function () { openInlineReply(c, r.author.login, group); }, c));
      });
      list.appendChild(group);
    });
  }

  // 「最受关注的段落」: the anchored passages ranked by votes + activity, Medium's
  // "top highlight" — only when there is something to rank (2+ passages), max 3.
  var HOT_MAX = 3;
  function renderHotPassages() {
    var host = commentsHost.querySelector('.ac-hot');
    // score = passage 赞 + 2 × net comment votes + comments
    var ranked = passages().filter(function (p) { return !passageResolved(p); }).map(function (p) {
      var votes = 0, n = commentCount(p), r = p.reaction || { up: 0 };
      p.list.forEach(function (a) {
        votes += a.votes.up - a.votes.down;
        a.replies.forEach(function (x) { votes += x.votes.up - x.votes.down; });
      });
      return { p: p, votes: votes, comments: n, up: r.up, score: r.up + votes * 2 + n };
    }).filter(function (x) { return x.score > 0; }).sort(function (x, y) { return y.score - x.score; });
    if (ranked.length < 2) { host.innerHTML = ''; return; }
    host.innerHTML = '<div class="ac-hot-title"><i class="fa fa-fire"></i> 最受关注的段落</div>';
    ranked.slice(0, HOT_MAX).forEach(function (x) {
      var p = x.p, meta = [];
      if (x.up) meta.push('<i class="fa fa-thumbs-up"></i> ' + x.up);
      if (x.votes) meta.push('<i class="fa fa-caret-up"></i> ' + x.votes);
      if (x.comments) meta.push(x.comments + ' 条评论');
      var item = document.createElement('a');
      item.className = 'ac-hot-item';
      item.href = '#annot-' + p.hash;
      item.innerHTML =
        '<span class="ac-hot-quote">' + escapeHtml(p.exact) + '</span>' +
        '<span class="ac-hot-meta">' + meta.join(' · ') + '</span>';
      item.addEventListener('click', function (e) {
        e.preventDefault();
        var q = passageFor(p.ids);
        if (!q) return;
        scrollIntoViewInstant(q.marks[0]);
        flashMarks(q.marks);
        openThread(q.ids, q.marks[q.marks.length - 1]);
      });
      host.appendChild(item);
    });
  }

  // Head of the comment section: comment count and the GitHub link. Re-rendered
  // on its own after a views / load response so the editors below are left alone.
  function renderLikeBar() {
    if (!commentsHost) return;
    var head = commentsHost.querySelector('.ac-head');
    var total = comments.reduce(function (n, c) { return n + (c.archived ? 0 : (c.deleted ? 0 : 1) + c.replies.length); }, 0);
    var archivedCount = allComments.filter(function (c) { return c.archived; }).length;
    head.innerHTML =
      '<span class="ac-count">' + (loadError ? '<i class="fa fa-exclamation-circle"></i> 评论加载失败：' + escapeHtml(loadError.message)
        : !loaded ? '正在加载评论…' : '<i class="fa fa-regular fa-comment"></i> ' + total + ' 条评论') + '</span>' +
      (isOwner() && archivedCount ? '<button type="button" class="ac-archived-toggle" title="只有你能看到：在原文位置显示已折叠（已解决）的评论">' + (showArchived() ? '隐藏已解决' : '显示已解决（' + archivedCount + '）') + '</button>' : '') +
      (discussion && discussion.url ? '<a class="ac-github" href="' + escapeAttr(discussion.url) + '" target="_blank" rel="noopener noreferrer" title="这个讨论串在 GitHub Discussions 上"><i class="fa fa-brands fa-github"></i> GitHub</a>' : '');
    var archivedToggle = head.querySelector('.ac-archived-toggle');
    if (archivedToggle) archivedToggle.addEventListener('click', function () {
      try { localStorage.setItem(SHOW_ARCHIVED_KEY, showArchived() ? '0' : '1'); } catch (e) { /* ignore */ }
      if (!showArchived() && panelState && panelState.kind === 'archived-section') closePanel();
      syncViews();
    });
    // Header meta badges (`.post-stats`) are painted by js/share.js; hand it
    // what we know (views once the worker answered, comment count once loaded).
    var stats = {};
    if (pageViews !== null) stats.views = pageViews;
    if (loaded) stats.comments = total;
    if (Object.keys(stats).length) { try { document.dispatchEvent(new CustomEvent('blog:stats', { detail: stats })); } catch (e) { /* old browsers */ } }
  }

  // Reply box right under the comment's replies (only one open at a time).
  function openInlineReply(c, mention, group) {
    var old = commentsHost.querySelector('.ac-reply-editor');
    if (old) old.parentNode.removeChild(old);
    var host = document.createElement('div');
    host.className = 'ap-editor ac-reply-editor';
    group.appendChild(host);
    var ta = renderEditor(host, {
      placeholder: '回复 @' + mention + '…',
      submitLabel: '回复',
      compact: true,
      inline: true,
      initialText: mention !== c.author.login ? '@' + mention + ' ' : '',
      onCancel: function () { host.parentNode.removeChild(host); },
      onSubmit: function (text) { return postReply(c, text); }
    });
    ta.focus();
  }

  function commentDraftKey() { return 'commentDraft:' + cfg.path; }
  function saveCommentDraft(text) {
    try { if (text && text.trim()) sessionStorage.setItem(commentDraftKey(), text); else sessionStorage.removeItem(commentDraftKey()); } catch (e) { /* ignore */ }
  }
  function readCommentDraft() { try { return sessionStorage.getItem(commentDraftKey()) || ''; } catch (e) { return ''; } }

  function logout() {
    try { localStorage.removeItem(SESSION_KEY); } catch (e) { /* ignore */ }
    token = null; viewer = null;
    try { document.dispatchEvent(new CustomEvent('blog:viewer', { detail: null })); } catch (e) { /* old browsers */ }
    updateToolbar();
    var hosts = document.querySelectorAll('.ap-editor');
    for (var i = 0; i < hosts.length; i++) if (hosts[i].querySelector('.ap-user')) refreshAuthUI(hosts[i]);
    if (discussion) discussion.likes.mine = null; // the counts stay, our own vote marks go
    allComments.forEach(function (c) { c.votes.mine = null; c.replies.forEach(function (r) { r.votes.mine = null; }); });
    if (!refilterComments()) renderCommentSection();
    if (panelState && panelState.kind === 'thread') refreshThreadPanel();
  }

  // ---------------------------------------------------------------- editor

  // Shared editor block: textarea + preview + auth + buttons. `opts.onSubmit(text)`
  // returns a promise; the editor shows errors and offers the clipboard fallback.
  var FORMAT_BUTTONS = [
    { key: 'bold', icon: 'fa-bold', title: '加粗 (**文字**)' },
    { key: 'italic', icon: 'fa-italic', title: '斜体 (*文字*)' },
    { key: 'heading', icon: 'fa-header', title: '标题 (### )' },
    { key: 'quote', icon: 'fa-quote-right', title: '引用 (> )' },
    { key: 'code', icon: 'fa-code', title: '行内代码 (`code`)' },
    { key: 'codeblock', icon: 'fa-regular fa-file-code', title: '代码块 (```)' },
    { key: 'table', icon: 'fa-table', title: '表格 (3×3)' },
    { key: 'link', icon: 'fa-link', title: '链接 [文字](url)' },
    { key: 'image', icon: 'fa-regular fa-image', title: '插入图片（也可直接粘贴截图 / 拖拽）' },
    { key: 'screenshot', icon: 'fa-scissors', title: '截图（截取当前页面或其他窗口，截图时自动隐藏评论框）' },
    { key: 'ul', icon: 'fa-list-ul', title: '无序列表 (- )' },
    { key: 'ol', icon: 'fa-list-ol', title: '有序列表 (1. )' }
  ];

  function renderEditor(host, opts) {
    host.classList.toggle('is-inline', !!opts.inline);
    host.innerHTML =
      '<div class="ap-tabs"><button type="button" class="is-active" data-tab="write">撰写</button><button type="button" data-tab="preview">预览</button>' +
        '<span class="ap-format">' + FORMAT_BUTTONS.map(function (b) {
          if (b.key === 'screenshot' && !(navigator.mediaDevices && typeof navigator.mediaDevices.getDisplayMedia === 'function')) return '';
          return '<button type="button" data-format="' + b.key + '" title="' + b.title + '"><i class="fa ' + b.icon + '"></i></button>';
        }).join('') + '</span>' +
      '</div>' +
      (opts.replyChip ? '<div class="ap-reply-chip" style="display:none"><i class="fa fa-reply"></i><span></span><button type="button" title="改为对这段文字评论">×</button></div>' : '') +
      '<textarea class="ap-text" rows="' + (opts.compact ? 4 : 6) + '" placeholder="' + escapeAttr(opts.placeholder) + '"></textarea>' +
      '<input class="ap-image-input" type="file" accept="image/*" multiple hidden>' +
      '<div class="ap-preview" style="display:none"></div>' +
      '<div class="ap-status" style="display:none"></div>' +
      '<div class="ap-footer">' +
        '<span class="ap-user"></span>' +
        '<span class="ap-buttons">' +
          '<span class="ap-hint">Markdown · 可粘贴截图 · ⌘/Ctrl+Enter 提交</span>' +
          (opts.issueOption && cfg.issues ? '<label class="ap-issue" title="除评论外，再以你的名义在 GitHub 仓库创建一个 Issue，提醒作者这里可能有问题"><input type="checkbox"><i class="fa fa-flag"></i> 同时提交 Issue</label>' : '') +
          (opts.onCancel ? '<button type="button" class="ap-cancel">取消</button>' : '') +
          '<button type="button" class="ap-login"><i class="fa fa-brands fa-github"></i> 使用 GitHub 登录</button>' +
          '<button type="button" class="ap-submit" disabled><i class="fa fa-paper-plane"></i> <span class="ap-submit-label">' + escapeHtml(opts.submitLabel) + '</span></button>' +
        '</span>' +
      '</div>';
    var ta = host.querySelector('.ap-text');
    var submitBtn = host.querySelector('.ap-submit');
    var imageInput = host.querySelector('.ap-image-input');
    var pendingUploads = 0, uploadSequence = 0, uploadError = '';
    if (opts.initialText) ta.value = opts.initialText;
    function updateSubmitState() {
      host._apPendingUploads = pendingUploads;
      submitBtn.disabled = host.classList.contains('is-busy') || pendingUploads > 0 || !ta.value.trim();
    }
    ta.addEventListener('input', function () { updateSubmitState(); if (opts.onChange) opts.onChange(ta.value); });
    updateSubmitState();
    if (opts.onCancel) host.querySelector('.ap-cancel').addEventListener('click', opts.onCancel);
    host.querySelector('.ap-login').addEventListener('click', function () { if (opts.beforeLogin) opts.beforeLogin(ta.value); login(); });
    var tabs = host.querySelectorAll('.ap-tabs button[data-tab]');
    // the preview shows the comment as it will be posted — with the quote header
    // for a passage comment, so nobody wonders whether the quote is kept
    for (var i = 0; i < tabs.length; i++) tabs[i].addEventListener('click', function () { switchTab(host, this.getAttribute('data-tab'), opts.fallbackText); });
    var fmts = host.querySelectorAll('.ap-format button');
    for (var f = 0; f < fmts.length; f++) fmts[f].addEventListener('click', function () {
      switchTab(host, 'write');
      var format = this.getAttribute('data-format');
      if (format === 'image') {
        imageInput.click();
        return;
      }
      if (format === 'screenshot') {
        if (!getSession()) {
          setStatus(host, '登录 GitHub 后才能截图', 'error');
          return;
        }
        captureScreenshot({ copyToClipboard: true }).then(function (shot) {
          if (!shot) return;
          if (shot.action === 'share') shareScreenshot(shot.blob);
          else return handleCopiedScreenshot(ta, host, shot);
        }).catch(function (err) {
          setStatus(host, '截图失败：' + (err.message || String(err)), 'error');
        }).then(function () {
          if (ta.isConnected) ta.focus();
        });
        return;
      }
      applyFormat(ta, format);
    });

    imageInput.addEventListener('change', function () {
      uploadImages(Array.prototype.slice.call(imageInput.files || []));
      imageInput.value = '';
    });
    ta.addEventListener('paste', function (e) {
      var files = transferImages(e.clipboardData);
      if (!files.length) return;
      e.preventDefault();
      uploadImages(files);
    });
    ta.addEventListener('dragover', function (e) {
      if (!transferImages(e.dataTransfer).length) return;
      e.preventDefault();
      host.classList.add('is-dragover');
    });
    ta.addEventListener('dragleave', function (e) {
      if (!e.relatedTarget || !host.contains(e.relatedTarget)) host.classList.remove('is-dragover');
    });
    ta.addEventListener('drop', function (e) {
      host.classList.remove('is-dragover');
      var files = transferImages(e.dataTransfer);
      if (!files.length) return;
      e.preventDefault();
      uploadImages(files);
    });

    function uploadImages(files) {
      files = (files || []).filter(function (file) { return file && /^image\//i.test(file.type || ''); });
      if (!files.length) return;
      if (!getSession()) {
        setStatus(host, '登录 GitHub 后才能粘贴图片', 'error');
        return;
      }
      files.forEach(uploadImage);
    }
    ta._apUploadImages = uploadImages;

    function uploadImage(file) {
      var placeholder = '![上传中 截图-' + (++uploadSequence) + '…]()';
      var start = ta.selectionStart, end = ta.selectionEnd;
      ta.setRangeText(placeholder, start, end, 'end');
      ta.dispatchEvent(new Event('input', { bubbles: true }));
      pendingUploads++;
      updateSubmitState();
      setStatus(host, '正在上传图片…');

      ensureToken().then(function (tk) {
        return compressCommentImage(file).then(function (blob) {
          return fetch(cfg.api + '/comments/images', {
            method: 'POST',
            headers: { Authorization: 'Bearer ' + tk, 'Content-Type': blob.type },
            body: blob
          }).then(function (r) {
            return r.json().then(function (data) {
              if (!r.ok) throw new Error((data && data.error) || ('HTTP ' + r.status));
              return data;
            });
          });
        });
      }).then(function (data) {
        replacePlaceholder(placeholder, '![' + '截图' + '](' + data.src + ')');
      }).catch(function (err) {
        replacePlaceholder(placeholder, '');
        uploadError = err.message || String(err);
      }).then(function () {
        pendingUploads--;
        updateSubmitState();
        if (pendingUploads) setStatus(host, '正在上传图片…');
        else if (uploadError) {
          setStatus(host, uploadError, 'error');
          uploadError = '';
        } else setStatus(host, '');
      });
    }

    function replacePlaceholder(placeholder, replacement) {
      var at = ta.value.indexOf(placeholder);
      if (at < 0) return;
      ta.setRangeText(replacement, at, at + placeholder.length, 'preserve');
      ta.dispatchEvent(new Event('input', { bubbles: true }));
    }

    function submit() {
      if (pendingUploads) return;
      var text = ta.value.trim();
      if (!text) return;
      var issueBox = host.querySelector('.ap-issue input');
      var wantIssue = !!(issueBox && issueBox.checked);
      setBusy(host, true);
      setStatus(host, wantIssue ? '正在创建 Issue…' : '正在发表…');
      opts.onSubmit(text, { issue: wantIssue }).then(function () {
        setBusy(host, false);
        if (opts.clearOnSubmit && host.isConnected) {
          ta.value = '';
          if (issueBox) issueBox.checked = false;
          switchTab(host, 'write');
          setStatus(host, '');
          updateSubmitState();
        }
      }).catch(function (err) {
        setBusy(host, false);
        updateSubmitState();
        var msg = err.message || String(err);
        if (err.issuesDisabled) { cfg.issues = false; var lbl = host.querySelector('.ap-issue'); if (lbl) lbl.parentNode.removeChild(lbl); }
        setStatus(host, msg + ' — 也可以复制内容后粘贴到文末评论框发表。', 'error');
        var fallback = document.createElement('button');
        fallback.type = 'button';
        fallback.className = 'ap-fallback';
        fallback.innerHTML = '<i class="fa fa-clipboard"></i> 复制内容';
        fallback.addEventListener('click', function () {
          copyText(opts.fallbackText ? opts.fallbackText(text) : text).then(function () {
            showToast('已复制，请粘贴到评论框（⌘/Ctrl+V）');
            var target = document.getElementById('comments');
            if (target) InlinePopover.scrollToTargetWithOffset(target);
          });
        });
        host.querySelector('.ap-status').appendChild(document.createTextNode(' '));
        host.querySelector('.ap-status').appendChild(fallback);
        refreshAuthUI(host);
      });
    }
    submitBtn.addEventListener('click', submit);
    ta.addEventListener('keydown', function (e) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') { e.preventDefault(); submit(); return; }
      if ((e.metaKey || e.ctrlKey) && !e.shiftKey && (e.key === 'b' || e.key === 'i' || e.key === 'k')) {
        e.preventDefault();
        applyFormat(ta, e.key === 'b' ? 'bold' : e.key === 'i' ? 'italic' : 'link');
      }
    });
    refreshAuthUI(host);
    return ta;
  }

  function supportsScreenshotCapture() {
    return !!(navigator.mediaDevices && typeof navigator.mediaDevices.getDisplayMedia === 'function');
  }

  function viewportRectFor(surface, frameWidth, frameHeight, metrics) {
    var fullFrame = { left: 0, top: 0, width: frameWidth, height: frameHeight };
    var rect;
    if (surface === 'window') {
      if (!metrics.outerWidth || !metrics.outerHeight) return fullFrame;
      var sx = frameWidth / metrics.outerWidth;
      var sy = frameHeight / metrics.outerHeight;
      rect = {
        left: (metrics.outerWidth - metrics.innerWidth) / 2 * sx,
        top: (metrics.outerHeight - metrics.innerHeight) * sy,
        width: metrics.innerWidth * sx,
        height: metrics.innerHeight * sy
      };
    } else if (surface === 'monitor') {
      if (!metrics.screenWidth) return fullFrame;
      var scale = frameWidth / metrics.screenWidth;
      var originX = metrics.screenX - metrics.screenAvailLeft;
      var originY = metrics.screenY - metrics.screenAvailTop;
      rect = {
        left: (originX + (metrics.outerWidth - metrics.innerWidth) / 2) * scale,
        top: (originY + metrics.outerHeight - metrics.innerHeight) * scale,
        width: metrics.innerWidth * scale,
        height: metrics.innerHeight * scale
      };
    } else {
      return fullFrame;
    }
    var area = rect.width * rect.height;
    var left = Math.max(0, rect.left);
    var top = Math.max(0, rect.top);
    var width = Math.max(0, Math.min(frameWidth, rect.left + rect.width) - left);
    var height = Math.max(0, Math.min(frameHeight, rect.top + rect.height) - top);
    if (!isFinite(area) || area <= 0 || width * height < area * 0.5 || width < 50 || height < 50) return fullFrame;
    return { left: left, top: top, width: width, height: height };
  }

  function transparentColor(color) {
    return !color || color === 'transparent' || /rgba\([^)]*,\s*0(?:\.0+)?\)$/.test(color);
  }

  function captureBackgroundColor() {
    var candidates = [document.body, document.documentElement, container];
    for (var i = 0; i < candidates.length; i++) {
      if (!candidates[i]) continue;
      var color = window.getComputedStyle(candidates[i]).backgroundColor;
      if (!transparentColor(color)) return color;
    }
    return '#fff';
  }

  function isolateBlockCloneIds(root) {
    var svgNamespace = 'http://www.w3.org/2000/svg';
    var prefix = 'ap-block-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2) + '-';
    var idMap = Object.create(null);
    var nodes = [root].concat(Array.prototype.slice.call(root.querySelectorAll('*')));
    for (var i = 0; i < nodes.length; i++) {
      var id = nodes[i].getAttribute && nodes[i].getAttribute('id');
      if (!id) continue;
      if (nodes[i].namespaceURI === svgNamespace) {
        idMap[id] = prefix + id;
        nodes[i].setAttribute('id', idMap[id]);
      } else {
        nodes[i].removeAttribute('id');
      }
    }
    for (var n = 0; n < nodes.length; n++) {
      var attrs = nodes[n].attributes;
      if (!attrs) continue;
      for (var a = 0; a < attrs.length; a++) {
        var attr = attrs[a], value = attr.value;
        if ((attr.name === 'href' || attr.name === 'xlink:href') && value.charAt(0) === '#' && idMap[value.slice(1)]) {
          value = '#' + idMap[value.slice(1)];
        }
        value = value.replace(/url\(\s*(['"]?)#([^)'"]+)\1\s*\)/g, function (match, quote, ref) {
          return idMap[ref] ? 'url(' + quote + '#' + idMap[ref] + quote + ')' : match;
        });
        if (/^(aria-labelledby|aria-describedby|for)$/i.test(attr.name)) {
          value = value.split(/\s+/).map(function (ref) { return idMap[ref] || ref; }).join(' ');
        }
        if (value !== attr.value) nodes[n].setAttribute(attr.name, value);
      }
      if (nodes[n].namespaceURI === svgNamespace && nodes[n].tagName.toLowerCase() === 'style') {
        nodes[n].textContent = nodes[n].textContent.replace(/([^{}]+)(?=\{)/g, function (selector) {
          return selector.replace(/#([A-Za-z_][\w:.-]*)/g, function (match, ref) {
            return idMap[ref] ? '#' + idMap[ref] : match;
          });
        });
      }
    }
  }

  function captureBlock(blockEl, opts) {
    opts = opts || {};
    if (!blockEl || blockEl.nodeType !== 1 || !container || !container.contains(blockEl)) return Promise.resolve(null);
    var sourceRect = blockEl.getBoundingClientRect();
    if (sourceRect.width < 1 || sourceRect.height < 1) return Promise.reject(new Error('无法显示要截图的内容'));
    var sizingSelector = '.table-responsive, table, pre, .code-block';
    var sourceSizingNodes = [blockEl];
    var sourceAncestor = blockEl.parentElement;
    while (sourceAncestor && sourceAncestor !== container) {
      if (sourceAncestor.matches(sizingSelector)) sourceSizingNodes.push(sourceAncestor);
      sourceAncestor = sourceAncestor.parentElement;
    }
    sourceSizingNodes = sourceSizingNodes.concat(Array.prototype.slice.call(blockEl.querySelectorAll(sizingSelector)));
    var fullWidth = sourceRect.width;
    for (var i = 0; i < sourceSizingNodes.length; i++) {
      fullWidth = Math.max(fullWidth, sourceSizingNodes[i].scrollWidth || 0);
    }

    var padding = typeof opts.padding === 'number' ? Math.max(0, opts.padding) : 48;
    var margin = typeof opts.margin === 'number' ? Math.max(0, opts.margin) : 12;
    var presenter = document.createElement('div');
    presenter.className = 'ap-block-present';
    presenter.style.backgroundColor = captureBackgroundColor();
    var frame = document.createElement('div');
    frame.className = 'ap-block-present-frame';
    var content = document.createElement('div');
    content.className = 'ap-block-present-content';
    var article = document.createElement('div');
    article.className = (container.className || 'post-container') + ' ap-block-present-article';
    article.style.width = fullWidth + 'px';
    article.style.maxWidth = 'none';
    article.style.margin = '0';
    article.style.padding = '0';
    article.style.float = 'none';
    var clone = blockEl.cloneNode(true);
    var cloneStyle = window.getComputedStyle(blockEl);
    if (cloneStyle.width && cloneStyle.width !== 'auto') clone.style.width = cloneStyle.width;
    clone.style.boxSizing = cloneStyle.boxSizing;
    var branch = clone;
    var ancestor = blockEl.parentElement;
    while (ancestor && ancestor !== container) {
      var ancestorClone = ancestor.cloneNode(false);
      ancestorClone.removeAttribute('id');
      ancestorClone.appendChild(branch);
      branch = ancestorClone;
      ancestor = ancestor.parentElement;
    }
    var cloneSizingNodes = [branch].concat(Array.prototype.slice.call(branch.querySelectorAll(sizingSelector)));
    for (var s = 0; s < cloneSizingNodes.length; s++) {
      var sizingNode = cloneSizingNodes[s];
      if (!sizingNode.matches(sizingSelector)) continue;
      sizingNode.style.maxWidth = 'none';
      sizingNode.style.maxHeight = 'none';
      sizingNode.style.overflow = 'visible';
      sizingNode.style.overflowX = 'visible';
      sizingNode.style.overflowY = 'visible';
      sizingNode.style.width = fullWidth + 'px';
    }
    isolateBlockCloneIds(branch);
    article.appendChild(branch);
    content.appendChild(article);
    frame.appendChild(content);
    presenter.appendChild(frame);
    presenter.style.setProperty('--ap-block-padding', padding + 'px');

    var originalScrollX = window.scrollX, originalScrollY = window.scrollY;
    var baseWidth = 0, baseHeight = 0;
    function fit() {
      var availableWidth = Math.max(1, window.innerWidth - padding * 2);
      var availableHeight = Math.max(1, window.innerHeight - padding * 2);
      var maxScale = 3;
      var image = blockEl.tagName === 'IMG' ? blockEl : blockEl.querySelector('img');
      if (image && image.naturalWidth) {
        var imageUrl = image.currentSrc || image.src || '';
        var isSvg = /^data:image\/svg\+xml/i.test(imageUrl) || /\.svg(?:[?#]|$)/i.test(imageUrl);
        if (!isSvg) {
          var renderedWidth = image.getBoundingClientRect().width;
          if (renderedWidth > 0) maxScale = Math.max(1, image.naturalWidth / renderedWidth);
        }
      }
      var widthScale = availableWidth / baseWidth;
      var scale = widthScale < 1
        ? Math.max(0.75, Math.min(maxScale, widthScale))
        : Math.max(1, Math.min(maxScale, widthScale, availableHeight / baseHeight));
      frame.style.width = baseWidth * scale + 'px';
      frame.style.height = baseHeight * scale + 'px';
      content.style.width = baseWidth + 'px';
      content.style.height = baseHeight + 'px';
      content.style.transform = 'scale(' + scale + ')';
      presenter.classList.remove('is-wide', 'is-tall');
      if (scale < 1) presenter.classList.add('is-wide');
      if (baseHeight * scale > availableHeight) presenter.classList.add('is-tall');
      clone.getBoundingClientRect();
    }
    function showPresenter() {
      document.body.appendChild(presenter);
      var contentRect = content.getBoundingClientRect();
      if (contentRect.width < 1 || contentRect.height < 1) throw new Error('无法显示要截图的内容');
      baseWidth = contentRect.width;
      baseHeight = contentRect.height;
      fit();
      window.addEventListener('resize', fit);
    }
    function removePresenter() {
      window.removeEventListener('resize', fit);
      if (presenter.parentNode) presenter.parentNode.removeChild(presenter);
      if (window.scrollX !== originalScrollX || window.scrollY !== originalScrollY) {
        window.scrollTo(originalScrollX, originalScrollY);
      }
    }
    return captureScreenshot({
      beforeCapture: showPresenter,
      afterFrame: removePresenter,
      candidateRect: function (viewportRect, viewportWidth, viewportHeight) {
        var rect = clone.getBoundingClientRect();
        var left = Math.max(0, rect.left - margin);
        var top = Math.max(0, rect.top - margin);
        var right = Math.min(viewportWidth, rect.right + margin);
        var bottom = Math.min(viewportHeight, rect.bottom + margin);
        if (right <= left || bottom <= top) return null;
        return {
          left: viewportRect.left + left * viewportRect.width / viewportWidth,
          top: viewportRect.top + top * viewportRect.height / viewportHeight,
          width: (right - left) * viewportRect.width / viewportWidth,
          height: (bottom - top) * viewportRect.height / viewportHeight
        };
      },
      copyToClipboard: !!opts.copyToClipboard
    });
  }

  async function captureScreenshot(opts) {
    opts = opts || {};
    var stream = null, video = null, captured = null;
    var frameWidth = 0, frameHeight = 0, surface, viewportRect;
    var scrollX = 0, scrollY = 0, viewportWidth = 0, viewportHeight = 0;
    var initialSelection = null, afterFrameCalled = false;
    var metrics;
    function readViewportMetrics() {
      viewportWidth = window.innerWidth;
      viewportHeight = window.innerHeight;
      metrics = {
        outerWidth: window.outerWidth,
        outerHeight: window.outerHeight,
        innerWidth: window.innerWidth,
        innerHeight: window.innerHeight,
        screenWidth: window.screen.width,
        screenAvailLeft: window.screen.availLeft || 0,
        screenAvailTop: window.screen.availTop || 0,
        screenX: window.screenX,
        screenY: window.screenY
      };
    }
    function afterFrame() {
      if (afterFrameCalled) return;
      afterFrameCalled = true;
      if (typeof opts.afterFrame === 'function') {
        try { opts.afterFrame({ scrollX: scrollX, scrollY: scrollY }); } catch (e) {}
      }
    }
    document.documentElement.classList.add('ap-capturing');
    try {
      scrollX = window.scrollX;
      scrollY = window.scrollY;
      readViewportMetrics();
      if (typeof opts.beforeCapture === 'function') opts.beforeCapture();
      stream = await navigator.mediaDevices.getDisplayMedia({
        video: { displaySurface: 'browser' },
        audio: false,
        preferCurrentTab: true,
        selfBrowserSurface: 'include',
        surfaceSwitching: 'exclude'
      });
      var tracks = stream.getVideoTracks();
      if (tracks.length && tracks[0].getSettings) {
        var settings = tracks[0].getSettings();
        surface = settings && settings.displaySurface;
      }
      video = document.createElement('video');
      video.muted = true;
      video.setAttribute('playsinline', '');
      video.style.cssText = 'position:fixed;left:-10000px;top:0;width:1px;height:1px;opacity:0;pointer-events:none;';
      video.srcObject = stream;
      document.body.appendChild(video);
      await video.play();
      if (!video.videoWidth || !video.videoHeight) {
        await new Promise(function (resolve, reject) {
          var finished = false;
          var timeout = setTimeout(function () {
            finished = true;
            reject(new Error('无法读取共享画面'));
          }, 5000);
          function checkSize() {
            if (finished) return;
            if (video.videoWidth && video.videoHeight) {
              finished = true;
              clearTimeout(timeout);
              resolve();
            } else requestAnimationFrame(checkSize);
          }
          checkSize();
        });
      }
      await new Promise(function (resolve) { requestAnimationFrame(function () { requestAnimationFrame(resolve); }); });
      await new Promise(function (resolve) { setTimeout(resolve, 300); });
      frameWidth = video.videoWidth;
      frameHeight = video.videoHeight;
      readViewportMetrics();
      viewportRect = viewportRectFor(surface, frameWidth, frameHeight, metrics);
      captured = document.createElement('canvas');
      captured.width = frameWidth;
      captured.height = frameHeight;
      var context = captured.getContext('2d');
      if (!context) throw new Error('无法处理共享画面');
      context.drawImage(video, 0, 0, captured.width, captured.height);
      initialSelection = viewportRect;
      if (typeof opts.candidateRect === 'function') {
        var candidate = opts.candidateRect(viewportRect, viewportWidth, viewportHeight, frameWidth, frameHeight);
        if (candidate && candidate.width > 0 && candidate.height > 0) initialSelection = candidate;
      }
      afterFrame();
    } catch (err) {
      if (err && (err.name === 'NotAllowedError' || err.name === 'AbortError')) return null;
      throw err;
    } finally {
      afterFrame();
      try {
        if (stream) {
          var tracks = stream.getTracks();
          for (var i = 0; i < tracks.length; i++) {
            try { tracks[i].stop(); } catch (e) {}
          }
        }
        if (video) {
          try { video.pause(); } catch (e) {}
          video.srcObject = null;
          if (video.parentNode) video.parentNode.removeChild(video);
        }
      } finally {
        document.documentElement.classList.remove('ap-capturing');
      }
    }
    if (!captured) return null;
    var crop = await openCropOverlay(captured, {
      initialSelection: initialSelection || viewportRect,
      copyToClipboard: !!opts.copyToClipboard
    });
    if (!crop) return null;
    var shot = {
      blob: crop.blob,
      rect: crop.rect,
      action: crop.action,
      viewportRect: viewportRect,
      frameWidth: frameWidth,
      frameHeight: frameHeight,
      surface: surface,
      scrollX: scrollX,
      scrollY: scrollY,
      viewportWidth: viewportWidth,
      viewportHeight: viewportHeight
    };
    if (Object.prototype.hasOwnProperty.call(crop, 'copied')) shot.copied = crop.copied;
    return shot;
  }

  function shareScreenshot(blob) {
    var bar = document.querySelector('.post-actions[data-url]');
    var url = (bar && bar.getAttribute('data-url')) || location.href.split('#')[0];
    var titleBar = document.querySelector('.post-actions[data-title]');
    var title = (titleBar && titleBar.getAttribute('data-title')) || document.title;
    var filename = 'screenshot-' + Date.now() + '.png';
    window.BlogShare.showImage(function () {
      return window.BlogPassageCard.renderScreenshot({ image: blob, title: title, url: url });
    }, filename);
  }

  function writeImageToClipboard(blobPromise) {
    if (!(navigator.clipboard && navigator.clipboard.write && window.ClipboardItem && window.isSecureContext)) {
      return Promise.resolve(false);
    }
    function write(item) {
      try {
        return Promise.resolve(navigator.clipboard.write([item])).then(function () { return true; }, function () { return false; });
      } catch (e) {
        return Promise.resolve(false);
      }
    }
    var item;
    try {
      item = new window.ClipboardItem({ 'image/png': blobPromise });
    } catch (e) {
      return blobPromise.then(function (blob) {
        if (!blob) return false;
        try {
          return write(new window.ClipboardItem({ 'image/png': blob }));
        } catch (err) {
          return false;
        }
      }, function () {
        return false;
      }).then(null, function () {
        return false;
      });
    }
    return write(item);
  }

  function handleCopiedScreenshot(ta, host, shot, opts) {
    opts = opts || {};
    host = host || (ta && ta.closest ? ta.closest('.ap-editor') : null);
    if (!ta || !host || !shot || !shot.copied) return Promise.resolve(false);
    return Promise.resolve(shot.copied).catch(function () {
      return false;
    }).then(function (copied) {
      if (!ta.isConnected || !host.isConnected) return false;
      if (copied) {
        ta.focus();
        ta.setSelectionRange(ta.value.length, ta.value.length);
        var shortcut = /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent) ? '⌘V' : 'Ctrl+V';
        var hint = '截图已复制到剪贴板，按 ' + shortcut + ' 粘贴到评论框后才会上传';
        setStatus(host, hint);
        if (opts.toast) {
          var toastText = typeof opts.toast === 'function' ? opts.toast(true, hint) : hint;
          if (toastText) showToast(toastText);
        }
        return true;
      }
      setStatus(host, '无法写入剪贴板', 'error');
      var fallback = document.createElement('button');
      fallback.type = 'button';
      fallback.className = 'ap-fallback';
      fallback.textContent = '插入截图';
      fallback.addEventListener('click', function () {
        if (fallback.parentNode) fallback.parentNode.removeChild(fallback);
        if (!ta.isConnected || typeof ta._apUploadImages !== 'function') return;
        ta.focus();
        ta.setSelectionRange(ta.value.length, ta.value.length);
        ta._apUploadImages([new File([shot.blob], 'screenshot.png', { type: 'image/png' })]);
      });
      var status = host.querySelector('.ap-status');
      if (status) {
        status.appendChild(document.createTextNode(' '));
        status.appendChild(fallback);
      }
      if (opts.toast) {
        var errorToast = typeof opts.toast === 'function' ? opts.toast(false, '无法写入剪贴板') : '';
        if (errorToast) showToast(errorToast);
      }
      return false;
    });
  }

  function openCropOverlay(canvas, options) {
    return new Promise(function (resolve, reject) {
      var overlay = document.createElement('div');
      overlay.className = 'ap-crop-overlay';
      overlay.setAttribute('role', 'dialog');
      overlay.setAttribute('aria-modal', 'true');
      overlay.setAttribute('aria-label', '裁剪截图');
      overlay.tabIndex = -1;

      var stage = document.createElement('div');
      stage.className = 'ap-crop-stage';
      canvas.classList.add('ap-crop-image');
      canvas.setAttribute('aria-label', '待裁剪截图');
      canvas.draggable = false;
      var markupCanvas = document.createElement('canvas');
      markupCanvas.className = 'ap-crop-markup';
      markupCanvas.width = canvas.width;
      markupCanvas.height = canvas.height;

      var selectionBox = document.createElement('div');
      selectionBox.className = 'ap-crop-selection';
      var sizeLabel = document.createElement('span');
      sizeLabel.className = 'ap-crop-size';
      selectionBox.appendChild(sizeLabel);
      var handleNames = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'];
      handleNames.forEach(function (name) {
        var handle = document.createElement('span');
        handle.className = 'ap-crop-handle';
        handle.setAttribute('data-handle', name);
        handle.setAttribute('aria-hidden', 'true');
        selectionBox.appendChild(handle);
      });
      stage.appendChild(canvas);
      stage.appendChild(markupCanvas);
      stage.appendChild(selectionBox);

      var toolbar = document.createElement('div');
      toolbar.className = 'ap-crop-toolbar';

      var toolButtons = [];
      function addToolButton(tool, icon, title) {
        var button = document.createElement('button');
        button.type = 'button';
        button.className = 'ap-crop-tool';
        button.setAttribute('data-tool', tool);
        button.setAttribute('title', title);
        button.setAttribute('aria-label', title);
        button.setAttribute('aria-pressed', 'false');
        button.innerHTML = '<i class="' + icon + '" aria-hidden="true"></i>';
        toolbar.appendChild(button);
        toolButtons.push(button);
        return button;
      }
      var rectButton = addToolButton('rect', 'fa fa-regular fa-square', '框');
      var arrowButton = addToolButton('arrow', 'fa fa-arrow-right', '箭头');
      var textButton = addToolButton('text', 'fa fa-font', '文字');
      var undoButton = document.createElement('button');
      undoButton.type = 'button';
      undoButton.className = 'ap-crop-undo';
      undoButton.setAttribute('title', '撤销 (⌘/Ctrl+Z)');
      undoButton.setAttribute('aria-label', '撤销 (⌘/Ctrl+Z)');
      undoButton.innerHTML = '<i class="fa fa-arrow-rotate-left" aria-hidden="true"></i>';

      var separator = document.createElement('span');
      separator.className = 'ap-crop-separator';
      separator.setAttribute('aria-hidden', 'true');
      var shareButton = null;
      if (window.BlogShare && window.BlogPassageCard) {
        shareButton = document.createElement('button');
        shareButton.type = 'button';
        shareButton.className = 'ap-crop-share';
        shareButton.setAttribute('title', '分享截图');
        shareButton.setAttribute('aria-label', '分享截图');
        shareButton.innerHTML = '<i class="fa fa-share-nodes" aria-hidden="true"></i>';
      }
      var cancelButton = document.createElement('button');
      cancelButton.type = 'button';
      cancelButton.className = 'ap-crop-cancel';
      cancelButton.setAttribute('title', '取消');
      cancelButton.setAttribute('aria-label', '取消');
      cancelButton.innerHTML = '<i class="fa fa-xmark" aria-hidden="true"></i>';
      var completeButton = document.createElement('button');
      completeButton.type = 'button';
      completeButton.className = 'ap-crop-complete';
      var completeTitle = options && options.copyToClipboard ? '完成（复制到剪贴板）' : '完成';
      completeButton.setAttribute('title', completeTitle);
      completeButton.setAttribute('aria-label', completeTitle);
      completeButton.innerHTML = '<i class="fa fa-check" aria-hidden="true"></i>';
      completeButton.disabled = true;
      toolbar.appendChild(undoButton);
      toolbar.appendChild(separator);
      if (shareButton) toolbar.appendChild(shareButton);
      toolbar.appendChild(cancelButton);
      toolbar.appendChild(completeButton);
      overlay.appendChild(stage);
      overlay.appendChild(toolbar);
      document.body.appendChild(overlay);

      var selection = null, candidate = null, drag = null, draftShape = null, activeTool = null;
      var shapes = [], textInput = null, closed = false, exporting = false;
      var listeners = [];
      function listen(target, type, handler, capture) {
        target.addEventListener(type, handler, !!capture);
        listeners.push({ target: target, type: type, handler: handler, capture: !!capture });
      }
      function removeListeners() {
        for (var i = 0; i < listeners.length; i++) {
          var listener = listeners[i];
          listener.target.removeEventListener(listener.type, listener.handler, listener.capture);
        }
        listeners = [];
      }
      function displayScale() {
        var rect = canvas.getBoundingClientRect();
        return rect.width ? canvas.width / rect.width : 1;
      }
      function hasValidSelection(rect) {
        return !!rect && rect.width >= 8 && rect.height >= 8;
      }
      function pointFromEvent(event) {
        var imageRect = canvas.getBoundingClientRect();
        return {
          x: Math.max(0, Math.min(imageRect.width, event.clientX - imageRect.left)),
          y: Math.max(0, Math.min(imageRect.height, event.clientY - imageRect.top))
        };
      }
      function limit(value, min, max) { return Math.max(min, Math.min(max, value)); }
      function containsPoint(rect, point) {
        return !!rect && point.x >= rect.x && point.x <= rect.x + rect.width &&
          point.y >= rect.y && point.y <= rect.y + rect.height;
      }
      function resizedSelection(original, point, currentDrag) {
        var dx = point.x - currentDrag.start.x, dy = point.y - currentDrag.start.y;
        var left = original.x, top = original.y;
        var right = original.x + original.width, bottom = original.y + original.height;
        var handle = currentDrag.handle;
        if (handle.indexOf('w') >= 0) left = limit(original.x + dx, 0, right - 8);
        if (handle.indexOf('e') >= 0) right = limit(original.x + original.width + dx, left + 8, canvas.getBoundingClientRect().width);
        if (handle.indexOf('n') >= 0) top = limit(original.y + dy, 0, bottom - 8);
        if (handle.indexOf('s') >= 0) bottom = limit(original.y + original.height + dy, top + 8, canvas.getBoundingClientRect().height);
        return { x: left, y: top, width: right - left, height: bottom - top };
      }
      function setSelectionBox(rect) {
        selectionBox.style.display = 'block';
        selectionBox.style.left = rect.x + 'px';
        selectionBox.style.top = rect.y + 'px';
        selectionBox.style.width = rect.width + 'px';
        selectionBox.style.height = rect.height + 'px';
        var scale = displayScale();
        sizeLabel.textContent = Math.round(rect.width * scale) + ' × ' + Math.round(rect.height * scale);
        sizeLabel.style.top = rect.y >= 30 ? '-25px' : '4px';
      }
      function renderSelection() {
        var validSelection = hasValidSelection(selection);
        var validCandidate = !validSelection && hasValidSelection(candidate);
        var rect = validSelection ? selection : (validCandidate ? candidate : null);
        overlay.setAttribute('data-state', validSelection ? 'selected' : (validCandidate ? 'candidate' : 'empty'));
        if (rect) {
          setSelectionBox(rect);
          selectionBox.classList.toggle('is-candidate', validCandidate);
          selectionBox.style.pointerEvents = activeTool ? 'none' : 'auto';
          if (validSelection && !(drag && drag.kind === 'selection')) {
            toolbar.style.display = 'flex';
            updateToolbarPlacement();
          } else toolbar.style.display = 'none';
        } else {
          selectionBox.style.display = 'none';
          selectionBox.classList.remove('is-candidate');
          toolbar.style.display = 'none';
        }
      }
      function updateToolbarPlacement() {
        if (!hasValidSelection(selection) || !toolbar.isConnected) return;
        var rect = selectionBox.getBoundingClientRect();
        var width = toolbar.offsetWidth, height = toolbar.offsetHeight;
        var left = rect.right - width, top;
        if (rect.bottom + 8 + height <= window.innerHeight) top = rect.bottom + 8;
        else if (rect.top >= height + 8) top = rect.top - height - 8;
        else {
          top = rect.bottom - height - 8;
          left -= 8;
        }
        left = Math.max(8, Math.min(window.innerWidth - width - 8, left));
        top = Math.max(8, Math.min(window.innerHeight - height - 8, top));
        toolbar.style.left = left + 'px';
        toolbar.style.top = top + 'px';
      }
      function updateToolbarState() {
        var valid = hasValidSelection(selection);
        for (var i = 0; i < toolButtons.length; i++) {
          toolButtons[i].disabled = !valid || exporting;
          toolButtons[i].classList.toggle('is-active', toolButtons[i].getAttribute('data-tool') === activeTool);
          toolButtons[i].setAttribute('aria-pressed', toolButtons[i].getAttribute('data-tool') === activeTool ? 'true' : 'false');
        }
        undoButton.disabled = !valid || !shapes.length || exporting;
        cancelButton.disabled = exporting;
        completeButton.disabled = !valid || exporting;
        if (shareButton) shareButton.disabled = !valid || exporting;
      }
      function updateActiveTool() {
        overlay.setAttribute('data-tool', activeTool || '');
        updateToolbarState();
        renderSelection();
      }
      function naturalShapeFromDrag(currentDrag) {
        var scale = displayScale();
        return {
          type: currentDrag.kind,
          x1: currentDrag.start.x * scale,
          y1: currentDrag.start.y * scale,
          x2: currentDrag.current.x * scale,
          y2: currentDrag.current.y * scale
        };
      }
      function drawShape(context, shape, scale) {
        if (shape.type === 'rect') {
          var left = Math.min(shape.x1, shape.x2);
          var top = Math.min(shape.y1, shape.y2);
          context.strokeRect(left, top, Math.abs(shape.x2 - shape.x1), Math.abs(shape.y2 - shape.y1));
        } else if (shape.type === 'arrow') {
          var dx = shape.x2 - shape.x1, dy = shape.y2 - shape.y1;
          var length = Math.sqrt(dx * dx + dy * dy);
          if (!length) return;
          var angle = Math.atan2(dy, dx);
          var headLength = Math.max(14 * scale, context.lineWidth * 4);
          var shaftLength = Math.max(0, length - headLength);
          var unitX = dx / length, unitY = dy / length;
          var baseX = shape.x1 + unitX * shaftLength;
          var baseY = shape.y1 + unitY * shaftLength;
          var halfAngle = 28 * Math.PI / 180;
          var leftX = shape.x2 - headLength * Math.cos(angle - halfAngle);
          var leftY = shape.y2 - headLength * Math.sin(angle - halfAngle);
          var rightX = shape.x2 - headLength * Math.cos(angle + halfAngle);
          var rightY = shape.y2 - headLength * Math.sin(angle + halfAngle);
          context.beginPath();
          context.moveTo(shape.x1, shape.y1);
          context.lineTo(baseX, baseY);
          context.stroke();
          context.beginPath();
          context.moveTo(shape.x2, shape.y2);
          context.lineTo(leftX, leftY);
          context.lineTo(rightX, rightY);
          context.closePath();
          context.fill();
        } else if (shape.type === 'text') {
          context.font = 'bold ' + (18 * scale) + 'px -apple-system, "PingFang SC", "Microsoft YaHei", sans-serif';
          context.textBaseline = 'top';
          context.fillText(shape.text, shape.x, shape.y);
        }
      }
      function redrawMarkup() {
        var context = markupCanvas.getContext('2d');
        if (!context) return;
        var scale = displayScale();
        context.clearRect(0, 0, markupCanvas.width, markupCanvas.height);
        context.strokeStyle = '#f5222d';
        context.fillStyle = '#f5222d';
        context.lineWidth = 3 * scale;
        context.lineCap = 'round';
        context.lineJoin = 'round';
        for (var i = 0; i < shapes.length; i++) drawShape(context, shapes[i], scale);
        if (draftShape) drawShape(context, draftShape, scale);
      }
      function removeTextInput(input) {
        if (!input || textInput !== input) return;
        textInput = null;
        input.removeEventListener('keydown', input._apCropKeydown);
        input.removeEventListener('blur', input._apCropBlur);
        if (input.parentNode) input.parentNode.removeChild(input);
      }
      function commitTextInput() {
        if (!textInput) return;
        var input = textInput;
        var text = input.value.trim();
        if (text) {
          shapes.push({
            type: 'text',
            x: input._apCropX,
            y: input._apCropY,
            text: text
          });
        }
        removeTextInput(input);
        if (text) {
          redrawMarkup();
          updateToolbarState();
        }
      }
      function discardTextInput() {
        if (textInput) removeTextInput(textInput);
      }
      function addTextInput(point) {
        if (textInput) {
          if (textInput.value.trim()) commitTextInput();
          else discardTextInput();
        }
        var input = document.createElement('input');
        var scale = displayScale();
        input.type = 'text';
        input.className = 'ap-crop-text';
        input.setAttribute('aria-label', '截图文字');
        input.autocomplete = 'off';
        input.spellcheck = false;
        input.style.left = point.x + 'px';
        input.style.top = point.y + 'px';
        input._apCropX = point.x * scale;
        input._apCropY = point.y * scale;
        input._apCropKeydown = function (event) {
          if (event.key === 'Enter') {
            event.preventDefault();
            event.stopPropagation();
            commitTextInput();
          } else if (event.key === 'Escape') {
            event.preventDefault();
            event.stopPropagation();
            discardTextInput();
          }
        };
        input._apCropBlur = commitTextInput;
        input.addEventListener('keydown', input._apCropKeydown);
        input.addEventListener('blur', input._apCropBlur);
        textInput = input;
        stage.appendChild(input);
        input.focus();
      }
      function undoShape() {
        if (!shapes.length) return;
        shapes.pop();
        redrawMarkup();
        updateToolbarState();
      }
      function onPointerDown(event) {
        if (event.button !== undefined && event.button !== 0 && event.pointerType !== 'touch') return;
        if (exporting) return;
        if (textInput) {
          if (textInput.value.trim()) commitTextInput();
          else discardTextInput();
        }
        var point = pointFromEvent(event);
        if (candidate) {
          var candidateRect = { x: candidate.x, y: candidate.y, width: candidate.width, height: candidate.height };
          event.preventDefault();
          drag = {
            pointerId: event.pointerId,
            kind: 'candidate',
            start: point,
            current: point,
            candidate: candidateRect,
            insideCandidate: containsPoint(candidateRect, point)
          };
          try { stage.setPointerCapture(event.pointerId); } catch (e) {}
          return;
        }
        var handle = event.target.closest && event.target.closest('.ap-crop-handle');
        var kind = activeTool || (handle && selectionBox.contains(handle) ? 'resize' :
          selection && selectionBox.contains(event.target) ? 'move' : 'selection');
        event.preventDefault();
        drag = {
          pointerId: event.pointerId,
          kind: kind,
          start: point,
          current: point,
          handle: handle && handle.getAttribute('data-handle'),
          original: selection && { x: selection.x, y: selection.y, width: selection.width, height: selection.height }
        };
        if (kind === 'selection') {
          selection = { x: point.x, y: point.y, width: 0, height: 0 };
          renderSelection();
        }
        else if (kind === 'rect' || kind === 'arrow') {
          draftShape = naturalShapeFromDrag(drag);
          redrawMarkup();
        }
        try { stage.setPointerCapture(event.pointerId); } catch (e) {}
      }
      function onPointerMove(event) {
        if (!drag || event.pointerId !== drag.pointerId) return;
        drag.current = pointFromEvent(event);
        if (drag.kind === 'candidate') {
          var candidateDx = drag.current.x - drag.start.x, candidateDy = drag.current.y - drag.start.y;
          if (Math.sqrt(candidateDx * candidateDx + candidateDy * candidateDy) < 4) return;
          drag.candidateOriginal = drag.candidate;
          candidate = null;
          drag.kind = 'selection';
          selection = { x: drag.start.x, y: drag.start.y, width: 0, height: 0 };
        }
        if (drag.kind === 'selection') {
          selection = {
            x: Math.min(drag.start.x, drag.current.x),
            y: Math.min(drag.start.y, drag.current.y),
            width: Math.abs(drag.current.x - drag.start.x),
            height: Math.abs(drag.current.y - drag.start.y)
          };
          renderSelection();
        } else if (drag.kind === 'move') {
          var imageRect = canvas.getBoundingClientRect();
          selection = {
            x: limit(drag.original.x + drag.current.x - drag.start.x, 0, imageRect.width - drag.original.width),
            y: limit(drag.original.y + drag.current.y - drag.start.y, 0, imageRect.height - drag.original.height),
            width: drag.original.width,
            height: drag.original.height
          };
          renderSelection();
        } else if (drag.kind === 'resize') {
          selection = resizedSelection(drag.original, drag.current, drag);
          renderSelection();
        } else if (drag.kind === 'rect' || drag.kind === 'arrow') {
          draftShape = naturalShapeFromDrag(drag);
          redrawMarkup();
        }
      }
      function onPointerEnd(event) {
        if (!drag || event.pointerId !== drag.pointerId) return;
        drag.current = pointFromEvent(event);
        onPointerMove(event);
        var finishedDrag = drag;
        drag = null;
        if (finishedDrag.kind === 'candidate') {
          if (finishedDrag.insideCandidate && containsPoint(finishedDrag.candidate, finishedDrag.current)) {
            selection = finishedDrag.candidate;
            candidate = null;
          }
          renderSelection();
          updateToolbarState();
        } else if (finishedDrag.kind === 'selection') {
          if (!hasValidSelection(selection)) selection = null;
          renderSelection();
          updateToolbarState();
        } else if (finishedDrag.kind === 'move' || finishedDrag.kind === 'resize') {
          renderSelection();
          updateToolbarState();
        } else if (finishedDrag.kind === 'rect' || finishedDrag.kind === 'arrow') {
          var dx = Math.abs(finishedDrag.current.x - finishedDrag.start.x);
          var dy = Math.abs(finishedDrag.current.y - finishedDrag.start.y);
          draftShape = null;
          if (dx >= 6 || dy >= 6) shapes.push(naturalShapeFromDrag(finishedDrag));
          redrawMarkup();
          updateToolbarState();
        } else if (finishedDrag.kind === 'text') {
          var textDx = Math.abs(finishedDrag.current.x - finishedDrag.start.x);
          var textDy = Math.abs(finishedDrag.current.y - finishedDrag.start.y);
          if (textDx < 6 && textDy < 6) addTextInput(finishedDrag.current);
        }
      }
      function onPointerCancel(event) {
        if (!drag || event.pointerId !== drag.pointerId) return;
        var cancelledDrag = drag;
        drag = null;
        if (cancelledDrag.kind === 'selection') {
          if (cancelledDrag.candidateOriginal) {
            candidate = cancelledDrag.candidateOriginal;
            selection = null;
          } else selection = cancelledDrag.original;
          renderSelection();
        } else if (cancelledDrag.kind === 'move' || cancelledDrag.kind === 'resize') {
          selection = cancelledDrag.original;
          renderSelection();
        }
        if (cancelledDrag.kind === 'rect' || cancelledDrag.kind === 'arrow') {
          draftShape = null;
          redrawMarkup();
        }
      }
      function cleanup() {
        removeListeners();
        discardTextInput();
        canvas.classList.remove('ap-crop-image');
        if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
      }
      function close(blob, err) {
        if (closed) return;
        closed = true;
        cleanup();
        if (err) reject(err);
        else resolve(blob || null);
      }
      function exportCrop(action) {
        if (closed || exporting) return;
        if (textInput) {
          if (textInput.value.trim()) commitTextInput();
          else discardTextInput();
        }
        if (!hasValidSelection(selection)) return;
        exporting = true;
        updateToolbarState();
        var imageRect = canvas.getBoundingClientRect();
        var scale = imageRect.width ? canvas.width / imageRect.width : 1;
        var left = Math.max(0, Math.floor(selection.x * scale));
        var top = Math.max(0, Math.floor(selection.y * scale));
        var right = Math.min(canvas.width, Math.ceil((selection.x + selection.width) * scale));
        var bottom = Math.min(canvas.height, Math.ceil((selection.y + selection.height) * scale));
        var output = document.createElement('canvas');
        output.width = Math.max(1, right - left);
        output.height = Math.max(1, bottom - top);
        var context = output.getContext('2d');
        if (!context) {
          close(null, new Error('无法处理裁剪图片'));
          return;
        }
        context.drawImage(canvas, left, top, output.width, output.height, 0, 0, output.width, output.height);
        context.drawImage(markupCanvas, left, top, output.width, output.height, 0, 0, output.width, output.height);
        if (action === 'complete' && options && options.copyToClipboard) {
          var blobPromise = new Promise(function (resolve) {
            output.toBlob(resolve, 'image/png');
          });
          var copied = writeImageToClipboard(blobPromise);
          blobPromise.then(function (blob) {
            if (!blob) {
              close(null, new Error('无法导出裁剪图片'));
              return;
            }
            close({
              blob: blob,
              rect: { left: left, top: top, width: output.width, height: output.height },
              action: action,
              copied: copied
            });
          }).catch(function () {
            close(null, new Error('无法导出裁剪图片'));
          });
          return;
        }
        output.toBlob(function (blob) {
          if (!blob) close(null, new Error('无法导出裁剪图片'));
          else close({
            blob: blob,
            rect: { left: left, top: top, width: output.width, height: output.height },
            action: action
          });
        }, 'image/png');
      }
      function onCancel() { close(null); }
      function onComplete() { exportCrop('complete'); }
      function onDoubleClick(event) {
        if (candidate) {
          if (!containsPoint(candidate, pointFromEvent(event))) return;
          selection = candidate;
          candidate = null;
        } else if (!hasValidSelection(selection)) return;
        event.preventDefault();
        event.stopPropagation();
        if (textInput) {
          if (textInput.value.trim()) commitTextInput();
          else discardTextInput();
        }
        exportCrop('complete');
      }
      function onKeyDown(event) {
        if (textInput && document.activeElement === textInput) return;
        if (event.key === 'Escape') {
          event.preventDefault();
          event.stopPropagation();
          close(null);
        } else if (event.key === 'Enter') {
          if (candidate || hasValidSelection(selection)) {
            event.preventDefault();
            event.stopPropagation();
            if (candidate) {
              selection = candidate;
              candidate = null;
            }
            exportCrop('complete');
          }
        } else if ((event.metaKey || event.ctrlKey) && String(event.key).toLowerCase() === 'z') {
          event.preventDefault();
          event.stopPropagation();
          undoShape();
        }
      }
      function onToolClick(event) {
        var tool = event.currentTarget.getAttribute('data-tool');
        activeTool = activeTool === tool ? null : tool;
        updateActiveTool();
      }
      for (var i = 0; i < toolButtons.length; i++) listen(toolButtons[i], 'click', onToolClick);
      listen(undoButton, 'click', undoShape);
      listen(cancelButton, 'click', onCancel);
      if (shareButton) listen(shareButton, 'click', function () { exportCrop('share'); });
      listen(completeButton, 'click', onComplete);
      listen(stage, 'pointerdown', onPointerDown);
      listen(stage, 'pointermove', onPointerMove);
      listen(stage, 'pointerup', onPointerEnd);
      listen(stage, 'pointercancel', onPointerCancel);
      listen(stage, 'dblclick', onDoubleClick);
      listen(document, 'keydown', onKeyDown, true);
      updateActiveTool();
      overlay.focus({ preventScroll: true });
      requestAnimationFrame(function () {
        if (closed) return;
        var initial = options && options.initialSelection;
        var scale = displayScale();
        selection = null;
        candidate = initial ? {
          x: initial.left / scale,
          y: initial.top / scale,
          width: initial.width / scale,
          height: initial.height / scale
        } : null;
        if (!hasValidSelection(candidate)) candidate = null;
        renderSelection();
        updateToolbarState();
      });
    });
  }

  function rangeForOffsets(offsets) {
    var segs = segmentsFor(offsets.start, offsets.end);
    if (!segs.length) return null;
    var range = document.createRange();
    range.setStart(segs[0].node, segs[0].from);
    range.setEnd(segs[segs.length - 1].node, segs[segs.length - 1].to);
    return range;
  }

  function rangeForContents(el) {
    if (!el) return null;
    var range = document.createRange();
    range.selectNodeContents(el);
    return range;
  }

  function screenshotAnchorRange(el) {
    if (!el || !container) return null;
    if (el.nodeType !== 1) el = el.parentElement;
    if (!el || el === container || !container.contains(el)) return null;

    var figure = el.closest('figure.post-figure');
    if (figure) {
      var figureCaption = figure.querySelector('.post-figcaption');
      var figureTitle = figureCaption && (figureCaption.querySelector('.fig-title') || figureCaption.querySelector('.fig-no'));
      return rangeForContents(figureTitle);
    }

    var mermaid = el.closest('.mermaid');
    var mermaidCaption = mermaid ? mermaid.nextElementSibling : null;
    if (!mermaid && el.closest('.post-figcaption')) {
      var possibleCaption = el.closest('.post-figcaption');
      if (possibleCaption.previousElementSibling && possibleCaption.previousElementSibling.classList.contains('mermaid')) {
        mermaidCaption = possibleCaption;
      }
    }
    if (mermaid || mermaidCaption) {
      mermaidCaption = mermaidCaption && mermaidCaption.classList.contains('post-figcaption') ? mermaidCaption : null;
      var mermaidTitle = mermaidCaption && (mermaidCaption.querySelector('.fig-title') || mermaidCaption.querySelector('.fig-no'));
      return rangeForContents(mermaidTitle);
    }

    var table = el.closest('table');
    var responsive = el.closest('.table-responsive');
    var tableCaption = el.closest('.table-caption');
    if (table || responsive || tableCaption) {
      if (!table && responsive) table = responsive.querySelector('table');
      var tableAnchor = table && table.parentElement.classList.contains('table-responsive') ? table.parentElement : table;
      if (!tableAnchor && tableCaption) {
        tableAnchor = tableCaption.previousElementSibling;
        if (tableAnchor && tableAnchor.classList.contains('table-responsive')) table = tableAnchor.querySelector('table');
        else if (tableAnchor && tableAnchor.tagName === 'TABLE') table = tableAnchor;
      }
      var caption = tableCaption || (tableAnchor && tableAnchor.nextElementSibling);
      if (caption && !caption.classList.contains('table-caption')) caption = null;
      var target = caption && caption.querySelector('.fig-title');
      if (!target && table) target = table.querySelector('thead > tr') || table;
      return rangeForContents(target);
    }

    var codeBlock = el.closest('.code-block');
    if (codeBlock) {
      var codeHeader = codeBlock.querySelector('.code-header');
      var codeTitle = codeHeader && (codeHeader.querySelector('.fig-title') || codeHeader.querySelector('.fig-no'));
      return rangeForContents(codeTitle);
    }

    var heading = el.closest('h1, h2, h3, h4, h5, h6');
    if (heading) return rangeForContents(heading);

    var block = el.closest('p, li, blockquote, dd, dt');
    if (!block) return null;
    buildIndex();
    var blockRange = rangeForContents(block);
    var offsets = blockRange && rangeToOffsets(blockRange);
    if (!offsets) return null;
    var text = index.text.slice(offsets.start, offsets.end);
    var punctuation = /[。！？!?；;]|\. /.exec(text);
    var length = Math.min(punctuation ? punctuation.index + 1 : text.length, 60);
    if (!length) return null;
    return rangeForOffsets({ start: offsets.start, end: offsets.start + length });
  }

  function screenshotAnchorElement(shot) {
    if (!shot || !shot.viewportRect ||
        !shot.frameWidth || !shot.frameHeight || !shot.viewportWidth || !shot.viewportHeight ||
        !shot.viewportRect.width || !shot.viewportRect.height ||
        Math.abs(shot.viewportRect.width / shot.viewportRect.height - shot.viewportWidth / shot.viewportHeight) >= 0.03) return null;

    var rect = shot.rect, viewportRect = shot.viewportRect;
    var centerX = rect.left + rect.width / 2, centerY = rect.top + rect.height / 2;
    if (centerX < viewportRect.left || centerX > viewportRect.left + viewportRect.width ||
        centerY < viewportRect.top || centerY > viewportRect.top + viewportRect.height) return null;
    var x = (centerX - viewportRect.left) * shot.viewportWidth / viewportRect.width + shot.scrollX - window.scrollX;
    var y = (centerY - viewportRect.top) * shot.viewportHeight / viewportRect.height + shot.scrollY - window.scrollY;
    var cropTop = (rect.top - viewportRect.top) * shot.viewportHeight / viewportRect.height + shot.scrollY - window.scrollY;
    var cropBottom = (rect.top + rect.height - viewportRect.top) * shot.viewportHeight / viewportRect.height + shot.scrollY - window.scrollY;
    function candidateAt(pointY) {
      var candidate = document.elementFromPoint(x, pointY);
      return candidate && candidate !== container && container.contains(candidate) && screenshotAnchorRange(candidate) ? candidate : null;
    }
    var candidate = candidateAt(y);
    if (candidate) return candidate;
    for (var up = 24; y - up >= cropTop; up += 24) {
      candidate = candidateAt(y - up);
      if (candidate) return candidate;
    }
    for (var down = 24; y + down <= cropBottom; down += 24) {
      candidate = candidateAt(y + down);
      if (candidate) return candidate;
    }
    return null;
  }

  function startScreenshotComment(targetEl, blockEl) {
    if (!supportsScreenshotCapture() || document.querySelector('.ap-crop-overlay') ||
        document.documentElement.classList.contains('ap-capturing')) return;
    if (!getSession()) {
      showToast('登录 GitHub 后才能截图评论');
      return;
    }
    hideToolbar();
    if (window.getSelection) window.getSelection().removeAllRanges();
    var shotOpts = { copyToClipboard: true };
    var shotPromise = blockEl ? captureBlock(blockEl, shotOpts) : captureScreenshot(shotOpts);
    shotPromise.then(function (shot) {
      if (!shot) return;
      if (shot.action === 'share') {
        shareScreenshot(shot.blob);
        return;
      }
      var anchorEl = targetEl || screenshotAnchorElement(shot);
      var range = anchorEl && screenshotAnchorRange(anchorEl);
      var offsets = range && selectionOffsets(range);
      if (offsets) {
        var sel = selectorFromOffsets(offsets);
        openComposer(sel, offsets, range.endContainer, '', '');
        var panelTextarea = panel && panel.querySelector('.ap-text');
        if (panelTextarea) {
          handleCopiedScreenshot(panelTextarea, panelTextarea.closest('.ap-editor'), shot, { toast: true });
          var panelRect = panel.getBoundingClientRect();
          if (panelRect.top < 0 || panelRect.bottom > window.innerHeight) panel.scrollIntoView({ block: 'center' });
        }
        return;
      }
      var bottomTextarea = document.querySelector('.annotation-comments .ac-editor .ap-text');
      if (bottomTextarea) {
        handleCopiedScreenshot(bottomTextarea, bottomTextarea.closest('.ap-editor'), shot, {
          toast: function (copied) {
            return copied
              ? '截图已复制；没有对应到文章里的位置，请在文末评论框粘贴'
              : '无法写入剪贴板；没有对应到文章里的位置，请在文末评论框点“插入截图”';
          }
        });
        bottomTextarea.scrollIntoView({ block: 'center' });
      }
    }).catch(function (err) {
      if (err && (err.name === 'AbortError' || err.name === 'NotAllowedError')) return;
      showToast('截图失败：' + (err.message || String(err)));
    });
  }

  function transferImages(transfer) {
    if (!transfer) return [];
    var images = [], items = transfer.items || [];
    for (var i = 0; i < items.length; i++) {
      var item = items[i];
      if (item.kind !== 'file' || !/^image\//i.test(item.type || '')) continue;
      var file = item.getAsFile && item.getAsFile();
      if (file && images.indexOf(file) < 0) images.push(file);
    }
    if (!images.length) {
      var files = transfer.files || [];
      for (var f = 0; f < files.length; f++) {
        if (/^image\//i.test(files[f].type || '') && images.indexOf(files[f]) < 0) images.push(files[f]);
      }
    }
    return images;
  }

  function compressCommentImage(file) {
    if (file.type === 'image/gif') {
      if (file.size > 3 * 1024 * 1024) return Promise.reject(new Error('图片太大（压缩后仍超过 3 MB）'));
      return Promise.resolve(file);
    }
    return new Promise(function (resolve, reject) {
      var url = URL.createObjectURL(file), img = new Image();
      img.onload = function () {
        var width = img.naturalWidth, height = img.naturalHeight;
        if (!width || !height) {
          URL.revokeObjectURL(url);
          reject(new Error('无法读取图片'));
          return;
        }
        var scale = Math.min(1, 1600 / Math.max(width, height));
        var canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(width * scale));
        canvas.height = Math.max(1, Math.round(height * scale));
        var context = canvas.getContext('2d');
        if (!context) {
          URL.revokeObjectURL(url);
          reject(new Error('无法处理图片'));
          return;
        }
        context.fillStyle = '#fff';
        context.fillRect(0, 0, canvas.width, canvas.height);
        context.drawImage(img, 0, 0, canvas.width, canvas.height);
        URL.revokeObjectURL(url);
        function finish(blob) {
          if (!blob) {
            reject(new Error('无法处理图片'));
            return;
          }
          var originalType = String(file.type || '').toLowerCase();
          if (Math.max(width, height) <= 1600 && file.size < blob.size &&
              ['image/webp', 'image/jpeg', 'image/png'].indexOf(originalType) >= 0) blob = file;
          if (blob.size > 3 * 1024 * 1024) {
            reject(new Error('图片太大（压缩后仍超过 3 MB）'));
            return;
          }
          resolve(blob);
        }
        canvas.toBlob(function (webp) {
          if (webp && webp.type === 'image/webp') finish(webp);
          else canvas.toBlob(finish, 'image/jpeg', 0.88);
        }, 'image/webp', 0.85);
      };
      img.onerror = function () {
        URL.revokeObjectURL(url);
        reject(new Error('无法读取图片'));
      };
      img.src = url;
    });
  }

  // Minimal Markdown helpers for the toolbar: wrap the selection or prefix the
  // selected lines; setRangeText keeps the browser's undo stack intact.
  function applyFormat(ta, kind) {
    var v = ta.value, s = ta.selectionStart, e = ta.selectionEnd, sel = v.slice(s, e);
    function wrap(left, right, placeholder) {
      var inner = sel || placeholder;
      ta.setRangeText(left + inner + right, s, e, 'select');
      if (!sel) ta.setSelectionRange(s + left.length, s + left.length + inner.length);
      else ta.setSelectionRange(s + left.length, s + left.length + inner.length);
    }
    function prefixLines(prefixFn) {
      var ls = v.lastIndexOf('\n', s - 1) + 1;
      var le = v.indexOf('\n', e); if (le === -1) le = v.length;
      var lines = v.slice(ls, le).split('\n');
      var out = lines.map(function (l, i) { return prefixFn(i) + l; }).join('\n');
      ta.setRangeText(out, ls, le, 'select');
      ta.setSelectionRange(ls, ls + out.length);
    }
    function block(open, close, placeholder) {
      var inner = sel || placeholder;
      var before = (s > 0 && v.charAt(s - 1) !== '\n') ? '\n' : '';
      var after = (e < v.length && v.charAt(e) !== '\n') ? '\n' : '';
      var text = before + open + '\n' + inner + '\n' + close + after;
      ta.setRangeText(text, s, e, 'select');
      var innerStart = s + before.length + open.length + 1;
      ta.setSelectionRange(innerStart, innerStart + inner.length);
    }
    switch (kind) {
      case 'bold': wrap('**', '**', '加粗文字'); break;
      case 'italic': wrap('*', '*', '斜体文字'); break;
      case 'code': wrap('`', '`', 'code'); break;
      case 'heading': prefixLines(function () { return '### '; }); break;
      case 'quote': prefixLines(function () { return '> '; }); break;
      case 'ul': prefixLines(function () { return '- '; }); break;
      case 'ol': prefixLines(function (i) { return (i + 1) + '. '; }); break;
      case 'codeblock': block('```', '```', '代码'); break;
      case 'table':
        var before = (s > 0 && v.charAt(s - 1) !== '\n') ? '\n' : '';
        var after = (e < v.length && v.charAt(e) !== '\n') ? '\n' : '';
        var table = '| 标题 | 标题 | 标题 |\n| --- | --- | --- |\n| 内容 | 内容 | 内容 |\n| 内容 | 内容 | 内容 |';
        ta.setRangeText(before + table + after, s, e, 'select');
        ta.setSelectionRange(s + before.length + 2, s + before.length + 4);
        break;
      case 'link':
        if (/^https?:\/\//.test(sel)) { ta.setRangeText('[链接文字](' + sel + ')', s, e, 'select'); ta.setSelectionRange(s + 1, s + 5); }
        else { var t = sel || '链接文字'; ta.setRangeText('[' + t + '](url)', s, e, 'select'); ta.setSelectionRange(s + t.length + 3, s + t.length + 6); }
        break;
      case 'image': ta.setRangeText('![' + (sel || '图片说明') + '](url)', s, e, 'select'); ta.setSelectionRange(s + (sel || '图片说明').length + 4, s + (sel || '图片说明').length + 7); break;
    }
    ta.focus();
    ta.dispatchEvent(new Event('input'));
  }

  function switchTab(host, tab, fullBody) {
    var tabs = host.querySelectorAll('.ap-tabs button');
    for (var i = 0; i < tabs.length; i++) tabs[i].classList.toggle('is-active', tabs[i].getAttribute('data-tab') === tab);
    var ta = host.querySelector('.ap-text'), pv = host.querySelector('.ap-preview');
    ta.style.display = tab === 'write' ? '' : 'none';
    pv.style.display = tab === 'preview' ? 'block' : 'none';
    // the quote header is part of the post even before anything is written
    if (tab === 'preview') renderPreview(pv, fullBody ? fullBody(ta.value) : ta.value, !ta.value.trim());
  }

  function renderPreview(pv, md, noteEmpty) {
    if (!md.trim()) { pv.innerHTML = '<em class="ap-muted">没有内容可预览</em>'; return; }
    pv.innerHTML = '<em class="ap-muted">渲染中…</em>';
    var headers = { 'Content-Type': 'application/json', Accept: 'application/vnd.github+json' };
    if (token) headers.Authorization = 'Bearer ' + token;
    fetch(GITHUB_MARKDOWN, { method: 'POST', headers: headers, body: JSON.stringify({ text: md, mode: 'gfm' }) })
      .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.text(); })
      .then(function (html) {
        pv.innerHTML = ''; pv.appendChild(sanitizeHtml(html)); InlinePopover.renderMathIfPresent(pv);
        if (noteEmpty) pv.insertAdjacentHTML('beforeend', '<p><em class="ap-muted">还没写评论内容——上面的引用是自动带上的原文，发表时会一起发出</em></p>');
      })
      .catch(function () { pv.innerHTML = '<em class="ap-muted">预览暂不可用（GitHub API 无法访问）</em>'; });
  }

  function setStatus(host, msg, kind) {
    var st = host.querySelector('.ap-status');
    st.textContent = msg || '';
    st.className = 'ap-status' + (kind ? ' is-' + kind : '');
    st.style.display = msg ? 'block' : 'none';
  }

  function setBusy(host, busy) {
    var btns = host.querySelectorAll('button, textarea, input');
    for (var i = 0; i < btns.length; i++) btns[i].disabled = !!busy;
    host.classList.toggle('is-busy', !!busy);
    var ta = host.querySelector('.ap-text'), submit = host.querySelector('.ap-submit');
    if (!busy && ta && submit) submit.disabled = !ta.value.trim() || Number(host._apPendingUploads || 0) > 0;
  }

  function refreshAuthUI(host) {
    var userEl = host.querySelector('.ap-user');
    var loginBtn = host.querySelector('.ap-login');
    var submitBtn = host.querySelector('.ap-submit');
    if (!getSession()) {
      userEl.innerHTML = '<span class="ap-muted">登录 GitHub 后即可发表</span>';
      loginBtn.style.display = '';
      submitBtn.style.display = 'none';
      return;
    }
    loginBtn.style.display = 'none';
    submitBtn.style.display = '';
    if (viewer) { renderViewer(userEl, viewer); return; }
    userEl.innerHTML = '<span class="ap-muted">正在连接 GitHub…</span>';
    fetchViewer();
  }

  var VIEWER_QUERY = '{ viewer { login avatarUrl url } }';
  var viewerPending = null;
  // One request at a time; onViewerKnown() updates every editor on success.
  function fetchViewer() {
    if (viewer || viewerPending) return;
    viewerPending = graphql(VIEWER_QUERY).then(function (data) {
      viewerPending = null;
      viewer = data.viewer;
      onViewerKnown();
    }).catch(function (err) {
      viewerPending = null;
      viewer = null;
      if (cfg && cfg.section) {
        var hosts = document.querySelectorAll('.ap-editor');
        for (var i = 0; i < hosts.length; i++) {
          var u = hosts[i].querySelector('.ap-user');
          if (u) u.innerHTML = '<span class="ap-muted">' + escapeHtml(err.message) + '</span>';
          if (!getSession()) refreshAuthUI(hosts[i]);
        }
      }
      dispatchViewer();
    });
  }

  // The reader's identity just became known: show it in every editor and
  // re-render the lists so their own comments get 编辑 / 删除.
  function onViewerKnown() {
    if (!cfg || !cfg.section) {
      dispatchViewer();
      return;
    }
    var hosts = document.querySelectorAll('.ap-editor');
    for (var i = 0; i < hosts.length; i++) if (hosts[i].querySelector('.ap-user')) refreshAuthUI(hosts[i]);
    if (!refilterComments()) {
      if (commentsHost && !commentsHost.querySelector('.ac-reply-editor, .ap-inline-editor')) renderCommentSection();
    }
    if (panelState && panelState.kind === 'thread' && !panel.querySelector('.ap-text').value) refreshThreadPanel();
    loadViewerReactions();
    dispatchViewer();
    updateToolbar();
  }

  function dispatchViewer() {
    try { document.dispatchEvent(new CustomEvent('blog:viewer', { detail: viewer })); } catch (e) { /* old browsers */ }
  }

  function renderViewer(userEl, v) {
    userEl.innerHTML =
      '<img src="' + escapeAttr(v.avatarUrl) + '" alt="" width="22" height="22"> ' +
      '<a href="' + escapeAttr(v.url) + '" target="_blank" rel="noopener noreferrer">' + escapeHtml(v.login) + '</a>' +
      '<button type="button" class="ap-logout" title="退出 GitHub 登录">退出</button>';
    userEl.querySelector('.ap-logout').addEventListener('click', logout);
  }

  // --------------------------------------------------------- new annotation

  // The underlined passage (commented or reaction-only) that contains the
  // selection — smallest wins — or null. A selection inside it joins it instead
  // of starting a second thread / counter for the same place.
  function passageContaining(offsets) {
    var best = null;
    passages().forEach(function (p) {
      var range = p.list.length ? p.list[0].range : p.reaction.range;
      if (!range || range.start > offsets.start || range.end < offsets.end) return;
      if (!best || (range.end - range.start) < best.len) best = { p: p, len: range.end - range.start };
    });
    return best ? best.p : null;
  }

  function openComposer(sel, offsets, anchorNode, draftText, mode) {
    var m = null;
    if (m && !draftText) draftText = m.template;
    var join = passageContaining(offsets);
    if (join) {
      // same passage -> one thread; the editor there defaults to a comment on the passage
      openThread(join.ids, join.marks[join.marks.length - 1]);
      var joinTa = panel && panel.querySelector('.ap-text');
      if (joinTa) {
        if (draftText) joinTa.value = draftText;
        joinTa.focus(); joinTa.dispatchEvent(new Event('input'));
      }
      return;
    }
    panelState = { kind: 'editor', selector: sel, offsets: offsets, mode: m ? mode : '' };
    ensurePanel();
    panel.className = 'annotation-panel is-editor';
    panel.innerHTML =
      '<div class="ap-head">' +
        '<i class="fa fa-quote-left"></i><span class="ap-quote" title="' + escapeAttr(sel.exact) + '">' + escapeHtml(sel.exact) + '</span>' +
        '<span class="ap-count">' + (m ? m.label : '新评论') + '</span>' +
        '<button type="button" class="ap-close" title="取消">×</button>' +
      '</div>' +
      '<div class="ap-editor"></div>';
    panel.querySelector('.ap-close').addEventListener('click', cancelComposer);
    var ta = renderEditor(panel.querySelector('.ap-editor'), {
      placeholder: m ? m.placeholder : '写下你对这段文字的评论…',
      submitLabel: m ? m.submit : '提交评论',
      initialText: draftText || '',
      issueOption: true,
      onCancel: cancelComposer,
      onChange: saveDraft,
      beforeLogin: saveDraft,
      fallbackText: function (text) { return buildCommentBody(sel, text); },
      onSubmit: function (text, extra) { return postAnnotation(sel, text, extra.issue); }
    });
    if (m) { var box = panel.querySelector('.ap-issue input'); if (box) box.checked = true; }
    mountPanel(anchorNode);
    if (window.getSelection) window.getSelection().removeAllRanges();
    ta.focus();
    if (m && ta.value === m.template) ta.setSelectionRange(m.caret, m.caret);
    saveDraft(ta.value);
  }


  function cancelComposer() { clearDraft(); closePanel(); }

  // With `withIssue` the GitHub Issue is filed first so the comment can link to
  // it (the comment is the record readers see; the issue is the author's todo).
  function postAnnotation(sel, text, withIssue) {
    var issue = null;
    return ensureDiscussion().then(function (id) {
      return (withIssue ? createIssue(sel, text) : Promise.resolve(null)).then(function (is) {
        issue = is;
        return graphql(ADD_COMMENT, { body: buildCommentBody(sel, text, issue), discussionId: id });
      });
    }).then(function (data) {
      var c = data.addDiscussionComment.comment;
      var a = parseComment(c);
      if (!a.selector) { a.selector = sel; a.noteHTML = c.bodyHTML; a.issue = issue; } // GitHub rendered it unexpectedly
      allComments.push(a);
      clearDraft();
      closePanel();
      syncViews();
      // The panel stays closed after posting: the flashing highlight + toast
      // confirm it; the thread is one click on the marker away.
      if (a.marks.length) flashMarks(a.marks);
      flashComment(a.id);
      showToast(issue ? '评论已发表，Issue #' + issue.number + ' 已创建' : '评论已发表');
    });
  }

  // A plain comment from the section at the bottom (no passage). With an
  // issue the body starts with `<sub>[⚑ Issue #N](url)</sub>` so it can be
  // recognised (parseBodyHeader) and shown with the flag badge.
  function postComment(text, withIssue) {
    var issue = null;
    return ensureDiscussion().then(function (id) {
      return (withIssue ? createIssue(null, text) : Promise.resolve(null)).then(function (is) {
        issue = is;
        var body = (issue ? '<sub>[⚑ Issue #' + issue.number + '](' + issue.url + ')</sub>\n\n' : '') + text.trim() + '\n';
        return graphql(ADD_COMMENT, { body: body, discussionId: id });
      });
    }).then(function (data) {
      var c = parseComment(data.addDiscussionComment.comment);
      if (issue && !c.issue) c.issue = issue;
      allComments.push(c);
      syncViews();
      flashComment(c.id);
      showToast(issue ? '评论已发表，Issue #' + issue.number + ' 已创建' : '评论已发表');
    });
  }

  // `sel` is null for a plain comment.
  function createIssue(sel, text) {
    var titleEl = document.querySelector('.page-header .title, .post-heading h1, h1');
    var postTitle = (titleEl && titleEl.textContent) || document.title.split(/\s[-|]\s/)[0];
    var snippet = sel ? sel.exact : text.trim().split('\n')[0].replace(/^[#>*\-\s]+/, '');
    if (snippet.length > 40) snippet = snippet.slice(0, 40) + '…';
    var body = sel
      ? '> ' + escapeMarkdown(sel.exact) + '\n>\n> [§ 原文位置](' + threadLink(sel) + ')' + sectionNote(sel) + '\n\n' + text.trim() + '\n'
      : text.trim() + '\n\n— 来自文章评论区：' + cfg.siteUrl + cfg.path + '#comments\n';
    return ensureToken().then(function (tk) {
      return api('/issues', {
        method: 'POST',
        headers: { Authorization: 'Bearer ' + tk },
        body: { title: postTitle.trim() + '：「' + snippet + '」', body: body }
      });
    }).catch(function (err) {
      if (/501|未启用/.test(err.message)) err.issuesDisabled = true;
      err.message = '创建 Issue 失败：' + err.message;
      throw err;
    });
  }

  function postReply(a, text) {
    return graphql(ADD_COMMENT, { body: text, discussionId: discussion.id, replyToId: a.id }).then(function (data) {
      var c = data.addDiscussionComment.comment;
      a.replies = (a.replies || []).concat([{ id: c.id, url: c.url, author: c.author || GHOST, createdAt: c.createdAt, lastEditedAt: null, bodyHTML: c.bodyHTML, owner: c.authorAssociation === 'OWNER', votes: parseVotes(null) }]);
      a.replyCount = a.replies.length;
      syncViews(); // marker counts, panel, comment section
      flashComment(c.id);
      showToast('回复已发表');
    });
  }

  // Briefly tint every rendering of a comment (panel and bottom section).
  function flashComment(id) {
    var els = document.querySelectorAll('.ap-comment[data-comment-id="' + id + '"]');
    for (var i = 0; i < els.length; i++) els[i].classList.add('is-new');
  }

  // Draft survives the GitHub login round-trip.
  function draftKey() { return 'annotationDraft:' + cfg.path; }
  function saveDraft(text) {
    if (!panelState || panelState.kind !== 'editor') return;
    if (typeof text !== 'string') { var ta = panel && panel.querySelector('.ap-text'); text = ta ? ta.value : ''; }
    try { sessionStorage.setItem(draftKey(), JSON.stringify({ selector: panelState.selector, text: text, mode: panelState.mode || '' })); } catch (e) { /* ignore */ }
  }
  function clearDraft() { try { sessionStorage.removeItem(draftKey()); } catch (e) { /* ignore */ } }
  function restoreDraft() {
    var raw = null;
    try { raw = sessionStorage.getItem(draftKey()); } catch (e) { /* ignore */ }
    if (!raw) return false;
    var draft;
    try { draft = JSON.parse(raw); } catch (e) { clearDraft(); return false; }
    if (!draft || !draft.selector) { clearDraft(); return false; }
    var range = anchor(draft.selector);
    if (!range) { clearDraft(); return false; }
    var segs = segmentsFor(range.start, range.end);
    if (!segs.length) { clearDraft(); return false; }
    var last = segs[segs.length - 1].node;
    scrollIntoViewInstant(last.parentNode);
    openComposer(draft.selector, range, last, draft.text, '');
    return true;
  }

  // --------------------------------------------------------- data loading

  function api(path, opts) {
    opts = opts || {};
    var headers = opts.headers || {};
    if (opts.body) headers['Content-Type'] = 'application/json';
    return fetch(cfg.api + path, {
      method: opts.method || 'GET',
      headers: headers,
      body: opts.body ? JSON.stringify(opts.body) : undefined
    }).then(function (r) {
      return r.json().then(function (data) {
        if (!r.ok) {
          var error = new Error((data && data.error) || ('HTTP ' + r.status));
          error.status = r.status;
          throw error;
        }
        return data;
      });
    });
  }

  function loadDiscussion(fresh) {
    var qs = '?term=' + encodeURIComponent(cfg.path) + (fresh ? '&t=' + Date.now() : '');
    return api('/discussions' + qs).then(function (data) {
      var d = data && data.discussion;
      discussion = d ? { id: d.id, url: d.url, totalCommentCount: d.totalCommentCount, likes: parseVotes(d.reactions || d.reactionGroups) } : null;
      allComments = d ? parseComments(d.comments || []) : [];
      loadError = null; loaded = true;
      syncViews();
      loadIssueStates(); // 「已修正」 for notes filed with an Issue; re-syncs when it answers
      if (viewer) loadViewerReactions(); // the anonymous payload cannot know what *we* voted
      return annotations;
    }).catch(function (err) {
      console.warn('[annotations] load failed:', err.message);
      loadError = err; loaded = true;
      applyHighlights(); // reaction-only passages still get their underline
      renderCommentSection();
      return [];
    });
  }

  // `allComments` is the source of truth; `comments` (what this reader sees),
  // the article highlights and the comment section at the bottom are views of
  // it. Call after every mutation.
  function syncViews() {
    markResolved();
    comments = visibleComments();
    annotations = comments.filter(function (c) { return c.selector; });
    renderedOwner = isOwner();
    renderedArchived = showArchived();
    applyHighlights();
    renderCommentSection();
  }

  // The author's own top-level comments (划线评论 included) are working notes
  // for the AI revision loop (修订简报 / 待修订 issues), not part of the page:
  // only the author, logged in, sees them. Their replies under readers'
  // comments stay public (已修正 …). Counts, hot passages and highlights all
  // derive from `comments`, so nothing leaks a hidden note.
  function isAuthorNote(c) {
    return c.owner || !!(cfg.author && c.author && c.author.login && c.author.login.toLowerCase() === cfg.author.toLowerCase());
  }
  function showArchived() {
    if (!isOwner()) return false;
    try { return localStorage.getItem(SHOW_ARCHIVED_KEY) === '1'; } catch (e) { return false; }
  }
  function visibleComments() {
    var owner = isOwner(), archived = showArchived();
    return allComments.filter(function (c) {
      if (!owner && isAuthorNote(c)) return false;
      return !c.archived || archived;
    });
  }
  // After the viewer changed (login / logout): re-sync when that changes what is visible.
  function refilterComments() {
    var next = visibleComments(), owner = isOwner(), archived = showArchived();
    if (owner === renderedOwner && archived === renderedArchived && next.length === comments.length &&
        next.every(function (c, i) { return comments[i] && comments[i].id === c.id; })) return false;
    syncViews();
    if (panelState && panelState.kind === 'thread') refreshThreadPanel();
    return true;
  }

  function parseComments(comments) {
    var out = [];
    for (var i = 0; i < comments.length; i++) {
      var a = parseComment(comments[i]);
      if (a) out.push(a);
    }
    return out;
  }

  // Every top-level comment of the discussion becomes a record; the ones that
  // carry the quote header additionally get `selector` / `noteHTML` and are
  // highlighted in the article. Deleted-but-with-replies comments are kept as
  // placeholders (GitHub soft-deletes those) so their replies stay readable.
  function parseComment(c) {
    if (!c) return null;
    var replies = parseReplies(c.replies);
    if (c.deletedAt && !replies.length) return null;
    var rec = {
      id: c.id,
      url: c.url,
      author: c.author || GHOST,
      createdAt: c.createdAt,
      lastEditedAt: c.lastEditedAt || null,
      deleted: !!c.deletedAt,
      owner: c.authorAssociation === 'OWNER',
      votes: parseVotes(c.reactions || c.reactionGroups),
      replyCount: (c.replies && (c.replies.totalCount !== undefined ? c.replies.totalCount : c.replies.length)) || c.replyCount || 0,
      replies: replies,
      bodyHTML: c.deletedAt ? '' : (c.bodyHTML || ''),
      selector: null, noteHTML: null, issue: null, archived: !!c.isMinimized, resolved: false
    };
    if (!rec.deleted) parseBodyHeader(rec);
    return rec;
  }

  // 「已修正」: a note filed with an Issue is resolved when that Issue is closed
  // (the author's `Fixes #N` commit does it); without an Issue the author says
  // so — a 🎉 (HOORAY) on the note or a reply containing 已修正 / 已修复.
  var RESOLVED_RE = /已修正|已修复|已更正|已改正|已订正|已采纳/;
  var closedIssues = {};  // number -> true, from GitHub REST (see loadIssueStates)
  function markResolved() {
    allComments.forEach(function (c) {
      if (c.deleted) { c.resolved = false; return; }
      if (c.archived) { c.resolved = true; return; }
      if (c.issue && c.issue.number) { c.resolved = !!closedIssues[c.issue.number]; return; }
      c.resolved = (c.votes && c.votes.hooray > 0) || c.replies.some(function (r) {
        return r.owner && RESOLVED_RE.test(r.bodyHTML.replace(/<[^>]+>/g, ''));
      });
    });
  }
  // Issue state comes from GitHub's anonymous REST API (60 req/h: a post has a
  // handful of issues at most; open ones are re-checked after 10 min, closed
  // ones are trusted for a day).
  var ISSUE_CACHE = 'issueState:';
  function loadIssueStates() {
    if (!cfg.repo) return Promise.resolve();
    var now = Date.now(), want = [];
    allComments.forEach(function (c) {
      if (!c.issue || !c.issue.number) return;
      var cached = null;
      try { cached = JSON.parse(localStorage.getItem(ISSUE_CACHE + cfg.repo + '#' + c.issue.number) || 'null'); } catch (e) { /* ignore */ }
      if (cached && now - cached.t < (cached.closed ? 86400000 : 600000)) { if (cached.closed) closedIssues[c.issue.number] = true; return; }
      want.push(c.issue.number);
    });
    return Promise.all(want.map(function (n) {
      return fetch('https://api.github.com/repos/' + cfg.repo + '/issues/' + n, { headers: { Accept: 'application/vnd.github+json' } })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (d) {
          if (!d || !d.state) return;
          var closed = d.state === 'closed';
          if (closed) closedIssues[n] = true;
          try { localStorage.setItem(ISSUE_CACHE + cfg.repo + '#' + n, JSON.stringify({ closed: closed, t: now })); } catch (e) { /* ignore */ }
        }).catch(function () { /* stays unresolved */ });
    })).then(function () { if (want.length) syncViews(); });
  }

  // Fill rec.selector / noteHTML / issue from the comment's HTML (see file header).
  function parseBodyHeader(rec) {
    var doc = new DOMParser().parseFromString('<div>' + rec.bodyHTML + '</div>', 'text/html');
    var root = doc.body.firstChild;
    var first = root.firstElementChild;
    if (!first) return;
    var issueLink, issueNo;
    if (first.tagName !== 'BLOCKQUOTE') {
      // plain comment filed with 「同时提交 Issue」: leading <p><sub>[⚑ Issue #N](…)</sub></p>
      if (first.tagName === 'P' && first.querySelector('sub a[href*="/issues/"]') && first.textContent.trim().length < 40) {
        issueLink = first.querySelector('a[href*="/issues/"]');
        issueNo = /\/issues\/(\d+)/.exec(issueLink.getAttribute('href'));
        rec.issue = { url: issueLink.getAttribute('href'), number: issueNo ? +issueNo[1] : 0 };
      }
      return;
    }
    var quote = first;
    var links = quote.querySelectorAll('a[href*="#annot-"], a[href*=":~:text="]');
    var link = null;
    for (var i = 0; i < links.length; i++) {
      try {
        var u = new URL(links[i].getAttribute('href'));
        if (u.pathname === cfg.path) { link = links[i]; break; }
      } catch (e) { /* ignore */ }
    }
    if (!link) return;
    var linkHref = link.getAttribute('href');
    var fragment = parseTextFragment(linkHref);
    var cellsMatch = /#annot-[0-9a-f]{8}-cells-(\d+)-(\d+)-(\d+)-(\d+)/.exec(linkHref);
    var linkBlock = link.closest('p, sub') || link;
    while (linkBlock.parentNode !== quote && linkBlock.parentNode !== root) linkBlock = linkBlock.parentNode;
    issueLink = linkBlock.querySelector('a[href*="/issues/"]');
    issueNo = issueLink && /\/issues\/(\d+)/.exec(issueLink.getAttribute('href'));
    var sectionM = /位于「(.+?)」/.exec(linkBlock.textContent || '');
    linkBlock.parentNode.removeChild(linkBlock);
    var exact = quote.textContent.replace(/\s+/g, ' ').trim();
    if (!exact) return;
    root.removeChild(quote);
    rec.noteHTML = root.innerHTML;
    rec.selector = { exact: exact, prefix: fragment.prefix, suffix: fragment.suffix, section: sectionM ? sectionM[1] : '' };
    if (cellsMatch && +cellsMatch[1] * +cellsMatch[2] > 1) {
      rec.selector.cells = {
        rows: +cellsMatch[1],
        cols: +cellsMatch[2],
        dr: +cellsMatch[3],
        dc: +cellsMatch[4]
      };
    }
    rec.issue = issueLink ? { url: issueLink.getAttribute('href'), number: issueNo ? +issueNo[1] : 0 } : null;
  }

  // giscus' adapter returns replies as a plain array; GitHub GraphQL as {nodes}.
  function parseReplies(replies) {
    var list = Array.isArray(replies) ? replies : (replies && replies.nodes) || [];
    return list.filter(function (r) { return r && !r.deletedAt && !r.isMinimized; }).map(function (r) {
      return { id: r.id, url: r.url, createdAt: r.createdAt, lastEditedAt: r.lastEditedAt || null, bodyHTML: r.bodyHTML, author: r.author || GHOST,
        owner: r.authorAssociation === 'OWNER', votes: parseVotes(r.reactions || r.reactionGroups) };
    });
  }

  // 👍 / 👎 GitHub reactions are our up / down votes. giscus' adapter ships them as
  // { THUMBS_UP: { count, viewerHasReacted }, … }, GitHub GraphQL as
  // reactionGroups [{ content, viewerHasReacted, reactors { totalCount } }].
  function parseVotes(src) {
    var v = { up: 0, down: 0, hooray: 0, mine: null };
    function take(content, n, mine) {
      if (content === 'HOORAY') { v.hooray = n || 0; return; } // the author's 「已修正」 mark
      var dir = content === 'THUMBS_UP' ? 'up' : content === 'THUMBS_DOWN' ? 'down' : null;
      if (!dir) return;
      v[dir] = n || 0;
      if (mine) v.mine = dir;
    }
    if (Array.isArray(src)) src.forEach(function (g) { take(g.content, g.reactors ? g.reactors.totalCount : (g.users ? g.users.totalCount : 0), g.viewerHasReacted); });
    else if (src) Object.keys(src).forEach(function (k) { take(k, src[k].count, src[k].viewerHasReacted); });
    return v;
  }

  // Older links carried a Text Fragment with prefix-/-suffix context; keep reading it.
  function parseTextFragment(href) {
    var out = { prefix: '', suffix: '' };
    var m = /:~:text=([^&]*)/.exec(href);
    if (!m) return out;
    var parts = m[1].split(',');
    var dec = function (s) { try { return decodeURIComponent(s); } catch (e) { return s; } };
    if (parts.length && /-$/.test(parts[0])) out.prefix = dec(parts.shift().slice(0, -1));
    if (parts.length && /^-/.test(parts[parts.length - 1])) out.suffix = dec(parts.pop().slice(1));
    return out;
  }

  // ------------------------------------------------------- serialisation

  // Escape only what URL / Markdown-link syntax needs; CJK stays readable.
  function encodeFragmentPart(s) {
    return s.replace(/[\s%,&#()"'<>\[\]\\^`{}|]/g, function (c) {
      return '%' + ('0' + c.charCodeAt(0).toString(16).toUpperCase()).slice(-2);
    });
  }

  // Short stable id of a quote (FNV-1a, 32 bit) used in #annot-<hash> links.
  function annotHash(exact) {
    var h = 0x811c9dc5;
    for (var i = 0; i < exact.length; i++) { h ^= exact.charCodeAt(i); h = Math.imul(h, 0x01000193) >>> 0; }
    return ('0000000' + h.toString(16)).slice(-8);
  }

  function textFragment(sel) {
    var parts = [];
    if (sel.prefix) parts.push(encodeFragmentPart(sel.prefix) + '-');
    parts.push(encodeFragmentPart(sel.exact));
    if (sel.suffix) parts.push('-' + encodeFragmentPart(sel.suffix));
    return ':~:text=' + parts.join(',');
  }
  function threadLink(sel) {
    return cfg.siteUrl + cfg.path + '#annot-' + annotHash(sel.exact) +
      (sel.cells ? '-cells-' + [sel.cells.rows, sel.cells.cols, sel.cells.dr, sel.cells.dc].join('-') : '') +
      textFragment(sel);
  }
  function shareLink(sel) { return cfg.siteUrl + cfg.path + '#hl=' + encodeFragmentPart(sel.exact); }

  function escapeMarkdown(s) {
    // A backslash only escapes ASCII punctuation: `1\.` not `\1.` (the latter
    // rendered literally and broke the quote's hash for passages like "1.5px").
    return s.replace(/[\\`*_\[\]<>~|]/g, '\\$&').replace(/^[#>+\-]/, '\\$&').replace(/^(\d+)([.)])/, '$1\\$2');
  }

  // ` · 位于「二、总览」` — the chapter, so the note is actionable without opening the page.
  function sectionNote(sel) { return sel.section ? ' · 位于「' + escapeMarkdown(sel.section) + '」' : ''; }

  function buildCommentBody(sel, note, issue) {
    var links = '[§ 原文位置](' + threadLink(sel) + ')' + (issue ? ' · [⚑ Issue #' + issue.number + '](' + issue.url + ')' : '') + sectionNote(sel);
    return '> ' + escapeMarkdown(sel.exact) + '\n>\n> <sub>' + links + '</sub>\n\n' + note.trim() + '\n';
  }

  // ------------------------------------------------------------ selection

  function bindSelection() {
    document.addEventListener('selectionchange', function () {
      clearTimeout(selectionTimer);
      selectionTimer = setTimeout(updateToolbar, 250);
    });
    document.addEventListener('mouseup', function () { setTimeout(updateToolbar, 10); });
    document.addEventListener('click', function (e) {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      var target = e.target.nodeType === 1 ? e.target : e.target.parentElement;
      if (!target || target.closest('.annotation-hl') || target.closest('a')) return;
      var host = diagramLabelHost(target);
      if (!host || !host.textContent.trim()) return;
      var selection = window.getSelection();
      if (!selection || !selection.isCollapsed) return;
      var range = document.createRange();
      range.selectNodeContents(host);
      if (!range.toString().trim()) return;
      selection.removeAllRanges();
      selection.addRange(range);
      updateToolbar();
    });
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape') { hideToolbar(); if (panelState && panelState.kind === 'thread') closePanel(); } });
    window.addEventListener('scroll', function () { if (toolbar && toolbar.style.display === 'block') positionToolbar(); }, { passive: true });
  }

  function tableSelection() {
    var selection = window.getSelection();
    if (selection && !selection.isCollapsed) return null;
    return window.BlogTableSelect && window.BlogTableSelect.current();
  }

  function currentRange() {
    var sel = window.getSelection(), range;
    if (!sel || sel.isCollapsed) {
      var ts = tableSelection();
      if (!ts) return null;
      range = ts.range.cloneRange();
    } else {
      if (sel.rangeCount === 0) return null;
      range = sel.getRangeAt(0).cloneRange();
    }
    if (!container.contains(range.commonAncestorContainer)) return null;
    var startHost = diagramLabelHost(range.startContainer), endHost = diagramLabelHost(range.endContainer);
    if (startHost && endHost !== startHost) {
      range.setEnd(startHost, startHost.childNodes.length);
    } else if (!mermaidOf(range.startContainer) && mermaidOf(range.endContainer)) {
      range.setEndBefore(mermaidOf(range.endContainer));
    }
    // A boundary inside a formula takes the whole formula (its TeX source is
    // what gets quoted); one inside another excluded element (heading buttons,
    // `↩`…) snaps to that element's edge instead of bailing.
    var k = katexOf(range.startContainer), ex = k || excludedAncestor(range.startContainer);
    if (k) range.setStartBefore(k); else if (ex) range.setStartAfter(ex);
    k = katexOf(range.endContainer); ex = k || excludedAncestor(range.endContainer);
    if (k) range.setEndAfter(k); else if (ex) range.setEndBefore(ex);
    if (range.collapsed || !container.contains(range.commonAncestorContainer)) return null;
    if (!range.toString().trim()) return null;
    return range;
  }

  function excludedAncestor(node) {
    if (diagramLabelHost(node)) return null;
    var el = node.nodeType === 1 ? node : node.parentNode;
    var hit = el && el.closest && el.closest(EXCLUDE_SELECTOR);
    if (!hit || !container.contains(hit)) return null;
    // Outermost excluded ancestor inside the article (e.g. `.katex`, not a span within it).
    var top = hit, p = hit.parentElement;
    while (p && container.contains(p) && p !== container) { if (p.matches(EXCLUDE_SELECTOR)) top = p; p = p.parentElement; }
    return top;
  }

  function updateToolbar() {
    if (panelState && panelState.kind === 'editor') return;
    if (container.querySelector('table.ts-dragging')) {
      if (toolbar) toolbar.style.display = 'none';
      closeSearchMenu();
      return;
    }
    var range = currentRange();
    if (!range) { hideToolbar(); return; }
    ensureToolbar();
    updateCommentButton(range);
    updatePinButton(range);
    updateFavoriteButton(range);
    toolbar.style.display = 'block';
    positionToolbar();
  }
  function updateFavoriteButton(range) {
    var btn = toolbar.querySelector('.annotation-tb-fav');
    if (!btn) return;
    var offsets = selectionOffsets(range), p = offsets && passageContaining(offsets);
    var exact = p ? p.exact : (offsets ? selectorFromOffsets(offsets).exact : '');
    var saved = !!(window.BlogFavorites && exact && window.BlogFavorites.hasPassage(cfg.path, exact));
    btn.innerHTML = '<i class="fa ' + (saved ? 'fa-bookmark' : 'fa-regular fa-bookmark') + '"></i> ' + (saved ? '取消收藏' : '收藏');
    btn.title = saved ? '取消收藏这段文字' : '收藏这段文字';
  }
  function updatePinButton(range) {
    var btn = toolbar.querySelector('.annotation-tb-pin');
    btn.hidden = !isOwner();
    if (btn.hidden) return;
    var offsets = selectionOffsets(range), p = offsets && passageContaining(offsets);
    var pinned = !!(p && p.reaction && p.reaction.pinned > 0);
    btn.innerHTML = '<i class="fa fa-pencil"></i> ' + (pinned ? '取消划重点' : '划重点');
    btn.title = pinned ? '取消这段划重点' : '替读者先划好这段';
  }

  // The 「评论」 button says what will actually happen: a selection inside an
  // already-underlined passage joins that thread — and when the viewer's own
  // note is there, it opens that note for editing (the common case for the
  // author re-reading a review: fix the wording, not add a second note).
  function selectionContext(range) {
    var offsets = selectionOffsets(range);
    var p = offsets && passageContaining(offsets);
    return { offsets: offsets, passage: p, mine: p ? myCommentIn(p) : null };
  }
  function updateCommentButton(range) {
    var ctx = selectionContext(range), btn = toolbar.querySelector('.annotation-tb-comment'), n = ctx.passage ? commentCount(ctx.passage) : 0;
    if (ctx.mine) { btn.innerHTML = '<i class="fa fa-pencil"></i> 编辑评论'; btn.title = '你评论过这段话，点击修改那条评论'; }
    else if (n) { btn.innerHTML = '<i class="fa fa-regular fa-comment"></i> 加入讨论'; btn.title = '这段话已有 ' + n + ' 条评论，在同一讨论串里接着说'; }
    else { btn.innerHTML = '<i class="fa fa-regular fa-comment"></i> 评论'; btn.title = '对选中的文字发表评论'; }
  }
  // Open the passage thread and drop straight into editing the viewer's note.
  function editMyComment(p, mine) {
    openThread(p.ids, p.marks[p.marks.length - 1]);
    var el = panel && panel.querySelector('.ap-comment[data-comment-id="' + mine.id + '"] .ap-edit-btn');
    if (el) el.click();
  }

  function positionToolbar() {
    var range = currentRange();
    if (!range) return;
    var ts = tableSelection();
    var rect = ts ? ts.rect : range.getBoundingClientRect();
    var scrollX = window.pageXOffset, scrollY = window.pageYOffset;
    var w = toolbar.offsetWidth, h = toolbar.offsetHeight;
    var below = rect.top - h - 10 < 60;
    toolbar.classList.toggle('is-below', below);
    var top = below ? rect.bottom + scrollY + 10 : rect.top + scrollY - h - 10;
    var left = rect.left + scrollX + rect.width / 2 - w / 2;
    left = Math.max(scrollX + 8, Math.min(left, scrollX + document.documentElement.clientWidth - w - 8));
    toolbar.style.top = top + 'px';
    toolbar.style.left = left + 'px';
  }

  function hideToolbar() {
    if (toolbar) toolbar.style.display = 'none';
    closeSearchMenu();
    if (window.BlogTableSelect) window.BlogTableSelect.clear();
  }

  // ------------------------------------------------------------ 搜一搜 menu
  // 站内 = the site search overlay (search-overlay.html exposes
  // `openSearchOverlay(q)`); Google = a plain web search; Google AI 模式 =
  // `udm=50` with the passage wrapped in a prompt that names the article and
  // chapter — a URL has no system prompt, the instructions ride in `q`.
  var searchPop = null;
  function openSearchMenu(btn, q, section) {
    if (!searchPop) {
      searchPop = document.createElement('div');
      searchPop.className = 'pa-share-pop annotation-search-pop';
      searchPop.setAttribute('role', 'menu');
      searchPop.innerHTML =
        '<button type="button" class="pa-sp-item" data-k="site"><i class="fa fa-search"></i>站内搜索</button>' +
        '<button type="button" class="pa-sp-item" data-k="google"><i class="fa fa-brands fa-google"></i>Google</button>' +
        '<button type="button" class="pa-sp-item" data-k="ai" title="在 Google AI 模式里解释这段话（带上文章与章节）"><i class="fa fa-wand-magic-sparkles"></i>Google AI 模式</button>';
      searchPop.addEventListener('mousedown', function (e) { e.preventDefault(); }); // keep the selection
      searchPop.addEventListener('click', function (e) {
        var item = e.target.closest('.pa-sp-item');
        if (!item) return;
        e.stopPropagation();
        var k = item.getAttribute('data-k'), d = searchPop._ctx;
        if (k === 'site') { if (window.openSearchOverlay) window.openSearchOverlay(d.q); else location.href = '/search/?q=' + encodeURIComponent(d.q); }
        else if (k === 'google') window.open('https://www.google.com/search?q=' + encodeURIComponent(d.q), '_blank', 'noopener');
        else window.open('https://www.google.com/search?udm=50&q=' + encodeURIComponent(aiPrompt(d)), '_blank', 'noopener');
        hideToolbar();
        if (window.getSelection) window.getSelection().removeAllRanges();
      });
      document.addEventListener('click', function (e) { if (!searchPop.hidden && !searchPop.contains(e.target)) closeSearchMenu(); });
      document.addEventListener('keydown', function (e) { if (e.key === 'Escape') closeSearchMenu(); });
      document.body.appendChild(searchPop);
    }
    searchPop._ctx = { q: q, section: section };
    searchPop.hidden = false; searchPop.style.visibility = 'hidden';
    var r = btn.getBoundingClientRect(), pw = searchPop.offsetWidth, ph = searchPop.offsetHeight;
    var left = Math.min(Math.max(8, r.left + window.scrollX), window.scrollX + document.documentElement.clientWidth - pw - 8);
    var below = r.bottom + 8 + ph < window.innerHeight || r.top < ph + 8;
    searchPop.style.left = left + 'px';
    searchPop.style.top = (below ? r.bottom + window.scrollY + 8 : r.top + window.scrollY - ph - 8) + 'px';
    searchPop.style.visibility = '';
    btn.setAttribute('aria-expanded', 'true');
  }
  function closeSearchMenu() {
    if (!searchPop || searchPop.hidden) return;
    searchPop.hidden = true;
    if (toolbar) toolbar.querySelector('.annotation-tb-search').setAttribute('aria-expanded', 'false');
  }
  function postTitle() {
    var og = document.querySelector('meta[property="og:title"]');
    return (og && og.getAttribute('content')) || document.title.replace(/\s+-\s+[^-]+$/, '');
  }
  function aiPrompt(d) {
    return '下面这段话出自技术博客文章《' + postTitle() + '》' + (d.section ? '的「' + d.section + '」一节' : '') +
      '（' + location.href.split('#')[0] + '）。请用中文解释它的含义和背后的原理，指出其中容易误解或可能有误的地方，并补充相关背景与延伸阅读：\n\n“' + d.q + '”';
  }

  function ensureToolbar() {
    if (toolbar) return toolbar;
    toolbar = document.createElement('div');
    toolbar.className = 'annotation-toolbar';
    toolbar.innerHTML =
      '<button type="button" class="annotation-tb-up" title="点赞这段话（不用登录）"><i class="fa fa-regular fa-thumbs-up"></i> 点赞</button>' +
      '<button type="button" class="annotation-tb-pin" hidden><i class="fa fa-pencil"></i> 划重点</button>' +
      '<span class="annotation-tb-sep"></span>' +
      '<button type="button" class="annotation-tb-comment"><i class="fa fa-regular fa-comment"></i> 评论</button>' +
      '<button type="button" class="annotation-tb-fav" title="收藏选中的文字"><i class="fa fa-regular fa-bookmark"></i> 收藏</button>' +
      '<button type="button" class="annotation-tb-copy" title="复制选中的文字"><i class="fa fa-copy"></i> 复制</button>' +
      '<button type="button" class="annotation-tb-search" title="搜这段文字：站内 / Google / Google AI 模式" aria-haspopup="true" aria-expanded="false"><i class="fa fa-search"></i> 搜一搜</button>' +
      '<button type="button" class="annotation-tb-share" title="分享这段话：微博 / X / 微信 / 复制链接（打开后自动定位这段文字）" aria-haspopup="true" aria-expanded="false"><i class="fa fa-share-alt"></i> 分享</button>' +
      '<span class="annotation-tb-arrow"></span>';
    toolbar.addEventListener('mousedown', function (e) { e.preventDefault(); }); // keep the selection
    toolbar.querySelector('.annotation-tb-up').addEventListener('click', function (e) {
      e.stopPropagation();
      var range = currentRange();
      var offsets = range && selectionOffsets(range);
      hideToolbar();
      if (!offsets) return;
      // inside an underlined passage -> react on that passage, not on a new sub-range
      var p = passageContaining(offsets);
      var exact = p ? p.exact : selectorFromOffsets(offsets).exact;
      if (window.getSelection) window.getSelection().removeAllRanges();
      reactFromToolbar(exact);
    });
    toolbar.querySelector('.annotation-tb-pin').addEventListener('click', function (e) {
      e.stopPropagation();
      if (!isOwner()) return;
      var range = currentRange(), offsets = range && selectionOffsets(range);
      if (!offsets) { hideToolbar(); return; }
      var p = passageContaining(offsets), exact = p ? p.exact : selectorFromOffsets(offsets).exact;
      var on = !(p && p.reaction && p.reaction.pinned > 0);
      hideToolbar();
      if (window.getSelection) window.getSelection().removeAllRanges();
      setPinned(exact, on);
    });
    [['comment', '']].forEach(function (pair) {
      toolbar.querySelector('.annotation-tb-' + pair[0]).addEventListener('click', function (e) {
        e.stopPropagation();
        var ts = tableSelection();
        var range = currentRange();
        var ctx = range ? selectionContext(range) : { offsets: null };
        if (!ctx.offsets) { hideToolbar(); return; }
        var sel = selectorFromOffsets(ctx.offsets);
        if (ts) sel.cells = ts.cells;
        var endNode = range.endContainer;
        hideToolbar();
        if (ctx.mine) editMyComment(ctx.passage, ctx.mine);
        else openComposer(sel, ctx.offsets, endNode, ts ? ts.markdown + '\n\n' : '', pair[1]);
      });
    });
    toolbar.querySelector('.annotation-tb-fav').addEventListener('click', function (e) {
      e.stopPropagation();
      var range = currentRange(), offsets = range && selectionOffsets(range);
      if (!offsets || !window.BlogFavorites || document.documentElement.classList.contains('private-post')) { hideToolbar(); return; }
      var p = passageContaining(offsets), sel = p ? { exact: p.exact, section: ((p.list || [])[0] && (p.list || [])[0].selector.section) || (p.reaction && p.reaction.section) || sectionForOffsets(offsets) } : selectorFromOffsets(offsets);
      if (!p) sel.section = sectionForOffsets(offsets);
      var saved = window.BlogFavorites.togglePassage({ path: cfg.path, title: postTitle(), exact: sel.exact, section: sel.section });
      hideToolbar();
      if (window.getSelection) window.getSelection().removeAllRanges();
      showToast(saved ? '已收藏这段摘录' : '已取消收藏');
    });
    toolbar.querySelector('.annotation-tb-copy').addEventListener('click', function (e) {
      e.stopPropagation();
      var ts = tableSelection();
      var range = currentRange();
      if (!range) return;
      copyText(ts ? ts.text : range.toString()).then(function () { showToast('已复制'); });
      hideToolbar();
    });
    toolbar.querySelector('.annotation-tb-search').addEventListener('click', function (e) {
      e.stopPropagation();
      var ts = tableSelection();
      var range = currentRange();
      if (!range) return;
      var offsets = selectionOffsets(range);
      var q = (ts ? ts.text : range.toString()).replace(/\s+/g, ' ').trim().slice(0, 200);
      openSearchMenu(e.currentTarget, q, offsets ? sectionForOffsets(offsets) : '');
    });
    toolbar.querySelector('.annotation-tb-share').addEventListener('click', function (e) {
      e.stopPropagation();
      var range = currentRange();
      var offsets = range && selectionOffsets(range);
      if (!offsets) return;
      // inside an underlined passage -> share that passage (its thread link)
      var p = passageContaining(offsets);
      sharePassage(e.currentTarget, p ? p.exact : selectorFromOffsets(offsets).exact, !!(p && p.list.length));
    });
    document.body.appendChild(toolbar);
    return toolbar;
  }

  // ----------------------------------------------------------------- auth

  function getSession() {
    try {
      var raw = localStorage.getItem(SESSION_KEY);
      return raw ? JSON.parse(raw) : '';
    } catch (e) { return ''; }
  }

  function login() {
    var url = new URL(location.href);
    url.hash = '';
    url.searchParams.delete('giscus');
    location.href = GISCUS_ORIGIN + '/api/oauth/authorize?redirect_uri=' + encodeURIComponent(url.toString());
  }

  function ensureToken() {
    if (token) return Promise.resolve(token);
    var session = getSession();
    if (!session) return Promise.reject(new Error('尚未登录 GitHub'));
    return api('/token', { method: 'POST', body: { session: session } }).then(function (data) {
      if (!data.token) throw new Error('no token');
      token = data.token;
      return token;
    });
  }

  function graphql(query, variables) {
    return ensureToken().then(function (tk) {
      return fetch(GITHUB_GRAPHQL, {
        method: 'POST',
        headers: { Authorization: 'Bearer ' + tk, 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: query, variables: variables })
      });
    }).then(function (r) {
      return r.json().then(function (data) {
        if (r.status === 401 || (data.message && /bad credentials/i.test(data.message))) {
          token = null;
          try { localStorage.removeItem(SESSION_KEY); } catch (e) { /* ignore */ }
          throw new Error('登录已过期，请重新登录 GitHub');
        }
        if (data.errors && data.errors.length) throw new Error(data.errors[0].message);
        if (!r.ok) throw new Error(data.message || ('HTTP ' + r.status));
        if (!viewer && query !== VIEWER_QUERY) fetchViewer(); // an earlier attempt failed; the token works now
        return data.data;
      });
    });
  }

  // --------------------------------------------------------------- GitHub

  var ADD_COMMENT = 'mutation($body: String!, $discussionId: ID!, $replyToId: ID) {' +
    ' addDiscussionComment(input: {body: $body, discussionId: $discussionId, replyToId: $replyToId}) { comment {' +
    ' id url createdAt bodyHTML authorAssociation author { login avatarUrl url } replies { totalCount } } } }';

  // ------------------------------------------------ likes / votes (reactions)

  var REACTION_FIELDS = 'reactionGroups { content viewerHasReacted reactors { totalCount } }';
  var VIEWER_REACTIONS = 'query($id: ID!) { node(id: $id) { ... on Discussion { ' + REACTION_FIELDS +
    ' comments(first: 100) { nodes { id ' + REACTION_FIELDS + ' replies(first: 100) { nodes { id ' + REACTION_FIELDS + ' } } } } } } }';
  var ADD_REACTION = 'mutation($id: ID!, $content: ReactionContent!) { addReaction(input: {subjectId: $id, content: $content}) { reaction { id } } }';
  var REMOVE_REACTION = 'mutation($id: ID!, $content: ReactionContent!) { removeReaction(input: {subjectId: $id, content: $content}) { reaction { id } } }';
  var CONTENT = { up: 'THUMBS_UP', down: 'THUMBS_DOWN' };

  // The relay serves the discussion anonymously (cached), so once the reader is
  // known fetch the same reactions with their token to learn what they voted.
  var viewerReactionsPending = false;
  function loadViewerReactions() {
    if (!discussion || !discussion.id || viewerReactionsPending) return;
    viewerReactionsPending = true;
    graphql(VIEWER_REACTIONS, { id: discussion.id }).then(function (data) {
      viewerReactionsPending = false;
      var d = data.node;
      if (!d) return;
      var byId = {};
      comments.forEach(function (c) { byId[c.id] = c; c.replies.forEach(function (r) { byId[r.id] = r; }); });
      discussion.likes = parseVotes(d.reactionGroups);
      (d.comments.nodes || []).forEach(function (n) {
        if (byId[n.id]) byId[n.id].votes = parseVotes(n.reactionGroups);
        (n.replies.nodes || []).forEach(function (r) { if (byId[r.id]) byId[r.id].votes = parseVotes(r.reactionGroups); });
      });
      comments.forEach(function (c) { updateVoteEls(c); c.replies.forEach(updateVoteEls); });
      renderLikeBar();
    }).catch(function () { viewerReactionsPending = false; });
  }

  // Toggle the reader's 👍 / 👎 on a comment (`dir` = 'up' | 'down'): optimistic,
  // one vote per person, switching sides removes the other reaction first.
  function toggleVote(rec, dir) {
    if (!getSession()) { showToast('登录 GitHub 后即可投票'); return; }
    var v = rec.votes, prev = { up: v.up, down: v.down, mine: v.mine };
    var steps = [];
    if (v.mine === dir) { v[dir] = Math.max(0, v[dir] - 1); v.mine = null; steps.push([REMOVE_REACTION, dir]); }
    else {
      if (v.mine) { v[v.mine] = Math.max(0, v[v.mine] - 1); steps.push([REMOVE_REACTION, v.mine]); }
      v[dir] += 1; v.mine = dir; steps.push([ADD_REACTION, dir]);
    }
    updateVoteEls(rec);
    steps.reduce(function (p, s) {
      return p.then(function () { return graphql(s[0], { id: rec.id, content: CONTENT[s[1]] }); });
    }, Promise.resolve()).catch(function (err) {
      rec.votes = prev;
      updateVoteEls(rec);
      showToast('投票失败：' + err.message);
    });
  }

  function voteHtml(rec) {
    var v = rec.votes || parseVotes(null), score = v.up - v.down;
    return '<span class="ap-vote' + (v.mine ? ' is-' + v.mine : '') + '" title="' + v.up + ' 赞同 · ' + v.down + ' 反对">' +
      '<button type="button" class="ap-vote-up" title="赞同"><i class="fa fa-caret-up"></i></button>' +
      '<b class="ap-vote-score' + (score < 0 ? ' is-negative' : '') + '">' + score + '</b>' +
      '<button type="button" class="ap-vote-down" title="反对"><i class="fa fa-caret-down"></i></button></span>';
  }

  // Every rendering of a comment (panel + bottom section) shows the same votes.
  function updateVoteEls(rec) {
    if (commentsHost) renderHotPassages(); // ranking follows the votes
    var els = document.querySelectorAll('.ap-comment[data-comment-id="' + rec.id + '"] .ap-vote');
    for (var i = 0; i < els.length; i++) {
      var span = document.createElement('span');
      span.innerHTML = voteHtml(rec);
      bindVote(span.firstChild, rec);
      els[i].parentNode.replaceChild(span.firstChild, els[i]);
    }
  }

  function bindVote(voteEl, rec) {
    voteEl.querySelector('.ap-vote-up').addEventListener('click', function () { toggleVote(rec, 'up'); });
    voteEl.querySelector('.ap-vote-down').addEventListener('click', function () { toggleVote(rec, 'down'); });
  }

  // ------------------------------------------------ passage 赞 (anonymous)
  // Same trust model as page views: the worker keeps `passage_reactions(path,
  // hash, quote, up, share)` in D1, one row per passage; the browser remembers
  // its own choices in localStorage and sends toggles. No GitHub login — this
  // is "raising a hand", commenting is "speaking". A local preview only posts
  // when `localStorage.annotationsApi` points it at a dev worker.
  var reactLocalOnly = /^(localhost|127\.0\.0\.1)$/.test(location.hostname) && !localStorage.getItem('annotationsApi');

  function reactKey(hash, kind) { return 'react:' + cfg.path + ':' + hash + ':' + kind; }
  function myReaction(hash, kind) { try { return localStorage.getItem(reactKey(hash, kind)) === '1'; } catch (e) { return false; } }
  function rememberReaction(hash, kind, on) { try { if (on) localStorage.setItem(reactKey(hash, kind), '1'); else localStorage.removeItem(reactKey(hash, kind)); } catch (e) { /* ignore */ } }

  function newReaction(hash, quote) { return { hash: hash, quote: quote, section: '', up: 0, share: 0, pinned: 0, range: null, marks: [] }; }
  function takeCounts(r, d) {
    r.up = d.up || 0;
    r.share = d.share || 0;
    r.pinned = typeof d.pinned === 'number' ? d.pinned : (r.pinned || 0);
  }
  function isOwner() { return !!(viewer && cfg.author && viewer.login.toLowerCase() === cfg.author.toLowerCase()); }

  function loadReactions() {
    return api('/reactions?path=' + encodeURIComponent(cfg.path)).then(function (data) {
      reactions = {};
      chapters = {};
      (data.items || []).forEach(function (it) {
        if (!it || !/^[0-9a-f]{8}$/.test(it.hash) || !(it.up > 0 || it.share > 0 || it.pinned > 0)) return;
        if (it.quote && it.quote.indexOf(CHAPTER_PREFIX) === 0) { chapters[it.quote.slice(CHAPTER_PREFIX.length)] = { hash: it.hash, quote: it.quote, up: it.up || 0 }; return; }
        var r = reactions[it.hash] = newReaction(it.hash, it.quote || '');
        r.section = it.section || '';
        takeCounts(r, it);
      });
      renderChapterBars();
      if (loaded) { applyHighlights(); if (commentsHost) renderHotPassages(); }
    }).catch(function (err) { console.warn('[annotations] reactions unavailable:', err.message); });
  }

  // Section-level reactions: anonymous one-tap 点赞 on a whole section,
  // stored in passage_reactions with quote = '§ ' + title so the dashboard and
  // the 修订简报 can tell a section row from a passage row. Only pages that lay
  // down an empty `.sec-react` placeholder get them (`<span class="sec-react"
  // data-title="…" data-kinds="up" data-icon="fa-regular fa-heart"
  // data-icon-on="fa-heart">`, the ♡ under every 随笔 entry); article headings
  // carry none.
  var CHAPTER_PREFIX = '§ ';
  var CHAPTER_KINDS = [{ kind: 'up', label: '点赞', title: '点个赞' }];
  function chapterFor(title) {
    var quote = CHAPTER_PREFIX + title;
    return chapters[title] || (chapters[title] = { hash: annotHash(quote), quote: quote, up: 0 });
  }
  function fillChapterBar(bar, title) {
    var kinds = (bar.getAttribute('data-kinds') || 'up').split(/\s+/);
    CHAPTER_KINDS.forEach(function (k) {
      if (kinds.indexOf(k.kind) === -1) return;
      var b = document.createElement('button');
      b.type = 'button'; b.className = 'sec-react-btn sec-react-' + k.kind; b.setAttribute('data-kind', k.kind); b.title = k.title;
      var icon = bar.getAttribute('data-icon') || 'fa-thumbs-up';
      b.innerHTML = '<i class="fa ' + icon + '"></i>' + (bar.hasAttribute('data-icon') ? '' : k.label) + '<b></b>';
      b.addEventListener('click', onChapterClick);
      bar.appendChild(b);
    });
    bar.setAttribute('aria-label', title);
  }
  function renderChapterBars() {
    var placed = container.querySelectorAll('.sec-react[data-title]');
    for (var j = 0; j < placed.length; j++) {
      if (!placed[j].firstChild) fillChapterBar(placed[j], placed[j].getAttribute('data-title'));
      paintChapterBar(placed[j], placed[j].getAttribute('data-title'));
    }
  }
  function paintChapterBar(bar, title) {
    var c = chapters[title], hash = c ? c.hash : annotHash(CHAPTER_PREFIX + title);
    CHAPTER_KINDS.forEach(function (k) {
      var b = bar.querySelector('.sec-react-' + k.kind), n = c ? c[k.kind] : 0;
      if (!b) return;
      var on = myReaction(hash, k.kind);
      b.classList.toggle('is-on', on);
      if (bar.hasAttribute('data-icon-on')) b.querySelector('i').className = 'fa ' + (on ? bar.getAttribute('data-icon-on') : bar.getAttribute('data-icon'));
      b.querySelector('b').textContent = n > 0 ? n : '';
    });
  }
  function onChapterClick(e) {
    e.preventDefault(); e.stopPropagation();
    var btn = e.currentTarget, bar = btn.parentNode, title = bar.getAttribute('data-title'), kind = btn.getAttribute('data-kind');
    var c = chapterFor(title), on = !myReaction(c.hash, kind), before = c[kind];
    c[kind] = Math.max(0, c[kind] + (on ? 1 : -1));
    rememberReaction(c.hash, kind, on);
    paintChapterBar(bar, title);
    if (reactLocalOnly) { showToast(on ? '已点赞' : '已取消'); return; }
    api('/reactions', { method: 'POST', body: { path: cfg.path, hash: c.hash, quote: c.quote, kind: kind, on: on, section: title } })
      .then(function (d) { c.up = d.up || 0; paintChapterBar(bar, title); showToast(on ? '已点赞，谢谢' : '已取消'); })
      .catch(function (err) { c[kind] = before; rememberReaction(c.hash, kind, !on); paintChapterBar(bar, title); showToast('操作失败：' + err.message); });
  }

  // Toggle my 点赞 on the passage with this exact text. Optimistic; the
  // underline / marker / open panel / ranking follow the new count.
  function react(exact, kind) {
    var hash = annotHash(exact);
    var r = reactions[hash] || (reactions[hash] = newReaction(hash, exact));
    var on = !myReaction(hash, kind), before = r[kind];
    r[kind] = Math.max(0, r[kind] + (on ? 1 : -1));
    rememberReaction(hash, kind, on);
    refreshReactionViews(hash);
    if (reactLocalOnly) return Promise.resolve({ on: on, r: r });
    var body = { path: cfg.path, hash: hash, quote: exact, kind: kind, on: on, section: r.section || sectionForExact(exact) };
    return api('/reactions', { method: 'POST', body: body })
      .then(function (d) { takeCounts(r, d); refreshReactionViews(hash); return { on: on, r: r }; })
      .catch(function (err) {
        r[kind] = before; rememberReaction(hash, kind, !on); refreshReactionViews(hash);
        showToast('操作失败：' + err.message);
        return null;
      });
  }

  function passageCardContext(exact) {
    buildIndex();
    var match = anchor({ exact: exact });
    if (!match) return { before: '', after: '' };
    var segs = segmentsFor(match.start, match.end);
    if (!segs.length) return { before: '', after: '' };
    var blockSelector = 'p, li, blockquote, dd, td, th, figcaption, h2, h3, h4, h5, h6';
    function blockFor(node) {
      var el = node.nodeType === 1 ? node : node.parentElement;
      var block = el && el.closest(blockSelector);
      return block && container.contains(block) ? block : null;
    }
    var first = segs[0], last = segs[segs.length - 1];
    var firstBlock = blockFor(first.node), lastBlock = blockFor(last.node);
    if (!firstBlock || !lastBlock) return { before: '', after: '' };
    try {
      var beforeRange = document.createRange();
      beforeRange.selectNodeContents(firstBlock);
      beforeRange.setEnd(first.node, first.from);
      var afterRange = document.createRange();
      afterRange.selectNodeContents(lastBlock);
      afterRange.setStart(last.node, last.to);
      return {
        before: beforeRange.toString().slice(-120),
        after: afterRange.toString().slice(0, 120)
      };
    } catch (e) {
      return { before: '', after: '' };
    }
  }

  // 分享 a passage: the same popover as the article's 「分享」 (js/share.js,
  // window.BlogShare) with the passage link, quote, and generated share card.
  // Every completed share is one more `share` on the passage (worker also
  // bumps the article's share count). No toggle, no memory — a share is a share.
  function sharePassage(btn, exact, hasThread) {
    var sel = { exact: exact };
    var url = hasThread ? threadLink(sel) : shareLink(sel);
    var quote = exact.length > 120 ? exact.slice(0, 118) + '…' : exact;
    var bar = document.querySelector('.post-actions');
    var title = (bar && bar.getAttribute('data-title')) || document.title;
    var context = passageCardContext(exact);
    var screenshotBlock = supportsScreenshotCapture() && window.BlogPassageCard &&
      typeof window.BlogPassageCard.renderScreenshot === 'function' ? blockForCaptionPassage(exact) : null;
    var counted = function () { countPassageShare(exact); };
    if (!window.BlogShare) {
      copyText(url).then(function () { showToast('分享链接已复制：打开后会自动定位这段文字'); counted(); });
      return;
    }
    window.BlogShare.open(btn, {
      url: url,
      title: title,
      text: '「' + quote + '」',
      onShared: counted,
      toast: showToast,
      card: function () {
        if (screenshotBlock) {
          return captureBlock(screenshotBlock).then(function (shot) {
            if (!shot) return null;
            return window.BlogPassageCard.renderScreenshot({ image: shot.blob, title: title, url: url });
          });
        }
        return window.BlogPassageCard.render({
          before: context.before,
          quote: exact,
          after: context.after,
          title: title,
          url: url
        });
      },
      filename: 'passage-' + annotHash(exact) + '.png'
    });
  }

  function captionPassageMatches(caption, exact) {
    var title = caption.querySelector('.fig-title') || caption.querySelector('.fig-no');
    return !!title && title.textContent.replace(/\s+/g, ' ').trim() === exact;
  }

  function blockForCaptionPassage(exact) {
    exact = (exact || '').replace(/\s+/g, ' ').trim();
    if (!exact) return null;
    var captions = container.querySelectorAll('.post-figcaption');
    for (var i = 0; i < captions.length; i++) {
      if (!captionPassageMatches(captions[i], exact)) continue;
      var figure = captions[i].previousElementSibling;
      if (figure && figure.matches('figure.post-figure')) return figure.querySelector(':scope > .fig-media') || figure;
      if (figure && figure.matches('.mermaid')) return figure.querySelector(':scope > .fig-media') || figure;
    }
    var tableCaptions = container.querySelectorAll('.table-caption');
    for (var t = 0; t < tableCaptions.length; t++) {
      if (!captionPassageMatches(tableCaptions[t], exact)) continue;
      var preceding = tableCaptions[t].previousElementSibling;
      var table = preceding && (preceding.matches('table') ? preceding : preceding.querySelector('table'));
      if (table) return table.parentElement && table.parentElement.classList.contains('table-responsive') ? table.parentElement : table;
    }
    var headers = container.querySelectorAll('.code-header');
    for (var h = 0; h < headers.length; h++) {
      if (!captionPassageMatches(headers[h], exact)) continue;
      var codeBlock = headers[h].closest('.code-block');
      if (codeBlock) return codeBlock;
    }
    return null;
  }
  function countPassageShare(exact) {
    var hash = annotHash(exact);
    var r = reactions[hash] || (reactions[hash] = newReaction(hash, exact));
    r.share = (r.share || 0) + 1;
    refreshReactionViews(hash);
    if (reactLocalOnly) return;
    api('/reactions', { method: 'POST', body: { path: cfg.path, hash: hash, quote: exact, kind: 'share', section: r.section || sectionForExact(exact) } })
      .then(function (d) { takeCounts(r, d); refreshReactionViews(hash); if (typeof d.shares === 'number') document.dispatchEvent(new CustomEvent('blog:stats', { detail: { shares: d.shares } })); })
      .catch(function () { /* keep the optimistic number */ });
  }

  // Cheap path when the passage is already underlined; otherwise re-anchor so
  // the underline appears or goes.
  function refreshReactionViews(hash) {
    var p = passageFor(['r:' + hash]), r = reactions[hash];
    var alive = r && (r.up > 0 || r.pinned > 0), hadMarks = r && r.marks.length;
    if (!p && !alive && !hadMarks) return; // e.g. a share of a passage nobody underlined: nothing to paint
    if (!p || (!alive && !p.list.length)) {
      applyHighlights();
      if (panelState && panelState.kind === 'thread' && panelState.ids.indexOf('r:' + hash) !== -1) {
        var host = panel.querySelector('.ap-react'), fallback = { hash: hash, exact: r.quote, reaction: r, list: [] };
        if (host) { host.innerHTML = reactBarHtml(fallback); bindReactBar(host, fallback); }
      }
      if (commentsHost) renderHotPassages();
      return;
    }
    var marker = container.querySelector('.annotation-marker[data-hash="' + hash + '"]');
    if (!!marker !== markerNeeded(p)) {
      applyHighlights();
      p = passageFor(['r:' + hash]) || p;
      marker = container.querySelector('.annotation-marker[data-hash="' + hash + '"]');
    }
    if (marker) { marker.innerHTML = markerHtml(p); marker.title = markerTitle(p); }
    if (panelState && panelState.kind === 'thread' && passageFor(panelState.ids) && passageFor(panelState.ids).hash === hash) {
      var host = panel.querySelector('.ap-react');
      if (host) { host.innerHTML = reactBarHtml(p); bindReactBar(host, p); }
      var count = panel.querySelector('.ap-count');
      if (count) count.textContent = commentCount(p) ? commentCount(p) + ' 条评论' : '还没有评论';
    }
    if (commentsHost) renderHotPassages();
  }

  // From the selection toolbar, 赞 underlines and confirms.
  function reactFromToolbar(exact) {
    var hash = annotHash(exact);
    var wasOn = myReaction(hash, 'up');
    react(exact, 'up');
    var p = passageFor(['r:' + hash]);
    if (!p || !p.marks.length) { showToast(wasOn ? '已取消' : '已赞'); return; }
    flashMarks(p.marks);
    showToast(wasOn ? '已取消点赞' : '已点赞这段话');
  }
  function setPinned(exact, on) {
    var hash = annotHash(exact), r = reactions[hash] || (reactions[hash] = newReaction(hash, exact));
    var before = r.pinned || 0;
    r.pinned = on ? 1 : 0;
    refreshReactionViews(hash);
    return ensureToken().then(function (tk) {
      return api('/reactions/pin', {
        method: 'POST',
        headers: { Authorization: 'Bearer ' + tk },
        body: { path: cfg.path, hash: hash, quote: exact, section: r.section || sectionForExact(exact), on: on }
      });
    }).then(function (data) {
      takeCounts(r, data);
      refreshReactionViews(hash);
      showToast(on ? '已划重点' : '已取消划重点');
      return true;
    }).catch(function (err) {
      r.pinned = before;
      refreshReactionViews(hash);
      if (err.status === 404) showToast('worker 还没部署新版本');
      else if (err.status === 401) showToast('登录已过期，请重新登录 GitHub');
      else showToast('操作失败：' + err.message);
      return false;
    });
  }

  // ----------------------------------------------------------- page views

  // Count once per browser per post per day; local previews only read the number.
  function loadViews() {
    var key = 'viewed:' + cfg.path, now = Date.now(), last = 0;
    try { last = +localStorage.getItem(key) || 0; } catch (e) { /* ignore */ }
    var local = /^(localhost|127\.0\.0\.1)$/.test(location.hostname);
    var count = !document.documentElement.classList.contains('private-post') && !local && now - last > 86400000;
    var req = count ? api('/views', { method: 'POST', body: { path: cfg.path } }) : api('/views?path=' + encodeURIComponent(cfg.path));
    req.then(function (data) {
      if (count) { try { localStorage.setItem(key, String(now)); } catch (e) { /* ignore */ } }
      pageViews = typeof data.views === 'number' ? data.views : null;
      renderLikeBar();
    }).catch(function () { pageViews = null; });
  }

  function ensureDiscussion() {
    if (discussion && discussion.id) return Promise.resolve(discussion.id);
    return loadDiscussion(true).then(function () {
      if (discussion && discussion.id) return discussion.id;
      var meta = document.querySelector("meta[property='og:description'], meta[name='description']");
      var backLink = location.href.replace(/#.*$/, '');
      var body = '# ' + cfg.path + '\n\n' + (meta ? meta.content : '') + '\n\n' + backLink;
      return ensureToken().then(function (tk) {
        return api('/discussions', {
          method: 'POST',
          headers: { Authorization: 'Bearer ' + tk },
          body: { input: { repositoryId: cfg.repoId, categoryId: cfg.categoryId, title: cfg.path, body: body } }
        });
      }).then(function (data) {
        if (!data.id) throw new Error('无法创建讨论串');
        discussion = { id: data.id, url: '', totalCommentCount: 0, likes: parseVotes(null) };
        return data.id;
      });
    });
  }

  // ---------------------------------------------------------------- utils

  function sanitizeHtml(html) {
    var doc = new DOMParser().parseFromString('<div>' + (html || '') + '</div>', 'text/html');
    var root = doc.body.firstChild;
    var bad = root.querySelectorAll('script, style, iframe, object, embed, link, meta, form, input, textarea, button');
    for (var i = 0; i < bad.length; i++) bad[i].parentNode.removeChild(bad[i]);
    var all = root.querySelectorAll('*');
    for (var j = 0; j < all.length; j++) {
      var el = all[j], attrs = el.attributes;
      for (var k = attrs.length - 1; k >= 0; k--) {
        var name = attrs[k].name, value = attrs[k].value;
        if (/^on/i.test(name) || ((name === 'href' || name === 'src') && /^\s*javascript:/i.test(value))) el.removeAttribute(name);
      }
      if (el.tagName === 'IMG') {
        el.setAttribute('loading', 'lazy');
        var canonical = el.getAttribute('data-canonical-src');
        if (cfg.api && canonical && canonical.indexOf(cfg.api + '/media/') === 0) {
          el.setAttribute('src', canonical);
          var imageLink = el.closest('a');
          if (imageLink && /^https:\/\/camo\.githubusercontent\.com\//i.test(imageLink.getAttribute('href') || '')) {
            imageLink.setAttribute('href', canonical);
          }
        }
      }
      if (el.tagName === 'A') {
        var samePage = null;
        try { var u = new URL(el.getAttribute('href') || '', location.href); if (u.pathname === cfg.path && u.hash) samePage = u.hash; } catch (e) { /* ignore */ }
        if (samePage) el.setAttribute('href', samePage); // `§ 原文位置` → handled by hashchange
        else { el.setAttribute('target', '_blank'); el.setAttribute('rel', 'noopener noreferrer nofollow'); }
      }
    }
    var frag = document.createDocumentFragment();
    while (root.firstChild) frag.appendChild(document.adoptNode(root.firstChild));
    return frag;
  }

  var archivedSectionGroups = {};

  function archivedSectionButton(section, list) {
    var button = document.createElement('button');
    button.type = 'button';
    button.className = 'annotation-marker is-archived annotation-archived-section';
    button.setAttribute('data-section', section);
    button.title = '查看这组原文已改的评论';
    button.innerHTML = '<i class="fa fa-check"></i> ' + list.length + ' 条已解决（原文已改）';
    button.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      openArchivedSection(section, button);
    });
    return button;
  }

  function openArchivedSection(section, button) {
    if (panelState && panelState.kind === 'archived-section' && panelState.section === section) { closePanel(); return; }
    if (!archivedSectionGroups[section] || !archivedSectionGroups[section].length) return;
    panelState = { kind: 'archived-section', section: section };
    ensurePanel();
    panel.className = 'annotation-panel is-thread is-archived-section';
    renderArchivedSectionPanel();
    mountPanel(button);
  }

  function refreshArchivedSectionPanel() {
    if (!panelState || panelState.kind !== 'archived-section') return;
    if (!archivedSectionGroups[panelState.section] || !archivedSectionGroups[panelState.section].length) { closePanel(); return; }
    renderArchivedSectionPanel();
    if (!panel.parentNode) {
      var buttons = container.querySelectorAll('.annotation-archived-section'), button = null;
      for (var i = 0; i < buttons.length; i++) if (buttons[i].getAttribute('data-section') === panelState.section) { button = buttons[i]; break; }
      if (button) mountPanel(button);
    }
  }

  function renderArchivedSectionPanel() {
    var section = panelState && panelState.section, list = archivedSectionGroups[section] || [];
    if (!list.length) { closePanel(); return; }
    panel.innerHTML =
      '<div class="ap-head"><span class="ap-quote">§ ' + escapeHtml(section || '原文已改') + ' · ' + list.length + ' 条已解决</span>' +
        '<button type="button" class="ap-close" title="收起">×</button></div>' +
      '<div class="ap-thread"></div>';
    panel.querySelector('.ap-close').addEventListener('click', closePanel);
    var thread = panel.querySelector('.ap-thread');
    list.forEach(function (c) {
      var item = document.createElement('div');
      item.className = 'ap-archived-item';
      item.innerHTML = '<blockquote class="ap-archived-quote">' + escapeHtml(c.selector.exact) + '</blockquote>';
      item.appendChild(commentEl(c, c.noteHTML || c.bodyHTML, false, null, null));
      (c.replies || []).forEach(function (r) { item.appendChild(commentEl(r, r.bodyHTML, true, null, c)); });
      var link = document.createElement('a');
      link.className = 'ac-github';
      link.href = c.url; link.target = '_blank'; link.rel = 'noopener noreferrer';
      link.innerHTML = '<i class="fa fa-brands fa-github"></i> 在 GitHub 上查看';
      item.appendChild(link);
      thread.appendChild(item);
    });
  }

  function renderOrphans(orphans) {
    var old = cfg.section.querySelector('.annotation-orphans');
    if (old) old.parentNode.removeChild(old);
    var oldFallback = cfg.section.querySelector('.annotation-archived-fallback');
    if (oldFallback) oldFallback.parentNode.removeChild(oldFallback);
    archivedSectionGroups = {};
    var regular = orphans.filter(function (o) { return !o.archived; });
    var archived = orphans.filter(function (o) { return o.archived; });
    var box = null;
    if (regular.length) {
      box = document.createElement('p');
      box.className = 'annotation-orphans';
    // An anchor that no longer matches usually means the passage was rewritten —
    // often *because of* the note. Show what it was about, not just who wrote it.
      box.innerHTML = '<i class="fa fa-unlink"></i> ' + regular.length + ' 条划线评论对应的原文已修改（评论仍在 GitHub 上）：';
      for (var i = 0; i < regular.length; i++) {
        var o = regular[i], q = o.selector.exact;
        var a = document.createElement('a');
        a.href = o.url; a.target = '_blank'; a.rel = 'noopener noreferrer';
        a.textContent = '「' + (q.length > 24 ? q.slice(0, 24) + '…' : q) + '」';
        a.title = q + '\n— @' + o.author.login + (o.selector.section ? '，原位于「' + o.selector.section + '」' : '');
        box.appendChild(a);
        box.appendChild(document.createTextNode(' @' + o.author.login));
        if (i < regular.length - 1) box.appendChild(document.createTextNode('、'));
      }
    }
    var heads = container.querySelectorAll('h2, h3'), fallback = [];
    archived.forEach(function (o) {
      var section = o.selector.section || '', group = archivedSectionGroups[section] || (archivedSectionGroups[section] = []);
      group.push(o);
    });
    Object.keys(archivedSectionGroups).forEach(function (section) {
      var group = archivedSectionGroups[section], heading = null;
      if (section) for (var h = 0; h < heads.length; h++) {
        if (headingText(heads[h]).slice(0, SECTION_MAX) === section) { heading = heads[h]; break; }
      }
      if (heading) heading.appendChild(archivedSectionButton(section, group));
      else fallback = fallback.concat(group);
    });
    if (box || fallback.length) {
      var hint = cfg.section.querySelector('.comment-hint');
      if (box) (hint || cfg.section).insertAdjacentElement(hint ? 'afterend' : 'afterbegin', box);
      if (fallback.length) {
        var fallbackBox = document.createElement('p');
        fallbackBox.className = 'annotation-archived-fallback';
        fallbackBox.innerHTML = '<i class="fa fa-unlink"></i> ' + fallback.length + ' 条已解决的评论原文已改：';
        for (var f = 0; f < fallback.length; f++) {
          var archivedComment = fallback[f], quote = archivedComment.selector.exact;
          var link = document.createElement('a');
          link.href = archivedComment.url; link.target = '_blank'; link.rel = 'noopener noreferrer';
          link.textContent = '「' + (quote.length > 24 ? quote.slice(0, 24) + '…' : quote) + '」';
          link.title = quote;
          fallbackBox.appendChild(link);
          if (f < fallback.length - 1) fallbackBox.appendChild(document.createTextNode('、'));
        }
        if (box) box.insertAdjacentElement('afterend', fallbackBox);
        else (hint || cfg.section).insertAdjacentElement(hint ? 'afterend' : 'afterbegin', fallbackBox);
      }
    }
  }

  function copyText(text) {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      return navigator.clipboard.writeText(text).catch(function () { return legacyCopy(text); });
    }
    return Promise.resolve(legacyCopy(text));
  }

  function legacyCopy(text) {
    var ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed'; ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    try { document.execCommand('copy'); } catch (e) { /* ignore */ }
    document.body.removeChild(ta);
  }

  function showToast(msg) {
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'annotation-toast';
      document.body.appendChild(toast);
    }
    toast.textContent = msg;
    toast.classList.add('is-visible');
    clearTimeout(toast._timer);
    toast._timer = setTimeout(function () { toast.classList.remove('is-visible'); }, 3500);
  }

  function relativeTime(iso) {
    var diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return '刚刚';
    if (diff < 3600) return Math.floor(diff / 60) + ' 分钟前';
    if (diff < 86400) return Math.floor(diff / 3600) + ' 小时前';
    if (diff < 86400 * 30) return Math.floor(diff / 86400) + ' 天前';
    var d = new Date(iso);
    return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate());
  }

  function pad(n) { return (n < 10 ? '0' : '') + n; }

  function escapeHtml(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function escapeAttr(s) { return escapeHtml(s); }

  // Exposed for debugging / tests in the browser console.
  window.BlogAnnotations = {
    reload: function () { return loadDiscussion(true); },
    anchor: anchor,
    buildIndex: buildIndex,
    annotHash: annotHash,
    threadLink: threadLink,
    shareLink: shareLink,
    encodeFragmentPart: encodeFragmentPart,
    buildCommentBody: buildCommentBody,
    parseComment: parseComment,
    parseVotes: parseVotes,
    openThread: openThread,
    closePanel: closePanel,
    showArchived: showArchived,
    logout: logout,
    list: function () { return annotations; },
    comments: function () { return comments; },
    viewer: function () { return viewer; },
    // Auth / API plumbing other scripts may reuse.
    core: { api: api, graphql: graphql, getSession: getSession, login: login, ensureToken: ensureToken }
  };
  window.BlogAuth = { login: login };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
